import React, { useEffect, useMemo, useState } from 'react';
import { Table, Button, Modal, Form, Input, Switch, Space, message, Select, InputNumber, Typography, Card, Tag, Upload, Divider, Dropdown, Tooltip } from 'antd';
import { InfoCircleOutlined } from '@ant-design/icons';
import type { UploadFile } from 'antd/es/upload/interface';
import request from '../../../utils/request';
import { getCustomerList, getProjectList } from '../../../services/project';
import { HelpHintButton } from '../../../components/HelpHintButton';

const InstructionTypesPage: React.FC = () => {
  // 开发期强编辑开关：允许无视锁定直接编辑模板
  const STRONG_EDIT = true;
  const [data, setData] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [visible, setVisible] = useState(false);
  const [form] = Form.useForm();
  const [typeActions, setTypeActions] = useState<Array<{
    operation_type_id?: number;
    variant_key?: string;
    sort_order?: number;
    stage?: number;
    group_key?: string;
    source_priority?: string[];
    metric_sources?: Record<string, string[]>;
    quantity_semantic?: string;
    on_missing?: 'warn' | 'skip' | 'block';
    applicability_waybill_mode_in?: string[];
    applicability_waybill_mode_not_in?: string[];
    applicability_waybill_source_in?: string[];
    applicability_is_pack_required_in?: boolean[];
    applicability_estimated_packing_method_in?: string[];
    applicability_target_source_in?: string[];
    applicability_target_source_not_in?: string[];
  }>>([]);
  const [billingCfgOpen, setBillingCfgOpen] = useState(false);
  const [billingCfgIdx, setBillingCfgIdx] = useState<number>(-1);
  const [attachmentRows, setAttachmentRows] = useState<Array<{ scope: 'order' | 'actions'; action_key?: string; label?: string; types?: string[]; min?: number; max?: number; tips?: string; template_id?: number; template_ids?: number[]; printable?: boolean; sort_order?: number; show_if?: { key?: string; op?: 'eq'|'neq'|'in'|'contains'|'gt'|'gte'|'lt'|'lte'|'is_true'|'is_false'|'is_empty'|'not_empty'; value_text?: string } }>>([]);
  // 附件角色与绑定（方式A：通用角色目录 + 模板规则行绑定）
  const [roleOptions, setRoleOptions] = useState<Array<{ code: string; name: string }>>([]);
  const [rolesLoading, setRolesLoading] = useState(false);
  const [bindingsByRule, setBindingsByRule] = useState<Record<string, any>>({});
  const [bindingBusy, setBindingBusy] = useState(false);
  const [newRoleOpen, setNewRoleOpen] = useState(false);
  const [newRoleForRule, setNewRoleForRule] = useState<string | null>(null);
  const [newRoleForm, setNewRoleForm] = useState<{ code: string; name: string; is_active: boolean; default_schema_text: string; default_validators_text: string }>({
    code: '',
    name: '',
    is_active: true,
    default_schema_text: JSON.stringify({ type: 'barcode_mapping', columns: { old: ['旧条码','old_barcode'], new: ['新条码','new_barcode'] } }, null, 2),
    default_validators_text: JSON.stringify([{ type: 'unique_in_column', column: 'old', normalize: 'barcode' }], null, 2)
  });
  const loadAttachmentRoles = async () => {
    if (rolesLoading) return;
    setRolesLoading(true);
    try {
      const resp = await request.get('/api/v1/instruction/attachment-roles/', { params: { is_active: true, page_size: 1000 } });
      const rows = Array.isArray(resp?.data?.results) ? resp.data.results : (Array.isArray(resp?.data) ? resp.data : []);
      setRoleOptions((rows || []).map((r: any) => ({ code: String(r.code || ''), name: String(r.name || '') })));
    } catch {
      setRoleOptions([]);
    } finally {
      setRolesLoading(false);
    }
  };
  const loadAttachmentBindings = async (tplId?: number) => {
    const tid = Number(tplId);
    if (!Number.isFinite(tid) || tid <= 0) { setBindingsByRule({}); return; }
    try {
      const resp = await request.get('/api/v1/instruction/template-attachment-bindings/', { params: { template: tid, page_size: 1000 } });
      const rows = Array.isArray(resp?.data?.results) ? resp.data.results : (Array.isArray(resp?.data) ? resp.data : []);
      const map: Record<string, any> = {};
      (rows || []).forEach((b:any) => { if (b && b.rule_key) { map[String(b.rule_key)] = b; } });
      setBindingsByRule(map);
    } catch {
      setBindingsByRule({});
    }
  };
  const bindOrPatch = async (ruleKey: string, payload: Partial<{ role_code: string; block_on_fail: boolean; enabled: boolean; schema_override: any; validators_override: any }>) => {
    const tid = Number((editingTpl as any)?.id);
    if (!Number.isFinite(tid)) { message.warning('请先保存模板后再绑定角色'); return; }
    if (!ruleKey || !String(ruleKey).trim()) { message.warning('无法识别规则行，保存模板后再试'); return; }
    const existing = bindingsByRule[ruleKey];
    try {
      setBindingBusy(true);
      if (existing && existing.id) {
        // PATCH
        const resp = await request.patch(`/api/v1/instruction/template-attachment-bindings/${existing.id}/`, payload);
        const b = resp?.data || {};
        setBindingsByRule(prev => ({ ...prev, [ruleKey]: b }));
        message.success('绑定已更新');
      } else {
        // POST（需要 role_code）
        const roleCode = String(payload.role_code || '');
        if (!roleCode) { message.warning('请先选择角色'); return; }
        const body: any = { template: tid, rule_key: ruleKey, role_code: roleCode, enabled: true, block_on_fail: false };
        const resp = await request.post('/api/v1/instruction/template-attachment-bindings/', body);
        const b = resp?.data || {};
        setBindingsByRule(prev => ({ ...prev, [ruleKey]: b }));
        message.success('已绑定角色');
      }
    } catch (e:any) {
      message.error(e?.response?.data?.detail || '保存绑定失败');
    } finally {
      setBindingBusy(false);
    }
  };
  const [opTypes, setOpTypes] = useState<any[]>([]);
  const [variantOptionsByOp, setVariantOptionsByOp] = useState<Record<number, string[]>>({});
  const [extraScopedOpIds, setExtraScopedOpIds] = useState<number[]>([]);
  const [customerOptions, setCustomerOptions] = useState<Array<{ value: number; label: string }>>([]);
  const [projectOptions, setProjectOptions] = useState<Array<{ value: number; label: string }>>([]);
  const SOURCE_PRIORITY_OPTIONS = [
    { value: 'operation', label: '操作产物（阶段/IOC/操作记录）' },
    { value: 'instruction', label: '指令事实（目标IU/库存单元数据）' },
  ];
  const WAYBILL_MODE_OPTIONS = [
    { value: 'immediate', label: 'immediate（立即上传）' },
    { value: 'after_pack', label: 'after_pack（先申请包装数据）' },
  ];
  const WAYBILL_SOURCE_OPTIONS = [
    { value: 'upload', label: 'upload（上传运单）' },
    { value: 'warehouse', label: 'warehouse（仓库提供）' },
  ];
  const INVENTORY_FINALIZE_MODE_OPTIONS = [
    { value: 'consume_reserved', label: 'consume_reserved（转消耗）' },
    { value: 'release_reserved', label: 'release_reserved（释放占用）' },
    { value: 'transfer_to_business_hold', label: 'transfer_to_business_hold（转业务留存占用）' },
  ];
  const IOC_CONSUME_RESERVATION_KIND_OPTIONS = [
    { value: 'execution', label: 'execution（执行期占用）' },
    { value: 'business_hold', label: 'business_hold（业务留存占用）' },
  ];
  const inventoryFinalizeModeLabel = (v: string) => (
    INVENTORY_FINALIZE_MODE_OPTIONS.find((o: any) => String(o.value) === String(v || ''))?.label || String(v || '')
  );
  const PACK_REQUIRED_OPTIONS = [
    { value: true, label: 'true（需打包）' },
    { value: false, label: 'false（无需打包）' },
  ];
  const ESTIMATED_PACKING_METHOD_OPTIONS = [
    { value: 'warehouse_free', label: 'warehouse_free（仓库免费包材）' },
    { value: 'warehouse_paid', label: 'warehouse_paid（仓库收费包材）' },
    { value: 'customer_carton', label: 'customer_carton（客户自购包材-纸箱）' },
    { value: 'customer_non_carton', label: 'customer_non_carton（客户自购包材-非纸箱）' },
    { value: 'special_packing', label: 'special_packing（特殊包装）' },
  ];
  const TARGET_SOURCE_OPTIONS = [
    { value: 'inventory', label: 'inventory（当前可用库存）' },
    { value: 'ioc', label: 'ioc（历史指令产出IOC）' },
    { value: 'mixed', label: 'mixed（混合来源）' },
  ];
  const METRIC_KEYS_FOR_PROFILE = ['quantity', 'volume_m3', 'volume_cc', 'weight_kg', 'weight_g'];

  const sanitizePriorityList = (v: any): string[] => {
    const arr = Array.isArray(v) ? v : [];
    const out: string[] = [];
    arr.forEach((x: any) => {
      const s = String(x || '').trim().toLowerCase();
      if ((s === 'operation' || s === 'instruction') && !out.includes(s)) out.push(s);
    });
    return out;
  };
  const normalizeMetricSources = (v: any): Record<string, string[]> => {
    const out: Record<string, string[]> = {};
    if (!v || typeof v !== 'object') return out;
    METRIC_KEYS_FOR_PROFILE.forEach((mk) => {
      const list = sanitizePriorityList((v as any)?.[mk]);
      if (list.length) out[mk] = list;
    });
    return out;
  };
  const sanitizeTargetSourceList = (v: any): string[] => {
    const arr = Array.isArray(v) ? v : [];
    const out: string[] = [];
    arr.forEach((x: any) => {
      const s = String(x || '').trim().toLowerCase();
      let n = '';
      if (['inventory', 'inventory_unit', 'current_inventory', 'available_inventory'].includes(s)) n = 'inventory';
      else if (['ioc', 'instruction_output', 'history_ioc', 'output_ioc'].includes(s)) n = 'ioc';
      else if (['mixed', 'both', 'hybrid'].includes(s)) n = 'mixed';
      if (n && !out.includes(n)) out.push(n);
    });
    return out;
  };
  const mapActionFromTemplate = (a: any) => {
    const billing = (a?.meta && typeof a.meta === 'object' && a.meta.billing && typeof a.meta.billing === 'object') ? a.meta.billing : {};
    const ap = (billing?.applicability && typeof billing.applicability === 'object') ? billing.applicability : {};
    return {
      operation_type_id: a.operation_type || a.operation_type_id,
      variant_key: a.variant_key,
      sort_order: (a.sort_order !== undefined && a.sort_order !== null) ? Number(a.sort_order) : undefined,
      stage: (typeof a.stage === 'number' ? a.stage : (String(a.stage || '').trim() ? Number(a.stage) : undefined)),
      group_key: (typeof a.group_key === 'string' && a.group_key.trim() ? a.group_key : undefined),
      source_priority: sanitizePriorityList(billing?.source_priority),
      metric_sources: normalizeMetricSources(billing?.metric_sources),
      quantity_semantic: String(billing?.quantity_semantic || '').trim() || undefined,
      on_missing: (['warn', 'skip', 'block'].includes(String(billing?.on_missing || '').trim().toLowerCase()) ? String(billing?.on_missing || '').trim().toLowerCase() : undefined) as any,
      applicability_waybill_mode_in: Array.isArray(ap?.waybill_mode_in) ? ap.waybill_mode_in.map((x: any) => String(x || '').trim()).filter(Boolean) : undefined,
      applicability_waybill_mode_not_in: Array.isArray(ap?.waybill_mode_not_in) ? ap.waybill_mode_not_in.map((x: any) => String(x || '').trim()).filter(Boolean) : undefined,
      applicability_waybill_source_in: Array.isArray(ap?.waybill_source_in) ? ap.waybill_source_in.map((x: any) => String(x || '').trim()).filter(Boolean) : undefined,
      applicability_is_pack_required_in: Array.isArray(ap?.is_pack_required_in)
        ? ap.is_pack_required_in
            .map((x: any) => {
              if (typeof x === 'boolean') return x;
              const s = String(x || '').trim().toLowerCase();
              if (s === 'true' || s === '1' || s === 'yes') return true;
              if (s === 'false' || s === '0' || s === 'no') return false;
              return undefined;
            })
            .filter((x: any) => typeof x === 'boolean')
        : undefined,
      applicability_estimated_packing_method_in: Array.isArray(ap?.estimated_packing_method_in)
        ? ap.estimated_packing_method_in.map((x: any) => String(x || '').trim()).filter(Boolean)
        : undefined,
      applicability_target_source_in: Array.isArray(ap?.target_source_in)
        ? sanitizeTargetSourceList(ap?.target_source_in)
        : undefined,
      applicability_target_source_not_in: Array.isArray(ap?.target_source_not_in)
        ? sanitizeTargetSourceList(ap?.target_source_not_in)
        : undefined,
    } as any;
  };

  const buildBillingMetaForAction = (ta: any): any => {
    const sourcePriority = sanitizePriorityList(ta?.source_priority);
    const metricSources = normalizeMetricSources(ta?.metric_sources);
    const quantitySemantic = String(ta?.quantity_semantic || '').trim();
    const onMissing = String(ta?.on_missing || '').trim().toLowerCase();
    const waybillModes = (Array.isArray(ta?.applicability_waybill_mode_in) ? ta.applicability_waybill_mode_in : [])
      .map((x: any) => String(x || '').trim())
      .filter(Boolean);
    const waybillModeNotIn = (Array.isArray(ta?.applicability_waybill_mode_not_in) ? ta.applicability_waybill_mode_not_in : [])
      .map((x: any) => String(x || '').trim())
      .filter(Boolean);
    const waybillSourceIn = (Array.isArray(ta?.applicability_waybill_source_in) ? ta.applicability_waybill_source_in : [])
      .map((x: any) => String(x || '').trim())
      .filter(Boolean);
    const packRequiredIn = (Array.isArray(ta?.applicability_is_pack_required_in) ? ta.applicability_is_pack_required_in : [])
      .filter((x: any) => typeof x === 'boolean');
    const estimatedPackingMethodIn = (Array.isArray(ta?.applicability_estimated_packing_method_in) ? ta.applicability_estimated_packing_method_in : [])
      .map((x: any) => String(x || '').trim())
      .filter(Boolean);
    const targetSourceIn = sanitizeTargetSourceList(ta?.applicability_target_source_in);
    const targetSourceNotIn = sanitizeTargetSourceList(ta?.applicability_target_source_not_in);
    const billing: any = {};
    if (sourcePriority.length) billing.source_priority = sourcePriority;
    if (Object.keys(metricSources).length) billing.metric_sources = metricSources;
    if (quantitySemantic) billing.quantity_semantic = quantitySemantic;
    if (onMissing === 'warn' || onMissing === 'skip' || onMissing === 'block') billing.on_missing = onMissing;
    const applicability: any = {};
    if (waybillModes.length) applicability.waybill_mode_in = waybillModes;
    if (waybillModeNotIn.length) applicability.waybill_mode_not_in = waybillModeNotIn;
    if (waybillSourceIn.length) applicability.waybill_source_in = waybillSourceIn;
    if (packRequiredIn.length) applicability.is_pack_required_in = packRequiredIn;
    if (estimatedPackingMethodIn.length) applicability.estimated_packing_method_in = estimatedPackingMethodIn;
    if (targetSourceIn.length) applicability.target_source_in = targetSourceIn;
    if (targetSourceNotIn.length) applicability.target_source_not_in = targetSourceNotIn;
    if (Object.keys(applicability).length) billing.applicability = applicability;
    return Object.keys(billing).length ? { billing } : undefined;
  };

  const [customerLoading, setCustomerLoading] = useState(false);
  const [projectLoading, setProjectLoading] = useState(false);
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailRecord, setDetailRecord] = useState<any | null>(null);
  const [editingTpl, setEditingTpl] = useState<any | null>(null);
  // 显示用映射：客户/项目 可读名称
  const [customerLabelById, setCustomerLabelById] = useState<Record<number, string>>({});
  const [projectLabelById, setProjectLabelById] = useState<Record<number, string>>({});
  // 动作详情弹窗
  const [actionListOpen, setActionListOpen] = useState(false);
  const [actionList, setActionList] = useState<Array<{ name: string; variant?: string; basis?: string; order?: number; stage?: number; group?: string; opId?: number }>>([]);
  const [actionListTitle, setActionListTitle] = useState<string>('动作详情');
  const [actionListMode, setActionListMode] = useState<'actions' | 'questions' | 'attachments'>('actions');
  // 规则口径只读映射（operation_type_id -> basis label）
  const [ruleBasisByOpId, setRuleBasisByOpId] = useState<Record<number, string>>({});
  // 拉取“有效规则”并组装 operation_type -> 规则口径 映射（只读展示）
  const fetchRuleBasisMap = async (tpl: any): Promise<Record<number, string>> => {
    try {
      const params: any = {};
      if (tpl?.level === 'project' && tpl?.customer_id && tpl?.project_id) {
        params.project_id = tpl.project_id; params.customer_id = tpl.customer_id;
      } else if (tpl?.level === 'customer' && tpl?.customer_id) {
        params.customer_id = tpl.customer_id;
      } // system 级无法精准推断
      const resp = await request.get('/api/v1/billing/billing-rules/effective/', { params });
      const rows = Array.isArray(resp?.data) ? resp.data : [];
      const map: Record<number, string> = {};
      const getBasis = (r:any) => {
        return r?.billing_unit || r?.unit || r?.tier_basis || r?.basis || r?.measure || '';
      };
      rows.forEach((r:any) => {
        const opId = Number(r?.operation_type);
        if (!Number.isNaN(opId)) {
          const basis = String(getBasis(r) || '').trim();
          if (basis && !map[opId]) map[opId] = basis;
        }
      });
      return map;
    } catch {
      return {};
    }
  };

  // 附件模板选项
  const [attachTplOptions, setAttachTplOptions] = useState<Array<{ value: number; label: string; scope: 'order'|'actions'; action_key?: string; version?: string; file_name?: string }>>([]);
  const [attachTplLoading, setAttachTplLoading] = useState(false);
  const [uploadTplOpen, setUploadTplOpen] = useState(false);
  const [uploadTplRowIdx, setUploadTplRowIdx] = useState<number | null>(null);
  const [uploadTplFiles, setUploadTplFiles] = useState<UploadFile<any>[]>([]);
  const [uploadTplTips, setUploadTplTips] = useState<string>('');
  // 问题配置（问卷）
  type ConditionOperator = 'eq'|'neq'|'in'|'contains'|'gt'|'gte'|'lt'|'lte'|'is_true'|'is_false'|'is_empty'|'not_empty';
  type ConditionItem = {
    key?: string;
    op?: ConditionOperator;
    value_text?: string;
  };
  const [qRows, setQRows] = useState<Array<{
    key?: string;
    label?: string;
    type?: 'boolean'|'select'|'multiselect'|'number'|'text'|'textarea';
    required?: boolean;
    options?: Array<{ value: string; label: string }>;
    options_source?: { type?: 'custom'|'model'; model?: 'logistics_provider'; value_field?: string; label_field?: string };
    min?: number; max?: number; step?: number; unit?: string;
    max_length?: number; placeholder?: string; tips?: string;
    // 新增：默认值（可选），保存为 questionnaire.questions[*].default
    default?: any;
    // 显示条件（可选），保存为 questionnaire.questions[*].show_if
    show_if?: ConditionItem;
    show_if_logic?: 'and' | 'or';
    show_if_conditions?: ConditionItem[];
    // 新结构：必选条件(AND) + 任一条件(OR)
    show_if_must_conditions?: ConditionItem[];
    show_if_any_conditions?: ConditionItem[];
    // 条件必填（可选），保存为 questionnaire.questions[*].required_if
    required_if?: {
      key?: string;
      op?: ConditionOperator;
      value_text?: string;
    };
  }>>([]);
  const showIfValueToText = (val: any) => {
    if (val === undefined || val === null) return '';
    if (typeof val === 'string') return val;
    try { return JSON.stringify(val); } catch { return String(val); }
  };
  const parseShowIfValue = (raw: any) => {
    if (raw === undefined || raw === null) return undefined;
    if (typeof raw !== 'string') return raw;
    const s = raw.trim();
    if (s === '') return undefined;
    if (s === 'true') return true;
    if (s === 'false') return false;
    if (s === 'null') return null;
    if (/^-?\d+(\.\d+)?$/.test(s)) return Number(s);
    if ((s.startsWith('[') && s.endsWith(']')) || (s.startsWith('{') && s.endsWith('}'))) {
      try { return JSON.parse(s); } catch {}
    }
    return raw;
  };
  const normalizeQuestionShowIfFromPayload = (showIfRaw: any) => {
    try {
      const mustRaw = Array.isArray(showIfRaw?.must_conditions) ? showIfRaw.must_conditions : [];
      const anyRaw = Array.isArray(showIfRaw?.any_conditions) ? showIfRaw.any_conditions : [];
      if (mustRaw.length > 0 || anyRaw.length > 0) {
        const norm = (arr: any[]) => arr
          .map((c:any) => ({
            key: String(c?.key || '').trim(),
            op: (c?.op || 'eq') as ConditionOperator,
            value_text: showIfValueToText(c?.value),
          }))
          .filter((c: ConditionItem) => !!c.key);
        const must = norm(mustRaw);
        const any = norm(anyRaw);
        return {
          show_if_logic: 'and' as const,
          show_if_conditions: [],
          show_if: undefined as any,
          show_if_must_conditions: must,
          show_if_any_conditions: any,
        };
      }
      const condsRaw = Array.isArray(showIfRaw?.conditions) ? showIfRaw.conditions : [];
      if (condsRaw.length > 0) {
        const logic = String(showIfRaw?.logic || showIfRaw?.mode || 'and').toLowerCase() === 'or' ? 'or' : 'and';
        const conditions: ConditionItem[] = condsRaw
          .map((c:any) => ({
            key: String(c?.key || '').trim(),
            op: (c?.op || 'eq') as ConditionOperator,
            value_text: showIfValueToText(c?.value),
          }))
          .filter((c: ConditionItem) => !!c.key);
        if (conditions.length > 0) {
          const asMust = logic === 'and' ? conditions : [];
          const asAny = logic === 'or' ? conditions : [];
          return {
            show_if_logic: logic as 'and'|'or',
            show_if_conditions: conditions,
            show_if: conditions[0],
            // 兼容旧结构：在编辑器里映射到“必选/任一”可视区域，避免条件存在但看不见
            show_if_must_conditions: asMust,
            show_if_any_conditions: asAny,
          };
        }
      }
      if (showIfRaw?.key) {
        const single: ConditionItem = {
          key: String(showIfRaw.key || '').trim(),
          op: (showIfRaw.op || 'eq') as ConditionOperator,
          value_text: showIfValueToText(showIfRaw.value),
        };
        if (single.key) {
          return {
            show_if_logic: 'and' as const,
            show_if_conditions: [single],
            show_if: single,
            // 兼容单条件旧结构：默认映射为 AND 条件，确保在编辑器中可见
            show_if_must_conditions: [single],
            show_if_any_conditions: [],
          };
        }
      }
    } catch {}
    return {
      show_if_logic: 'and' as const,
      show_if_conditions: [] as ConditionItem[],
      show_if: undefined as any,
      show_if_must_conditions: [] as ConditionItem[],
      show_if_any_conditions: [] as ConditionItem[],
    };
  };
  const buildQuestionShowIfPayload = (row: any) => {
    try {
      const toCond = (c:any) => {
        const key = String(c?.key || '').trim();
        if (!key) return null;
        const op = (c?.op || 'eq') as ConditionOperator;
        const out: any = { key, op };
        if (!['is_true', 'is_false', 'is_empty', 'not_empty'].includes(String(op || ''))) {
          const parsed = parseShowIfValue(c?.value_text);
          if (parsed !== undefined) out.value = parsed;
        }
        return out;
      };
      const mustRaw = Array.isArray(row?.show_if_must_conditions) ? row.show_if_must_conditions : [];
      const anyRaw = Array.isArray(row?.show_if_any_conditions) ? row.show_if_any_conditions : [];
      const must = mustRaw.map(toCond).filter(Boolean);
      const any = anyRaw.map(toCond).filter(Boolean);
      if (must.length > 0 || any.length > 0) {
        const out: any = {};
        if (must.length > 0) out.must_conditions = must;
        if (any.length > 0) out.any_conditions = any;
        return out;
      }
      const fromList = Array.isArray(row?.show_if_conditions) ? row.show_if_conditions : [];
      const fromSingle = row?.show_if?.key ? [row.show_if] : [];
      const merged = (fromList.length > 0 ? fromList : fromSingle)
        .map((c:any) => ({
          key: String(c?.key || '').trim(),
          op: (c?.op || 'eq') as ConditionOperator,
          value_text: c?.value_text,
        }))
        .filter((c:any) => !!c.key)
        .map((c:any) => {
          const out: any = { key: c.key, op: c.op };
          if (!['is_true', 'is_false', 'is_empty', 'not_empty'].includes(String(c.op || ''))) {
            const parsed = parseShowIfValue(c.value_text);
            if (parsed !== undefined) out.value = parsed;
          }
          return out;
        });
      if (merged.length === 0) return undefined;
      if (merged.length === 1) return merged[0];
      const logic = String(row?.show_if_logic || 'and').toLowerCase() === 'or' ? 'or' : 'and';
      return { logic, conditions: merged };
    } catch {
      return undefined;
    }
  };
  const moveQuestionRow = (idx: number, dir: -1 | 1) => {
    setQRows(prev => {
      const arr = Array.isArray(prev) ? [...prev] : [];
      if (arr.length <= 1) return arr;
      const from = Number(idx);
      const to = from + Number(dir);
      if (!Number.isFinite(from) || !Number.isFinite(to)) return arr;
      if (from < 0 || from >= arr.length) return arr;
      if (to < 0 || to >= arr.length) return arr;
      const tmp = arr[from];
      arr[from] = arr[to];
      arr[to] = tmp;
      return arr;
    });
  };
  const moveAttachmentRow = (idx: number, dir: -1 | 1) => {
    setAttachmentRows(prev => {
      const arr = Array.isArray(prev) ? [...prev] : [];
      if (arr.length <= 1) return arr;
      const from = Number(idx);
      const to = from + Number(dir);
      if (!Number.isFinite(from) || !Number.isFinite(to)) return arr;
      if (from < 0 || from >= arr.length) return arr;
      if (to < 0 || to >= arr.length) return arr;
      const tmp = arr[from];
      arr[from] = arr[to];
      arr[to] = tmp;
      return arr;
    });
  };
  const [showActionPlanHelp, setShowActionPlanHelp] = useState(false);
  const [inventoryConsumeCfg, setInventoryConsumeCfg] = useState<{
    enabled: boolean;
    mode: 'consume_reserved' | 'release_reserved' | 'transfer_to_business_hold';
    ioc_consume_reservation_kinds: string[];
    waybill_source_in: string[];
    waybill_mode_in: string[];
    waybill_mode_not_in: string[];
    is_pack_required_in: boolean[];
  }>({
    enabled: false,
    mode: 'consume_reserved',
    ioc_consume_reservation_kinds: ['execution', 'business_hold'],
    waybill_source_in: [],
    waybill_mode_in: [],
    waybill_mode_not_in: [],
    is_pack_required_in: [],
  });

  const readInventoryConsumeCfgFromMeta = (meta: any) => {
    const inv = (meta && typeof meta === 'object' && meta.inventory && typeof meta.inventory === 'object') ? meta.inventory : {};
    const finalizeRaw = (inv && typeof inv.final_review_finalize === 'object') ? inv.final_review_finalize : {};
    const legacyConsumeRaw = (inv && typeof inv.final_review_consume === 'object') ? inv.final_review_consume : {};
    const hasFinalize = !!(finalizeRaw && Object.keys(finalizeRaw).length > 0);
    const raw = hasFinalize ? finalizeRaw : legacyConsumeRaw;
    const ap = (raw && typeof raw.applicability === 'object') ? raw.applicability : {};
    const toTextList = (v: any) => Array.isArray(v) ? v.map((x: any) => String(x || '').trim()).filter(Boolean) : [];
    const toBoolList = (v: any) => {
      const arr = Array.isArray(v) ? v : [];
      const out: boolean[] = [];
      arr.forEach((x: any) => {
        if (typeof x === 'boolean' && !out.includes(x)) out.push(x);
      });
      return out;
    };
    const rawMode = String(raw?.mode || '').trim();
    const mode = (rawMode === 'release_reserved' || rawMode === 'transfer_to_business_hold' || rawMode === 'consume_reserved')
      ? rawMode
      : 'consume_reserved';
    const iocKindsRaw = (
      inv?.regular_outbound_ioc_consume
      && typeof inv.regular_outbound_ioc_consume === 'object'
      && Array.isArray(inv.regular_outbound_ioc_consume.reservation_kinds)
    )
      ? inv.regular_outbound_ioc_consume.reservation_kinds
      : (Array.isArray(inv?.ioc_consume_reservation_kinds) ? inv.ioc_consume_reservation_kinds : []);
    const iocKinds = Array.from(new Set(
      (Array.isArray(iocKindsRaw) ? iocKindsRaw : [])
        .map((x: any) => String(x || '').trim().toLowerCase())
        .filter((x: string) => x === 'execution' || x === 'business_hold')
    ));
    const enabled = hasFinalize
      ? !!mode
      : !!legacyConsumeRaw?.enabled;
    return {
      enabled,
      mode: mode as 'consume_reserved' | 'release_reserved' | 'transfer_to_business_hold',
      ioc_consume_reservation_kinds: iocKinds.length > 0 ? iocKinds : ['execution', 'business_hold'],
      waybill_source_in: toTextList(ap?.waybill_source_in),
      waybill_mode_in: toTextList(ap?.waybill_mode_in),
      waybill_mode_not_in: toTextList(ap?.waybill_mode_not_in),
      is_pack_required_in: toBoolList(ap?.is_pack_required_in),
    };
  };
  // 默认工单模板选项
  const [wotplOptions, setWotplOptions] = useState<Array<{ value: number; label: string }>>([]);
  const [wotplLoading, setWotplLoading] = useState(false);
  // 自动生成（单模板）：预览/执行
  const [autoGenOpen, setAutoGenOpen] = useState(false);
  const [autoGenLoading, setAutoGenLoading] = useState(false);
  const [autoGenTpl, setAutoGenTpl] = useState<any | null>(null);
  const [autoGenPreview, setAutoGenPreview] = useState<{ created_templates: any[]; updated_templates: any[]; route?: any; warnings?: string[] } | null>(null);
  // 自动生成状态缓存（是否已存在对应工单模板与路由）
  const [genStatusByTplId, setGenStatusByTplId] = useState<Record<number, { ready: boolean; checking?: boolean; locked?: boolean }>>({});
  // 执行动作包配置
  const [execPackOpen, setExecPackOpen] = useState(false);
  const [execPackLoading, setExecPackLoading] = useState(false);
  const [execPackTpl, setExecPackTpl] = useState<any | null>(null); // 指令模板记录
  const [execTaskTpl, setExecTaskTpl] = useState<any | null>(null); // ExecutionTaskTemplate
  const [execSteps, setExecSteps] = useState<Array<any>>([]); // ExecutionStepTemplate 列表
  const [execTplName, setExecTplName] = useState<string>('默认执行清单');
  const [execDeletedStepIds, setExecDeletedStepIds] = useState<Set<number>>(new Set());
  // 基准数据规范（模板级声明）
  const [baselineItems, setBaselineItems] = useState<Array<{
    code?: string;
    name?: string;
    required?: boolean;
    formats?: string[];
    min_files?: number;
    max_files?: number;
    bind?: { stage?: number; group_key?: string; steps?: string[] };
    schema_hint_json?: string;
  }>>([]);
  const [baselineSchemaOpen, setBaselineSchemaOpen] = useState(false);
  const [baselineSchemaIdx, setBaselineSchemaIdx] = useState<number | null>(null);
  const [baselineSchemaText, setBaselineSchemaText] = useState<string>('');
  // 步骤库导入/选择
  const [stepLibOpen, setStepLibOpen] = useState(false);
  const [stepLibLoading, setStepLibLoading] = useState(false);
  const [stepLibRows, setStepLibRows] = useState<Array<any>>([]);
  const [stepLibSelectedKeys, setStepLibSelectedKeys] = useState<React.Key[]>([]);
  // 行内 JSON 弹窗编辑器
  const [jsonEditorOpen, setJsonEditorOpen] = useState(false);
  const [jsonEditorField, setJsonEditorField] = useState<'required_metrics_json'|'ui_schema_json'|'action_plan_json'|null>(null);
  const [jsonEditorIdx, setJsonEditorIdx] = useState<number | null>(null);
  const [jsonEditorText, setJsonEditorText] = useState<string>('');
  const openJsonEditor = (field: 'required_metrics_json'|'ui_schema_json'|'action_plan_json', idx: number) => {
    try {
      setJsonEditorField(field);
      setJsonEditorIdx(idx);
      const row = execSteps[idx] || {};
      setJsonEditorText(String(row[field] || (field==='action_plan_json' ? '[]' : '{}')));
      setJsonEditorOpen(true);
    } catch {
      setJsonEditorOpen(true);
    }
  };
  const saveJsonEditor = () => {
    if (jsonEditorField == null || jsonEditorIdx == null) { setJsonEditorOpen(false); return; }
    setExecSteps(prev => prev.map((x,i)=> i===jsonEditorIdx ? ({ ...x, [jsonEditorField]: jsonEditorText }) : x));
    setJsonEditorOpen(false);
  };
  // 执行动作包查看
  const [viewExecOpen, setViewExecOpen] = useState(false);
  const [viewExecLoading, setViewExecLoading] = useState(false);
  const [viewExecTpl, setViewExecTpl] = useState<any | null>(null);
  const [viewExecSteps, setViewExecSteps] = useState<Array<any>>([]);
  // 执行动作包计数（列中展示）
  const [execPackCountByTplId, setExecPackCountByTplId] = useState<Record<number, number | undefined>>({});
  // 执行步骤填写提示
  const [showExecStepHelp, setShowExecStepHelp] = useState(false);
  // 列表筛选：状态/层级
  const [filterStatus, setFilterStatus] = useState<'all' | 'active' | 'inactive'>('active');
  const [filterLevel, setFilterLevel] = useState<'all' | 'system' | 'customer' | 'project'>('all');
  const [filterCustomerId, setFilterCustomerId] = useState<number | undefined>(undefined);
  const ensureGenStatus = async (tpl: any) => {
    const id = Number(tpl?.id);
    if (!Number.isFinite(id)) return;
    setGenStatusByTplId(prev => prev[id] ? prev : { ...prev, [id]: { ready: false, checking: true } });
    try {
      const resp = await request.post(`/api/v1/instruction/templates/${id}/auto-generate/`, { dry_run: true }, { params: { dry_run: 1 } });
      const payload = resp?.data || {};
      const route = payload.route || {};
      const ready = !!(route?.updated || route?.created);
      setGenStatusByTplId(prev => ({ ...prev, [id]: { ready, checking: false } }));
    } catch {
      setGenStatusByTplId(prev => ({ ...prev, [id]: { ready: false, checking: false } }));
    }
  };

  // 查看已生成的专属工单模板与路由
  const [ownedOpen, setOwnedOpen] = useState(false);
  const [ownedTpl, setOwnedTpl] = useState<any | null>(null);
  const [ownedLoading, setOwnedLoading] = useState(false);
  const [ownedData, setOwnedData] = useState<{ templates: any[]; route?: any } | null>(null);
  const openViewOwned = async (tpl: any) => {
    setOwnedTpl(tpl);
    setOwnedData(null);
    setOwnedOpen(true);
    setOwnedLoading(true);
    try {
      // 后端显式关联接口
      const resp = await request.get(`/api/v1/instruction/templates/${tpl.id}/owned-workorders/`);
      const payload = resp?.data || {};
      const templates = Array.isArray(payload.templates) ? payload.templates : [];
      const route = payload.route || undefined;
      setOwnedData({ templates, route });
    } finally { setOwnedLoading(false); }
  };
  const loadWorkOrderTemplates = async () => {
    setWotplLoading(true);
    const endpoints = ['/api/v1/workorders/workorder-templates/', '/api/v1/workorders/templates/'];
    try {
      let list:any[] = [];
      for (const ep of endpoints) {
        try {
          const resp = await request.get(ep, { params: { is_active: true, all: 1 } });
          const data = resp?.data;
          list = Array.isArray(data?.results) ? data.results : (Array.isArray(data) ? data : []);
          if (list && list.length) break;
        } catch {}
      }
      if (!list || list.length === 0) {
        try {
          const resp = await request.get(endpoints[0], { params: { page: 1, page_size: 200 } });
          list = Array.isArray(resp?.data?.results) ? resp.data.results : [];
        } catch {}
      }
      setWotplOptions((list || []).map((t:any) => ({
        value: Number(t.id),
        label: `${t.name || '-'} · ${t.code || t.id}`,
        code: String(t.code || ''),
        name: t.name || '',
        searchLabel: `${t.name || ''} ${t.code || ''}`.trim()
      })));
    } finally { setWotplLoading(false); }
  };

  // 打开“配置执行动作包”弹窗
  const openExecPack = async (tpl: any) => {
    setExecPackTpl(tpl);
    setExecPackOpen(true);
    setExecPackLoading(true);
    setExecTaskTpl(null);
    setExecSteps([]);
    // 动作包名称默认：指令模板名称 + [动作序列]
    try {
      const baseName = String((tpl?.name || tpl?.system_code || '') || '').trim();
      const defName = baseName ? `${baseName}[步骤序列]` : '操作步骤序列';
      setExecTplName(defName);
    } catch {
      setExecTplName('操作步骤序列');
    }
    setExecDeletedStepIds(new Set());
    try {
      // 1) 查询是否已存在动作包
      const resp = await request.get('/api/v1/instruction/exec/task-templates/', { params: { instruction_template: Number(tpl.id) } });
      const list = Array.isArray(resp?.data?.results) ? resp.data.results : (Array.isArray(resp?.data) ? resp.data : []);
      let taskTpl = list && list.length > 0 ? list[0] : null;
      if (!taskTpl) {
        // 若未存在，先创建一个（后端已加唯一约束）
        const baseName = String((tpl?.name || tpl?.system_code || '') || '').trim();
        const defName = baseName ? `${baseName}[步骤序列]` : '操作步骤序列';
        const created = await request.post('/api/v1/instruction/exec/task-templates/', { instruction_template: Number(tpl.id), name: defName, meta: {} });
        taskTpl = created?.data || null;
      }
      setExecTaskTpl(taskTpl);
      // 打开时名称：已有则用已有；否则使用默认（已在上方设置）
      if (taskTpl?.name) {
        setExecTplName(String(taskTpl.name));
      }
      // 2) 加载步骤列表
      if (taskTpl?.id) {
        const sResp = await request.get('/api/v1/instruction/exec/step-templates/', { params: { task_template: Number(taskTpl.id) } });
        const rows = Array.isArray(sResp?.data?.results) ? sResp.data.results : (Array.isArray(sResp?.data) ? sResp.data : []);
        const mapped = (rows || []).map((r:any) => ({
          ...r,
          required_metrics_json: (() => {
            try { return JSON.stringify(r.required_metrics || {}, null, 2); } catch { return '{}'; }
          })(),
          ui_schema_json: (() => {
            try {
              // 优先加载自身 ui_schema；若为空再回退到有效 ui_schema_effective，避免编辑时看到空白
              const own = r.ui_schema;
              const eff = r.ui_schema_effective;
              const src = (own && Object.keys(own || {}).length > 0) ? own : (eff || {});
              return JSON.stringify(src || {}, null, 2);
            } catch { return '{}'; }
          })(),
          action_plan_json: (() => {
            try {
              // 优先加载自身 action_plan；若为空再回退到有效 action_plan_effective
              const own = Array.isArray(r.action_plan) ? r.action_plan : [];
              const eff = Array.isArray(r.action_plan_effective) ? r.action_plan_effective : [];
              const src = (own.length > 0 ? own : eff);
              return JSON.stringify(src || [], null, 2);
            } catch { return '[]'; }
          })(),
        }));
        // 排序：stage/group/sort_order
        mapped.sort((a:any,b:any)=>{
          const sa = Number.isFinite(Number(a.stage)) ? Number(a.stage) : Number.POSITIVE_INFINITY;
          const sb = Number.isFinite(Number(b.stage)) ? Number(b.stage) : Number.POSITIVE_INFINITY;
          if (sa !== sb) return sa - sb;
          const ga = String(a.group_key || '~');
          const gb = String(b.group_key || '~');
          if (ga !== gb) return ga.localeCompare(gb);
          return Number(a.sort_order || 0) - Number(b.sort_order || 0);
        });
        setExecSteps(mapped);
      }
    } catch (e:any) {
      message.error(e?.response?.data?.detail || '加载执行动作包失败');
    } finally {
      setExecPackLoading(false);
    }
  };

  const addExecStep = () => {
    setExecSteps(prev => [
      ...prev,
      {
        id: undefined,
        task_template: execTaskTpl?.id,
        step_type: '',
        title: '',
        required: true,
        stage: 1,
        group_key: '',
        sort_order: (prev.reduce((m, x) => Math.max(m, Number(x.sort_order || 0)), 0) + 1),
        required_metrics_json: '{}',
        ui_schema_json: '{}',
        action_plan_json: '[]',
      }
    ]);
  };

  const removeExecStep = (idx: number) => {
    setExecSteps(prev => {
      const row = prev[idx];
      const next = prev.filter((_, i) => i !== idx);
      const setDel = new Set(execDeletedStepIds);
      if (row && Number.isFinite(Number(row.id))) setDel.add(Number(row.id));
      setExecDeletedStepIds(setDel);
      return next;
    });
  };

  const saveExecPack = async () => {
    if (!execTaskTpl || !execPackTpl) { setExecPackOpen(false); return; }
    setExecPackLoading(true);
    try {
      // 1) 更新动作包名称
      if (execTplName && execTplName !== execTaskTpl.name) {
        await request.patch(
          `/api/v1/instruction/exec/task-templates/${execTaskTpl.id}/`,
          {
            name: execTplName,
            meta: execTaskTpl.meta || {},
            // 兼容后端对 PUT/完整对象的校验，PATCH 也一并传递，避免字段缺失导致 400
            instruction_template: execTaskTpl.instruction_template || execPackTpl.id,
          }
        );
      }
      // 2) 删除标记的步骤
      if (execDeletedStepIds.size > 0) {
        const ids = Array.from(execDeletedStepIds.values());
        await Promise.all(ids.map(id => request.delete(`/api/v1/instruction/exec/step-templates/${id}/`).catch(()=>null)));
        setExecDeletedStepIds(new Set());
      }
      // 3) 新增/更新步骤
      for (const st of execSteps) {
        let metrics: any = {};
        let uiSchema: any = {};
        let actionPlan: any[] = [];
        try {
          const txt = String(st.required_metrics_json || '').trim();
          metrics = txt ? JSON.parse(txt) : {};
          if (metrics == null || typeof metrics !== 'object') metrics = {};
        } catch {
          message.error(`步骤【${st.title || st.step_type || '未命名'}】的指标(JSON)无效`);
          setExecPackLoading(false);
          return;
        }
        try {
          const txt = String(st.ui_schema_json || '').trim();
          uiSchema = txt ? JSON.parse(txt) : {};
          if (uiSchema == null || typeof uiSchema !== 'object') uiSchema = {};
        } catch {
          message.error(`步骤【${st.title || st.step_type || '未命名'}】的UI配置(JSON)无效`);
          setExecPackLoading(false);
          return;
        }
        try {
          const txt = String(st.action_plan_json || '').trim();
          actionPlan = txt ? JSON.parse(txt) : [];
          if (!Array.isArray(actionPlan)) actionPlan = [];
        } catch {
          message.error(`步骤【${st.title || st.step_type || '未命名'}】的动作计划(JSON)无效`);
          setExecPackLoading(false);
          return;
        }
        const payload = {
          task_template: execTaskTpl.id,
          step_type: String(st.step_type || '').trim(),
          title: String(st.title || '').trim(),
          required: !!st.required,
          required_metrics: metrics,
          ui_schema: uiSchema,
          action_plan: actionPlan,
          library_step: (st.library_step != null && st.library_step !== '') ? Number(st.library_step) : null,
          stage: Number.isFinite(Number(st.stage)) ? Number(st.stage) : null,
          group_key: String(st.group_key || '').trim() || null,
          sort_order: Number.isFinite(Number(st.sort_order)) ? Number(st.sort_order) : 0,
          meta: st.meta || {},
        };
        if (!payload.step_type) {
          message.error('请填写步骤的 step_type');
          setExecPackLoading(false);
          return;
        }
        if (Number.isFinite(Number(st.id))) {
          await request.put(`/api/v1/instruction/exec/step-templates/${Number(st.id)}/`, payload);
        } else {
          await request.post('/api/v1/instruction/exec/step-templates/', payload);
        }
      }
      message.success('已保存执行动作序列');
      // 关闭弹窗并刷新列表
      setExecPackOpen(false);
      setExecPackTpl(null);
      setExecTaskTpl(null);
      setExecSteps([]);
      fetchData();
    } catch (e:any) {
      message.error(e?.response?.data?.detail || '保存失败');
    } finally {
      setExecPackLoading(false);
    }
  };

  // 仅查看“执行动作包”步骤序列
  const openViewExecSteps = async (tpl: any) => {
    setViewExecTpl(tpl);
    setViewExecOpen(true);
    setViewExecLoading(true);
    setViewExecSteps([]);
    try {
      const resp = await request.get('/api/v1/instruction/exec/task-templates/', { params: { instruction_template: Number(tpl.id) } });
      const list = Array.isArray(resp?.data?.results) ? resp.data.results : (Array.isArray(resp?.data) ? resp.data : []);
      const taskTpl = list && list.length > 0 ? list[0] : null;
      if (!taskTpl) {
        setViewExecSteps([]);
        setViewExecLoading(false);
        return;
      }
      const sResp = await request.get('/api/v1/instruction/exec/step-templates/', { params: { task_template: Number(taskTpl.id) } });
      const rows = Array.isArray(sResp?.data?.results) ? sResp.data.results : (Array.isArray(sResp?.data) ? sResp.data : []);
      const mapped = (rows || []).map((r:any) => ({
        ...r,
        metrics_keys: (() => {
          try {
            const obj = r.required_metrics || {};
            return Object.keys(obj || {});
          } catch { return []; }
        })(),
      }));
      mapped.sort((a:any,b:any)=>{
        const sa = Number.isFinite(Number(a.stage)) ? Number(a.stage) : Number.POSITIVE_INFINITY;
        const sb = Number.isFinite(Number(b.stage)) ? Number(b.stage) : Number.POSITIVE_INFINITY;
        if (sa !== sb) return sa - sb;
        const ga = String(a.group_key || '~');
        const gb = String(b.group_key || '~');
        if (ga !== gb) return ga.localeCompare(gb);
        return Number(a.sort_order || 0) - Number(b.sort_order || 0);
      });
      setViewExecSteps(mapped);
    } catch (e:any) {
      message.error(e?.response?.data?.detail || '加载执行动作包失败');
    } finally {
      setViewExecLoading(false);
    }
  };

  const loadAttachmentTemplates = async () => {
    if (attachTplOptions.length > 0) return;
    setAttachTplLoading(true);
    try {
      const resp = await request.get('/api/v1/instruction/attachment-templates/', { params: { is_active: 1, page_size: 200 } });
      const rows = Array.isArray(resp.data?.results) ? resp.data.results : (Array.isArray(resp.data) ? resp.data : []);
      const opts = rows.map((r:any) => ({
        value: Number(r.id),
        label: `${r.name || '模板'}${r.version ? ` (${r.version})` : ''}`,
        scope: (r.scope === 'actions' ? 'actions' : 'order') as 'order'|'actions',
        action_key: (typeof r.action_key === 'string' ? r.action_key : undefined),
        version: r.version,
        file_name: r.file_name || undefined,
      }));
      setAttachTplOptions(opts);
    } catch { setAttachTplOptions([]); } finally { setAttachTplLoading(false); }
  };

  const normalizeTemplateIds = (node: any): number[] => {
    if (Array.isArray(node?.template_ids)) {
      return node.template_ids
        .map((id: any) => Number(id))
        .filter((id: number) => Number.isFinite(id));
    }
    if (node?.template_id != null && node.template_id !== '') {
      const id = Number(node.template_id);
      return Number.isFinite(id) ? [id] : [];
    }
    return [];
  };

  const downloadTemplate = async (id: number, name?: string) => {
    try {
      const resp = await request.get(`/api/v1/instruction/attachment-templates/${id}/download/`, { responseType: 'blob' as any });
      const blob: Blob = resp.data as any;
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      const disposition = String(resp?.headers?.['content-disposition'] || '');
      const fileNameMatch = disposition.match(/filename\*?=(?:UTF-8''|")?([^\";]+)(?:\"|;|$)/i);
      const headerName = fileNameMatch ? decodeURIComponent(fileNameMatch[1]) : '';
      a.href = url;
      a.download = (headerName && headerName.trim()) ? headerName.trim() : ((name && name.trim()) ? name.trim() : `附件模板-${id}`);
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e:any) {
      message.error('下载附件模板失败');
    }
  };

  const openUploadTemplateForRow = (idx: number) => {
    setUploadTplRowIdx(idx);
    setUploadTplFiles([]);
    setUploadTplTips('');
    setUploadTplOpen(true);
  };

  const doUploadTemplate = async () => {
    if (uploadTplRowIdx == null) { setUploadTplOpen(false); return; }
    const file = uploadTplFiles[0]?.originFileObj as File | undefined;
    if (!file) { message.warning('请先选择文件'); return; }
    const row = attachmentRows[uploadTplRowIdx];
    const scope = row.scope;
    const actionKey = scope === 'actions' ? (row.action_key && row.action_key.trim() ? row.action_key.trim() : 'ALL') : '';
    const fd = new FormData();
    const name = `${row.label || '附件模板'}-模板`;
    fd.append('name', name);
    fd.append('scope', scope);
    if (scope === 'actions') fd.append('action_key', actionKey);
    fd.append('file', file);
    try {
      const resp = await request.post('/api/v1/instruction/attachment-templates/', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      const tpl = resp.data || {};
      setAttachmentRows(prev => prev.map((x, i) => {
        if (i !== uploadTplRowIdx) return x;
        const prevIds = Array.isArray((x as any).template_ids)
          ? (x as any).template_ids.map((id:any)=>Number(id)).filter((id:number)=>Number.isFinite(id))
          : ((x as any).template_id != null ? [Number((x as any).template_id)].filter((id:number)=>Number.isFinite(id)) : []);
        const nextIds = Array.from(new Set([...prevIds, Number(tpl.id)].filter((id)=>Number.isFinite(id))));
        return { ...x, template_ids: nextIds, template_id: nextIds.length === 1 ? nextIds[0] : undefined };
      }));
      // 追加到选项，便于后续下拉选择
      setAttachTplOptions(prev => [{ value: Number(tpl.id), label: `${tpl.name || '模板'}${tpl.version ? ` (${tpl.version})` : ''}`, scope, action_key: (tpl.action_key || undefined), version: tpl.version }, ...prev]);
      message.success('已上传模板并绑定到此规则');
      setUploadTplOpen(false);
    } catch (e:any) {
      message.error(e?.response?.data?.detail || '上传失败');
    }
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const baseParams: any = {};
      if (filterStatus === 'active') baseParams.is_active = true;
      else if (filterStatus === 'inactive') baseParams.is_active = false;
      if (filterLevel !== 'all') baseParams.level = filterLevel;
      if (Number.isFinite(Number(filterCustomerId))) baseParams.customer_id = Number(filterCustomerId);

      // 全量拉取模板（前端分页）
      const fetchAllTemplates = async (params: any): Promise<any[]> => {
        const pageSize = 200;
        let page = 1;
        let total = Infinity;
        const all: any[] = [];
        while ((page - 1) * pageSize < total) {
          const resp = await request.get('/api/v1/instruction/templates/', { params: { ...params, page, page_size: pageSize } });
          const payload = resp?.data || {};
          const rows = Array.isArray(payload?.results) ? payload.results : (Array.isArray(payload) ? payload : []);
          const count = Number(payload?.count ?? (Array.isArray(payload) ? rows.length : 0));
          if (Number.isFinite(count)) total = count;
          all.push(...rows);
          if (!rows || rows.length < pageSize) break;
          page += 1;
          // 安全阈值，避免异常导致死循环
          if (page > 1000) break;
        }
        return all;
      };

      const [tRows, actionsResp] = await Promise.all([
        fetchAllTemplates(baseParams),
        request.get('/api/v1/billing/operation-types/', { params: { all: 1 } })
      ]);
      const aPayload = actionsResp.data;
      const aRows = Array.isArray(aPayload) ? aPayload : (Array.isArray(aPayload?.results) ? aPayload.results : []);
      setData(tRows);
      setOpTypes(aRows);
      // 预取前若干条的执行动作包步骤数，避免列表初次为空
      try {
        const firstIds = tRows.slice(0, 50).map((t:any)=> Number(t.id)).filter((x:number)=> Number.isFinite(x));
        await ensureExecPackCounts(firstIds);
      } catch {}
      // 载入就绪状态（批量）：避免重复请求相同ID集合
      const ids = tRows.map((t:any)=> Number(t.id)).filter((x:number)=> Number.isFinite(x));
      const key = JSON.stringify(ids.sort((a:number,b:number)=>a-b));
      (fetchData as any).__lastReadyKey = (fetchData as any).__lastReadyKey || '';
      if ((fetchData as any).__lastReadyKey === key) {
        // 同一页签/相同结果集不重复请求
      } else {
        (fetchData as any).__lastReadyKey = key;
        try {
          if (ids.length > 0) {
            const resp = await request.post('/api/v1/instruction/templates/ready-status/', { ids });
            const arr = Array.isArray(resp?.data) ? resp.data : [];
            const map: Record<number, { ready: boolean; locked?: boolean }> = {};
            arr.forEach((row:any) => {
              const id = Number(row?.id);
              if (Number.isFinite(id)) {
                map[id] = { ready: !!row?.ready, locked: !!row?.locked };
              }
            });
            setGenStatusByTplId(prev => {
              const next = { ...prev };
              Object.keys(map).forEach((k)=> { next[Number(k)] = { ...(next[Number(k)]||{}), ...map[Number(k)] }; });
              return next;
            });
          }
        } catch {}
      }
    } catch (e: any) {
      message.error('加载失败');
    } finally {
      setLoading(false);
    }
  };

  // 确保加载指定模板ID集合的执行动作包步骤数
  const ensureExecPackCounts = async (ids: number[]) => {
    const pending: number[] = [];
    ids.forEach(id => {
      if (execPackCountByTplId[id] === undefined) pending.push(id);
    });
    if (pending.length === 0) return;
    // 顺序拉取，避免同时过多请求；每条仅两次轻请求
    for (const id of pending) {
      try {
        const tResp = await request.get('/api/v1/instruction/exec/task-templates/', { params: { instruction_template: id } });
        const list = Array.isArray(tResp?.data?.results) ? tResp.data.results : (Array.isArray(tResp?.data) ? tResp.data : []);
        const taskTpl = list && list.length > 0 ? list[0] : null;
        if (!taskTpl) {
          setExecPackCountByTplId(prev => ({ ...prev, [id]: undefined }));
          continue;
        }
        const sResp = await request.get('/api/v1/instruction/exec/step-templates/', { params: { task_template: Number(taskTpl.id), page: 1, page_size: 1 } });
        const count = Number((sResp?.data && (sResp.data.count ?? ((Array.isArray(sResp.data?.results) ? sResp.data.results.length : (Array.isArray(sResp.data) ? sResp.data.length : 0))))) ?? 0);
        setExecPackCountByTplId(prev => ({ ...prev, [id]: (Number.isFinite(count) ? count : 0) }));
      } catch {
        setExecPackCountByTplId(prev => ({ ...prev, [id]: undefined }));
      }
    }
  };

  useEffect(() => { fetchData(); }, [filterStatus, filterLevel, filterCustomerId]);

  // 载入客户/项目映射用于列表可读展示
  useEffect(() => {
    (async () => {
      try {
        const list = await getCustomerList({ onlyMain: true });
        const map: Record<number, string> = {};
        (list || []).forEach((c: any) => {
          const id = Number(c.id);
          if (!Number.isNaN(id)) {
            const label = `${c.name || c.username || ''}${(c.customer_code || c.profile?.customer_code) ? ' · ' + (c.customer_code || c.profile?.customer_code) : ''}`.trim();
            map[id] = label || String(id);
          }
        });
        setCustomerLabelById(map);
      } catch {}
    })();
    (async () => {
      try {
        // 拉取尽量多的项目用于映射（如需可改为分页聚合）
        const resp: any = await getProjectList({ page: 1, page_size: 1000 });
        const results = Array.isArray(resp?.data?.results) ? resp.data.results : (Array.isArray(resp?.data) ? resp.data : (Array.isArray(resp) ? resp : []));
        const map: Record<number, string> = {};
        (results || []).forEach((p: any) => {
          const id = Number(p.id);
          if (!Number.isNaN(id)) {
            const code = p.project_code || p.code || id;
            const name = p.name || '';
            map[id] = name ? `${code} - ${name}` : String(code);
          }
        });
        setProjectLabelById(map);
      } catch {}
    })();
  }, []);

  // 详情弹窗：仅在打开并且有记录时拉取“有效计费规则”映射，避免在渲染过程中请求导致循环渲染
  useEffect(() => {
    if (!detailOpen || !detailRecord) return;
    let cancelled = false;
    (async () => {
      try {
        const map = await fetchRuleBasisMap(detailRecord);
        if (!cancelled) setRuleBasisByOpId(map);
      } catch {}
    })();
    return () => { cancelled = true; };
  }, [detailOpen, detailRecord]);

  // 加载单个模板详情（确保拿到最新 actions/attachments）
  const fetchTemplateDetail = async (id: number) => {
    try {
      const res = await request.get(`/api/v1/instruction/templates/${id}/`);
      return res.data;
    } catch {
      return null;
    }
  };

  // 根据已配置的收费规则推导“专属操作类型”，弥补未维护 OperationTypeScope 的情况
  const loadEffectiveOps = async () => {
    try {
      const v = form.getFieldsValue();
      const lvl = v.level;
      const cid = v.customer_id;
      const pid = v.project_id;
      if (lvl === 'project' && cid && pid) {
        const resp = await request.get('/api/v1/billing/billing-rules/effective/', { params: { customer_id: cid, project_id: pid } });
        const rows = Array.isArray(resp.data) ? resp.data : [];
        const ids = Array.from(new Set(rows.filter((r:any)=>r.rule_level==='project').map((r:any)=>Number(r.operation_type)).filter((x:number)=>!Number.isNaN(x))));
        setExtraScopedOpIds(ids);
      } else if (lvl === 'customer' && cid) {
        const resp = await request.get('/api/v1/billing/billing-rules/effective/', { params: { customer_id: cid } });
        const rows = Array.isArray(resp.data) ? resp.data : [];
        const ids = Array.from(new Set(rows.filter((r:any)=>r.rule_level==='customer').map((r:any)=>Number(r.operation_type)).filter((x:number)=>!Number.isNaN(x))));
        setExtraScopedOpIds(ids);
      } else {
        setExtraScopedOpIds([]);
      }
    } catch { setExtraScopedOpIds([]); }
  };

  // 操作类型分组（系统级 vs 客户/项目级），严格按所选作用域过滤
  const opTypeGroups = useMemo(() => {
    const v = form.getFieldsValue();
    const level = v.level;
    const cid = v.customer_id;
    const pid = v.project_id;
    const list = Array.isArray(opTypes) ? opTypes : [];
    const sysOps = list.filter((ot: any) => {
      const scopes = Array.isArray(ot.scopes) ? ot.scopes : [];
      // 系统级判定：显式 system 作用域或无作用域记录
      return scopes.length === 0 || scopes.some((s: any) => s.scope_level === 'system');
    });
    let scopedOps: any[] = [];
    if (level === 'customer') {
      if (cid) {
        // 客户级：仅显示当前客户的客户级操作类型
        scopedOps = list.filter((ot: any) => (Array.isArray(ot.scopes) ? ot.scopes : []).some((s: any) => s.scope_level === 'customer' && Number(s.customer_id) === Number(cid)));
      }
    } else if (level === 'project') {
      if (cid && pid) {
        // 项目级：需要显示 系统级 + 当前客户的客户级 + 当前项目的项目级
        const customerScoped = list.filter((ot: any) => (Array.isArray(ot.scopes) ? ot.scopes : []).some((s: any) => s.scope_level === 'customer' && Number(s.customer_id) === Number(cid)));
        const projectScoped = list.filter((ot: any) => (Array.isArray(ot.scopes) ? ot.scopes : []).some((s: any) => s.scope_level === 'project' && Number(s.customer_id) === Number(cid) && Number(s.project_id) === Number(pid)));
        const map = new Map<number, any>();
        customerScoped.forEach((o:any)=> { const id = Number(o.id); if (!Number.isNaN(id)) map.set(id, o); });
        projectScoped.forEach((o:any)=> { const id = Number(o.id); if (!Number.isNaN(id)) map.set(id, o); });
        scopedOps = Array.from(map.values());
      }
    }
    // 兜底：把从“有效规则”推导的操作类型加入专属集合（去重）
    if (Array.isArray(extraScopedOpIds) && extraScopedOpIds.length > 0) {
      const map = new Map(scopedOps.map((x:any)=>[Number(x.id), x]));
      extraScopedOpIds.forEach((id:number) => {
        const found = list.find((x:any)=>Number(x.id)===Number(id));
        if (found) map.set(Number(id), found);
      });
      scopedOps = Array.from(map.values());
    }
    // 若是系统级，仅显示系统级
    if (level === 'system') return { sys: sysOps, scoped: [] };
    // 客户/项目级：系统级 + 当前作用域限定的专属
    return { sys: sysOps, scoped: scopedOps };
  }, [opTypes, form, extraScopedOpIds]);

  // 操作类型 id → 名称映射（用于附件规则下拉展示）
  const opIdToName = useMemo(() => {
    const m = new Map<number, string>();
    try {
      (opTypes || []).forEach((o: any) => {
        const id = Number(o.id);
        if (!Number.isNaN(id)) m.set(id, `${o.name} (${o.system_code})`);
      });
    } catch {}
    return m;
  }, [opTypes]);

  // 选择客户后加载项目选项
  const ensureCustomers = async () => {
    if (customerOptions.length > 0) return;
    setCustomerLoading(true);
    try {
      const list = await getCustomerList({ onlyMain: true });
      const opts = (list || []).map((c: any) => ({ value: Number(c.id), label: `${c.name || c.username} · ${c.customer_code || c.profile?.customer_code || ''}`.trim().replace(/ · $/, '') }));
      setCustomerOptions(opts);
    } catch { setCustomerOptions([]); } finally { setCustomerLoading(false); }
  };
  const searchProjects = async (search?: string) => {
    const v = form.getFieldsValue();
    const cid = v.customer_id;
    if (!cid) { setProjectOptions([]); return; }
    setProjectLoading(true);
    try {
      const resp = await getProjectList({ page: 1, page_size: 100, search: search || '', customer: cid });
      const results = Array.isArray((resp.data as any)?.results) ? (resp.data as any).results : [];
      setProjectOptions(results.map((p: any) => ({ value: Number(p.id), label: `${p.project_code || p.id} - ${p.name || ''}` })));
    } catch { setProjectOptions([]); } finally { setProjectLoading(false); }
  };

  const addTypeAction = () => setTypeActions(prev => [...prev, ({
    operation_type_id: undefined,
    variant_key: undefined,
    sort_order: undefined,
    stage: undefined,
    group_key: undefined,
    source_priority: [],
    metric_sources: {},
    quantity_semantic: undefined,
    on_missing: undefined,
    applicability_waybill_mode_in: [],
    applicability_waybill_mode_not_in: [],
    applicability_waybill_source_in: [],
    applicability_is_pack_required_in: [],
    applicability_estimated_packing_method_in: [],
    applicability_target_source_in: [],
    applicability_target_source_not_in: [],
  } as any)]);
  const loadVariantKeys = async (opId?: number) => {
    const v = form.getFieldsValue();
    if (!opId) return;
    try {
      const params: any = { operation_type: opId };
      if (v.level === 'project' && v.customer_id && v.project_id) {
        params.rule_level = 'project'; params.customer_id = v.customer_id; params.project_id = v.project_id;
      } else if (v.level === 'customer' && v.customer_id) {
        params.rule_level = 'customer'; params.customer_id = v.customer_id;
      } else if (v.level === 'system') {
        params.rule_level = 'system';
      }
      const resp = await request.get('/api/v1/billing/billing-rules/variant-keys/', { params });
      const arr = Array.isArray(resp.data?.variant_keys) ? resp.data.variant_keys : [];
      setVariantOptionsByOp(prev => ({ ...prev, [opId]: arr }));
    } catch { /* ignore */ }
  };

  const removeTypeAction = (idx: number) => setTypeActions(prev => prev.filter((_, i) => i !== idx));

  const handleOk = async () => {
    let values: any;
    try {
      values = await form.validateFields();
    } catch (vf: any) {
      try {
        const errs = Array.isArray(vf?.errorFields)
          ? vf.errorFields.map((f: any) => (Array.isArray(f?.errors) && f.errors[0]) || '校验失败').filter(Boolean)
          : [];
        message.error(`请完善必填项${errs.length ? `：${errs.slice(0, 3).join('、')}${errs.length > 3 ? ' 等' : ''}` : ''}`);
      } catch {
        message.error('请完善必填项');
      }
      return;
    }
    // 必填校验：stage 与 group_key
    const invalid = typeActions.some(ta => !(Number.isFinite(Number(ta.stage)) && String(ta.group_key || '').trim().length > 0));
    if (invalid) {
      message.error('请为每个动作填写阶段(stage)与分组(group)；这将决定并行/串行关系');
      return;
    }
    const actionsForPost = typeActions.map((ta) => {
      const meta = buildBillingMetaForAction(ta);
      return {
        operation_type_id: ta.operation_type_id,
        variant_key: ta.variant_key,
        sort_order: ta.sort_order,
        stage: Number(ta.stage || 1),
        group_key: String(ta.group_key || '').trim(),
        meta,
      };
    });
    try {
      // 将可视化行组装为 JSON 结构
      let attachReq: any = undefined;
      if (attachmentRows && attachmentRows.length > 0) {
        const orderArr: any[] = [];
        const actionsMap: Record<string, any[]> = {};
        attachmentRows.forEach((r, rowIdx) => {
          const tplIds = Array.isArray((r as any).template_ids)
            ? (r as any).template_ids.map((id: any) => Number(id)).filter((id: number) => Number.isFinite(id))
            : ((r as any).template_id != null ? [Number((r as any).template_id)].filter((id: number) => Number.isFinite(id)) : []);
          const showIfKey = String((r as any)?.show_if?.key || '').trim();
          const showIfOp = ((r as any)?.show_if?.op || 'eq') as any;
          const showIfVal = (r as any)?.show_if?.value_text;
          const node = {
            label: r.label || undefined,
            types: Array.isArray(r.types) ? r.types : [],
            min: (r.min ?? undefined),
            max: (r.max ?? undefined),
            tips: r.tips || undefined,
            template_id: tplIds.length === 1 ? tplIds[0] : undefined,
            template_ids: tplIds.length > 0 ? tplIds : undefined,
            printable: !!(r as any).printable,
            sort_order: (rowIdx + 1),
            show_if: showIfKey ? { key: showIfKey, op: showIfOp, value: parseShowIfValue(showIfVal) } : undefined,
          };
          if (r.scope === 'order') {
            orderArr.push(node);
          } else {
            const key = r.action_key && r.action_key.trim() ? r.action_key.trim() : 'ALL';
            if (!actionsMap[key]) actionsMap[key] = [];
            actionsMap[key].push(node);
          }
        });
        attachReq = {} as any;
        if (orderArr.length > 0) attachReq.order = orderArr;
        if (Object.keys(actionsMap).length > 0) attachReq.actions = actionsMap;
        if (Object.keys(attachReq).length === 0) attachReq = undefined;
      }
      // 组装问卷（问题配置）
      let qnReq: any = undefined;
      if (Array.isArray(qRows) && qRows.length > 0) {
        const qs = qRows.filter(Boolean).map((r) => {
          const base:any = {
            key: (r.key || '').trim(),
            label: r.label,
            type: r.type,
            required: !!r.required,
            tips: r.tips,
            placeholder: r.placeholder,
          };
          if (r.type === 'select' || r.type === 'multiselect') {
            base.options = (r.options || []).filter(x=>x && String(x.value).trim()).map(x=>({ value: String(x.value).trim(), label: x.label || String(x.value).trim() }));
            if (r.options_source && r.options_source.type === 'model') {
              base.options_source = {
                type: 'model',
                model: r.options_source.model || 'logistics_provider',
                value_field: r.options_source.value_field || 'code',
                label_field: r.options_source.label_field || 'code',
              };
            }
          }
          if (r.type === 'number') {
            if (r.min != null) base.min = r.min;
            if (r.max != null) base.max = r.max;
            if (r.step != null) base.step = r.step;
            if (r.unit) base.unit = r.unit;
          }
          if (r.type === 'text' || r.type === 'textarea') {
            if (r.max_length != null) base.max_length = r.max_length;
          }
          // 默认值（可选）：按题型写入 questionnaire.questions[*].default
          try {
            if (r.type === 'boolean') {
              if (typeof r.default === 'boolean') base.default = r.default;
            } else if (r.type === 'number') {
              const n = Number(r.default);
              if (Number.isFinite(n)) base.default = n;
            } else if (r.type === 'select') {
              const s = String(r.default ?? '').trim();
              if (s) base.default = s;
            } else if (r.type === 'multiselect') {
              const arr = Array.isArray(r.default) ? r.default : [];
              const vals = arr.map((x:any)=> String(x ?? '').trim()).filter(Boolean);
              if (vals.length > 0) base.default = vals;
            } else if (r.type === 'text' || r.type === 'textarea') {
              const s = String(r.default ?? '');
              // 允许默认值为空串（显式清空），因此只要不是 undefined/null 就写入
              if (r.default !== undefined && r.default !== null) base.default = s;
            }
          } catch {}
          // 显示条件（可选）：支持单条件与复合条件（AND/OR）
          try {
            const showIf = buildQuestionShowIfPayload(r);
            if (showIf) base.show_if = showIf;
          } catch {}
          // 条件必填（可选）：按题型写入 questionnaire.questions[*].required_if
          try {
            const k = String(r?.required_if?.key || '').trim();
            if (k) {
              const op = (r?.required_if?.op || 'eq') as any;
              const reqIf: any = { key: k, op };
              if (!['is_true', 'is_false', 'is_empty', 'not_empty'].includes(op)) {
                const parsed = parseShowIfValue(r?.required_if?.value_text);
                if (parsed !== undefined) reqIf.value = parsed;
              }
              base.required_if = reqIf;
            }
          } catch {}
          return base;
        }).filter((x:any)=>x.key && x.type);
        if (qs.length > 0) qnReq = { questions: qs };
      }
      // 组装基准数据规范（模板级）
      let baselineReq: any = undefined;
      try {
        if (Array.isArray(baselineItems) && baselineItems.length > 0) {
          const items = baselineItems.map((it) => {
            const out:any = {
              code: String(it.code || '').trim(),
              name: String(it.name || '').trim(),
              required: !!it.required,
              formats: Array.isArray(it.formats) ? it.formats.filter(Boolean) : [],
              min_files: (it.min_files != null ? Number(it.min_files) : undefined),
              max_files: (it.max_files != null ? Number(it.max_files) : undefined),
              bind: (() => {
                const b:any = it.bind || {};
                const r:any = {};
                if (Number.isFinite(Number(b.stage))) r.stage = Number(b.stage);
                if (b.group_key && String(b.group_key).trim()) r.group_key = String(b.group_key).trim();
                if (Array.isArray(b.steps)) r.steps = (b.steps as string[]).map(s=>String(s).trim()).filter(Boolean);
                return r;
              })(),
            };
            if (it.schema_hint_json && String(it.schema_hint_json).trim()) {
              try {
                out.schema_hint = JSON.parse(String(it.schema_hint_json));
              } catch {
                throw new Error(`基准数据规范【${out.code || out.name || '未命名'}】的 schema JSON 无效`);
              }
            }
            return out;
          }).filter((x:any)=> x.code);
          baselineReq = items.length > 0 ? { items } : undefined;
        }
      } catch (e:any) {
        message.error(e?.message || '基准数据规范无效');
        return;
      }
      // 保存前校验：所有引用的模板是否存在且可用
      if (attachReq) {
        const collectIds = new Set<number>();
        const collectFrom = (node:any) => {
          if (!node) return;
          if (Array.isArray(node)) node.forEach(collectFrom);
          else if (typeof node === 'object') {
            if (node.template_id != null) {
              const n = Number(node.template_id);
              if (!Number.isNaN(n)) collectIds.add(n);
            }
            if (Array.isArray(node.template_ids)) {
              node.template_ids.forEach((id:any) => {
                const n = Number(id);
                if (!Number.isNaN(n)) collectIds.add(n);
              });
            }
            Object.values(node).forEach(collectFrom);
          }
        };
        collectFrom(attachReq);
        if (collectIds.size > 0) {
          const ids = Array.from(collectIds);
          const results = await Promise.all(ids.map(async (id) => {
            try {
              const resp = await request.get(`/api/v1/instruction/attachment-templates/${id}/`);
              const active = !!(resp?.data?.is_active);
              return { id, ok: active };
            } catch { return { id, ok: false }; }
          }));
          const invalid = results.filter(r => !r.ok).map(r => r.id);
          if (invalid.length > 0) {
            message.error(`引用的附件模板不存在或未启用：id=${invalid.join(', ')}`);
            return;
          }
        }
      }
      const baseMeta = (editingTpl && editingTpl.meta && typeof editingTpl.meta === 'object') ? ({ ...editingTpl.meta } as any) : {};
      const invMeta = { ...(baseMeta.inventory && typeof baseMeta.inventory === 'object' ? baseMeta.inventory : {}) } as any;
      if (inventoryConsumeCfg.enabled) {
        const applicability: any = {};
        if (inventoryConsumeCfg.waybill_source_in.length > 0) applicability.waybill_source_in = inventoryConsumeCfg.waybill_source_in;
        if (inventoryConsumeCfg.waybill_mode_in.length > 0) applicability.waybill_mode_in = inventoryConsumeCfg.waybill_mode_in;
        if (inventoryConsumeCfg.waybill_mode_not_in.length > 0) applicability.waybill_mode_not_in = inventoryConsumeCfg.waybill_mode_not_in;
        if (inventoryConsumeCfg.is_pack_required_in.length > 0) applicability.is_pack_required_in = inventoryConsumeCfg.is_pack_required_in;
        invMeta.final_review_finalize = {
          mode: inventoryConsumeCfg.mode || 'consume_reserved',
          applicability,
        };
        // 兼容旧字段：仅在“转消耗”模式下保留 old consume 配置
        if ((inventoryConsumeCfg.mode || 'consume_reserved') === 'consume_reserved') {
          invMeta.final_review_consume = {
            enabled: true,
            applicability,
          };
        } else {
          delete invMeta.final_review_consume;
        }
      } else {
        delete invMeta.final_review_finalize;
        delete invMeta.final_review_consume;
      }
      const iocKinds = Array.from(new Set(
        (Array.isArray(inventoryConsumeCfg.ioc_consume_reservation_kinds) ? inventoryConsumeCfg.ioc_consume_reservation_kinds : [])
          .map((x: any) => String(x || '').trim().toLowerCase())
          .filter((x: string) => x === 'execution' || x === 'business_hold')
      ));
      if (iocKinds.length > 0) {
        invMeta.regular_outbound_ioc_consume = {
          reservation_kinds: iocKinds,
        };
      } else {
        delete invMeta.regular_outbound_ioc_consume;
      }
      if (Object.keys(invMeta).length > 0) baseMeta.inventory = invMeta;
      else delete baseMeta.inventory;

      if (editingTpl && editingTpl.id) {
        // 编辑分支：同时提交 actions；当附件/问卷为空时也提交空结构以触发清空
        const attachReqFinal = (attachReq === undefined ? {} : attachReq);
        const qnReqFinal = (qnReq === undefined ? { questions: [] } : qnReq);
        await request.put(`/api/v1/instruction/templates/${editingTpl.id}/`, {
          ...values,
          meta: baseMeta,
          attachment_requirements: attachReqFinal,
          questionnaire: qnReqFinal,
          actions: actionsForPost,
          ...(baselineReq ? { baseline_requirements: baselineReq } : { baseline_requirements: { items: [] } }),
        });
        message.success('已更新');
      } else {
        await request.post('/api/v1/instruction/templates/', {
          ...values,
          meta: baseMeta,
          attachment_requirements: attachReq,
          questionnaire: qnReq,
          actions: actionsForPost,
          ...(baselineReq ? { baseline_requirements: baselineReq } : {}),
        });
        message.success('已保存');
      }
      setVisible(false);
      setTypeActions([]);
      setAttachmentRows([]);
      setQRows([]);
      setBaselineItems([]);
      setInventoryConsumeCfg({
        enabled: false,
        mode: 'consume_reserved',
        ioc_consume_reservation_kinds: ['execution', 'business_hold'],
        waybill_source_in: [],
        waybill_mode_in: [],
        waybill_mode_not_in: [],
        is_pack_required_in: [],
      });
      setEditingTpl(null);
      fetchData();
    } catch (e: any) {
      const detail = e?.response?.data?.detail || e?.message || '保存失败';
      message.error(String(detail));
    }
  };

  const openDeleteConfirm = (r: any) => {
    Modal.confirm({
      title: <div style={{ color: '#141414' }}>删除指令模板</div>,
      content: (
        <div style={{ whiteSpace: 'pre-line', color: '#141414' }}>
          {`将删除：${r.name || ''}（${r.system_code || ''}）。此操作不可恢复。\n提示：若已有指令引用该模板将无法删除。`}
        </div>
      ),
      okText: '确认删除',
      cancelText: '取消',
      icon: null,
      centered: true,
      okButtonProps: { style: { background: '#1677ff', color: '#fff' } },
      cancelButtonProps: { style: { color: '#141414' } },
      modalRender: (node) => (
        <div style={{ background: '#ffffff', color: '#141414', borderRadius: 8 }}>{node}</div>
      ),
      onOk: async () => {
        try {
          await request.delete(`/api/v1/instruction/templates/${r.id}/`);
          message.success('已删除指令模板');
          fetchData();
        } catch (e: any) {
          const resp = e?.response;
          const data = resp?.data || {};
          if (data?.detail === 'template_in_use') {
            const cntTpl = Number.isFinite(Number(data?.referenced_by_instructions)) ? Number(data.referenced_by_instructions) : undefined;
            const cntAct = Number.isFinite(Number(data?.referenced_by_instruction_actions)) ? Number(data.referenced_by_instruction_actions) : undefined;
            const lines: string[] = [];
            lines.push('该指令模板正被引用，无法直接删除。');
            if (cntTpl != null || cntAct != null) {
              lines.push(`引用情况：指令=${cntTpl ?? '-'}，指令动作=${cntAct ?? '-'}`);
            }
            lines.push('可选择先清理引用，再删除；或点击“禁用模板”将其设为不可用。');
            Modal.confirm({
              title: <div style={{ color: '#141414' }}>无法删除 - 模板被引用</div>,
              content: <div style={{ whiteSpace: 'pre-line', color: '#141414' }}>{lines.join('\n')}</div>,
              okText: '禁用模板',
              cancelText: '关闭',
              icon: null,
              centered: true,
              okButtonProps: { style: { background: '#1677ff', color: '#fff' } },
              cancelButtonProps: { style: { color: '#141414' } },
              onOk: async () => {
                try {
                  await request.post(`/api/v1/instruction/templates/${r.id}/archive/`, {});
                  message.success('已禁用该指令模板');
                  fetchData();
                } catch (e2: any) {
                  message.error(e2?.response?.data?.detail || '禁用失败');
                }
              }
            });
            return;
          }
          const detail = data?.message || data?.detail || e?.message || '删除失败';
          message.error(String(detail));
        }
      },
    });
  };

  return (
    <div className="" style={{ width: '100%', maxWidth: '100%' }}>
      <Card style={{ width: '100%' }} bodyStyle={{ padding: 12 }} headStyle={{ padding: '8px 12px' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
            <Typography.Title level={4} style={{ margin: 0 }}>指令模板列表</Typography.Title>
            <Tag color="blue" style={{ fontSize: 14, padding: '6px 8px' }}>共 {data.length} 条</Tag>
          </div>
          <Space>
            <HelpHintButton onClick={() => Modal.info({
              title: '说明：指令模板 · 工单模板 · 执行动作序列',
              icon: null,
              centered: true,
              okText: '知道了',
              width: 880,
              modalRender: (node) => (<div style={{ background: '#ffffff', color: '#141414', borderRadius: 8 }}>{node}</div>),
              content: (
                <div style={{ whiteSpace: 'pre-line', color: '#141414' }}>
{`一、三者定位
1) 指令模板（InstructionTemplate）【面向客户/业务配置】
   - 定义业务套餐：名称、问题集、附件要求、动作组合（OperationType + 变体键）等
   - 审核受理时会拍成模板快照（含 system_code）

2) 工单模板（WorkOrderTemplate）【面向计费/对账】
   - 绑定 operation_type，承载计费口径与清单（checklist/附件要求等）
   - 路由（WorkOrderRoute）按“指令模板编码”映射出要生成的工单模板集合，可含依赖（DAG）
   - 受理后生成“虚拟工单（kind=billing）”，用于计费与对账；员工端默认不展示

3) 执行动作序列（ExecutionTaskTemplate + ExecutionStepTemplate）【面向仓库执行】
   - 1:1 绑定到“指令模板”，定义步骤（step_type/title/required）、阶段与分组（stage/group）以及需要采集的指标（required_metrics）
   - 员工按此序列执行与上报；步骤提交的指标会回填到对应虚拟工单执行指标（execution_metrics）
   - 配置执行动作序列时，可从“仓库操作步骤库”中导入预设的“标准步骤”，按需覆写少量差异，快速复用

二、运行时关系（审核受理后）
1) 生成“执行任务与步骤实例” → 员工按步骤执行
2) 生成“虚拟工单（计费行）” → 用于规则匹配、重算与对账
3) 步骤提交指标 → 回填虚拟工单 → 命中收费规则 → 形成金额（草稿/发布）

三、命名与对齐建议
- 计费相关步骤：step_type 与 OperationType 语义保持一致（如 RELABEL、BOX_PACK），指标键名须与规则口径一致（label_count/box_count/volume_cc/weight_g/work_time_min）
- 非计费步骤（准备/收尾）：用独立 step_type（PRINT_PICK_LIST、PICK、QC、PHOTO、HANDOVER），required_metrics 可为空 {}

四、并行与顺序
- 同一 stage、不同 group → 并行；同一 stage、相同 group → 按组内顺序（sort_order）串行；不同 stage → 严格串行

五、推荐实践（换标+装箱）
- 阶段2 并行：RELABEL（换标，指标：label_count）与 BOX_PACK（装箱，指标：box_count/volume_cc/weight_g）
- UI 可做“合并录入”，底层依次提交两步，保证审计与计费颗粒度清晰`}
                </div>
              )
            })}>说明</HelpHintButton>
            <Button onClick={fetchData}>刷新</Button>
            <Button type="primary" onClick={() => { form.resetFields(); setTypeActions([]); setAttachmentRows([]); setBaselineItems([]); setQRows([]); setEditingTpl(null); setInventoryConsumeCfg({ enabled: false, mode: 'consume_reserved', ioc_consume_reservation_kinds: ['execution', 'business_hold'], waybill_source_in: [], waybill_mode_in: [], waybill_mode_not_in: [], is_pack_required_in: [] }); form.setFieldsValue({ level: 'system', is_active: true }); setVisible(true); }}>新增指令模板</Button>
          </Space>
        </div>
        {/* 筛选行 */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
          <Select
            placeholder="所属客户"
            allowClear
            onClear={() => setFilterCustomerId(undefined)}
            value={filterCustomerId as any}
            style={{ width: 220 }}
            showSearch
            loading={customerLoading}
            onFocus={ensureCustomers}
            options={customerOptions}
            optionFilterProp="label"
            onChange={(cid) => setFilterCustomerId(Number.isFinite(Number(cid)) ? Number(cid) : undefined)}
          />
          <Select
            value={filterStatus}
            style={{ width: 140 }}
            options={[
              { value: 'all', label: '状态：全部' },
              { value: 'active', label: '状态：启用' },
              { value: 'inactive', label: '状态：禁用' },
            ]}
            onChange={(v) => setFilterStatus(v as any)}
          />
          <Select
            value={filterLevel}
            style={{ width: 160 }}
            options={[
              { value: 'all', label: '层级：全部' },
              { value: 'system', label: '层级：系统级' },
              { value: 'customer', label: '层级：客户级' },
              { value: 'project', label: '层级：项目级' },
            ]}
            onChange={(v) => setFilterLevel(v as any)}
          />
          <Button onClick={() => { setFilterCustomerId(undefined); setFilterStatus('all'); setFilterLevel('all'); }}>重置</Button>
        </div>

        <Table rowKey="id" loading={loading} dataSource={data} columns={[
          { title: '系统编码', dataIndex: 'system_code' },
          { title: '名称', dataIndex: 'name', width: 220 },
          { title: '层级', dataIndex: 'level' },
          { title: '客户', key: 'customer', width: 150, render: (_: any, r: any) => {
            const cid = Number(r.customer_id);
            const mapped = !Number.isNaN(cid) ? customerLabelById[cid] : undefined;
            return mapped || r.customer_name || r.customer || r.customer_username || r.customer_code || r.customer_display || r.customer_id || '-';
          } },
          { title: '项目', key: 'project', width: 150, render: (_: any, r: any) => {
            const pid = Number(r.project_id);
            const mapped = !Number.isNaN(pid) ? projectLabelById[pid] : undefined;
            if (mapped) {
              try {
                // 拆成名称和编码（格式：CODE - NAME 或 NAME - CODE），尽量解析
                const parts = String(mapped).split(' - ');
                if (parts.length >= 2) {
                  const code = parts[0];
                  const name = parts.slice(1).join(' - ');
                  return (
                    <Space direction="vertical" size={0}>
                      <span>{name || '-'}</span>
                      <span style={{ color: '#1677ff' }}>{code || '-'}</span>
                    </Space>
                  );
                }
              } catch {}
            }
            const code = r.project_code || r.project_system_code || r.project || '';
            const name = r.project_name || '';
            const id = r.project_id;
            if (name || code || id) {
              return (
                <Space direction="vertical" size={0}>
                  <span>{name || '-'}</span>
                  <span style={{ color: '#1677ff' }}>{code || id || '-'}</span>
                </Space>
              );
            }
            return '-';
          } },
          { title: '启用', dataIndex: 'is_active', render: (v: boolean) => {
            const yes = {
              background: '#f6ffed', border: '1px solid #b7eb8f', color: '#389e0d'
            } as React.CSSProperties;
            const no = {
              background: '#fff1f0', border: '1px solid #ffa39e', color: '#cf1322'
            } as React.CSSProperties;
            const style = v ? yes : no;
            return <span style={{ display:'inline-block', padding:'0 8px', lineHeight:'22px', borderRadius: 4, ...style }}>{v ? '是' : '否'}</span>;
          } },
          { title: (
              <span>
                已被引用
                <HelpHintButton style={{ padding: 0, marginLeft: 6, height: 'auto' }} onClick={() => {
                  Modal.info({
                    title: '关于“已被引用”',
                    icon: null,
                    centered: true,
                    okText: '知道了',
                    width: 680,
                    content: (
                      <div style={{ whiteSpace: 'pre-line', color: '#141414' }}>
{`含义：该指令模板已被实际指令或指令动作引用（用于历史记录与计费）。
限制：
· 禁止编辑：如需调整，请“生成新版本”后在新版本上编辑，并再次生成工单模板与路由；
· 禁止删除：仅可“禁用”（不再对新单可见），历史数据不受影响与变更。`}
                      </div>
                    )
                  });
                }}>说明</HelpHintButton>
              </span>
            ),
            key: 'locked',
            width: 110,
            render: (_: any, r: any) => {
              const locked = !!genStatusByTplId[Number(r.id)]?.locked;
              const yes = {
                background: '#fffbe6', border: '1px solid #ffe58f', color: '#ad6800'
              } as React.CSSProperties;
              const no = {
                background: '#e6f4ff', border: '1px solid #91caff', color: '#0958d9'
              } as React.CSSProperties;
              const style = locked ? yes : no;
              return <span style={{ display:'inline-block', padding:'0 8px', lineHeight:'22px', borderRadius: 4, ...style }}>{locked ? '是' : '否'}</span>;
            }
          },
          { title: '涉及操作类型', dataIndex: 'actions', width: 100, align: 'center' as const, render: (_: any, r: any) => {
            const acts = Array.isArray(r.actions) ? r.actions : [];
            const ids = new Set<number>();
            acts.forEach((a: any) => {
              const id = Number((a && (a.operation_type_id ?? a.operation_type)) as any);
              if (!Number.isNaN(id)) ids.add(id);
            });
            const count = ids.size;
            if (!count) return <span style={{ display: 'inline-block', lineHeight: '20px', minWidth: 16 }}>0</span>;
            return (
              <Button type="link" size="small" style={{ padding: 0, height: 'auto', lineHeight: '20px' }} onClick={async () => {
                try {
                  const d = await fetchTemplateDetail(Number(r.id));
                  const tpl = d || r || {};
                  const arr = Array.isArray(tpl.actions) ? tpl.actions : [];
                  const ordered = [...arr].sort((a:any,b:any)=>{
                    const sa = Number.isFinite(Number(a.stage)) ? Number(a.stage) : Number.POSITIVE_INFINITY;
                    const sb = Number.isFinite(Number(b.stage)) ? Number(b.stage) : Number.POSITIVE_INFINITY;
                    if (sa !== sb) return sa - sb;
                    const ga = (a.group_key || '~') as string;
                    const gb = (b.group_key || '~') as string;
                    if (ga !== gb) return ga.localeCompare(gb);
                    const oa = Number(a.sort_order || 0);
                    const ob = Number(b.sort_order || 0);
                    if (oa !== ob) return oa - ob;
                    return Number(a.id || 0) - Number(b.id || 0);
                  });
                  const items: Array<{ name: string; variant?: string; basis?: string; order?: number; stage?: number; group?: string }> = ordered.map((a:any) => {
                    const opId = Number(a.operation_type_id || a.operation_type);
                    const label = (!Number.isNaN(opId) && opIdToName.get(opId)) || (a.operation_type_name || `ID=${opId || '-'}`);
                    return {
                      name: label,
                      stage: (a.stage != null ? Number(a.stage) : undefined),
                      group: a.group_key || undefined,
                      variant: a.variant_key,
                      basis: a.basis_override,
                      order: Number(a.sort_order || 0),
                      opId: (!Number.isNaN(opId) ? opId : undefined),
                    };
                  });
                  // 规则口径只读映射
                  try {
                    const map = await fetchRuleBasisMap(tpl);
                    setRuleBasisByOpId(map);
                  } catch {}
                  setActionListTitle(`${tpl.name || '指令模板'} - 涉及操作类型`);
                  setActionListMode('actions');
                  setActionList(items);
                  setActionListOpen(true);
                } catch {
                  setActionList([]);
                  setActionListOpen(true);
                }
              }}>{count}</Button>
            );
          } },
          // 新增：问题列（显示问题数量并可点击查看详情）
          { title: '问题', key: 'questionnaire', align: 'center' as const, render: (_: any, r: any) => {
            try {
              const qn = r.questionnaire || {};
              const qs = Array.isArray(qn.questions) ? qn.questions : [];
              const count = qs.length;
              if (!count) return <span style={{ display: 'inline-block', lineHeight: '20px', minWidth: 16 }}>0</span>;
              return (
                <Button type="link" size="small" style={{ padding: 0, height: 'auto', lineHeight: '20px' }} onClick={async () => {
                  try {
                    const d = await fetchTemplateDetail(Number(r.id));
                    const tpl = d || r || {};
                    const q = (tpl.questionnaire || {});
                    const arr = Array.isArray(q.questions) ? q.questions : [];
                  const fmtDefault = (it:any): string | undefined => {
                    try {
                      const t = String(it?.type || '');
                      const def = it?.default;
                      if (def === undefined || def === null) return undefined;
                      if (t === 'boolean') return (def === true ? '是' : def === false ? '否' : undefined);
                      if (t === 'number') {
                        const n = Number(def);
                        return Number.isFinite(n) ? String(n) : undefined;
                      }
                      if (t === 'select') {
                        const s = String(def ?? '').trim();
                        return s || undefined;
                      }
                      if (t === 'multiselect') {
                        const arr = Array.isArray(def) ? def : [];
                        const vals = arr.map((x:any)=> String(x ?? '').trim()).filter(Boolean);
                        return vals.length ? vals.join(' / ') : undefined;
                      }
                      if (t === 'text' || t === 'textarea') {
                        const s = String(def ?? '');
                        return (s !== '' ? s : '(空)');
                      }
                    } catch {}
                    return undefined;
                  };
                  const items: Array<{ name: string; variant?: string; basis?: string; options?: string; tips?: string; placeholder?: string; defaultText?: string; order?: number; stage?: number; group?: string }> = arr.map((it:any, i:number) => {
                    const opts = (it && (it.type === 'select' || it.type === 'multiselect') && Array.isArray(it.options))
                      ? it.options.map((o:any) => (o?.label || o?.value || '')).filter(Boolean).join(' / ')
                      : undefined;
                    return {
                      name: `${it.label || it.key || `问题${i+1}`}`,
                      variant: it.required ? '必填' : undefined,
                      basis: it.type,
                      options: opts,
                      tips: it.tips,
                      placeholder: it.placeholder,
                      defaultText: fmtDefault(it),
                      order: undefined,
                      stage: undefined,
                      group: undefined,
                    };
                  });
                    setActionListTitle(`${tpl.name || '指令模板'} - 问题详情`);
                    setActionListMode('questions');
                    setActionList(items);
                    setActionListOpen(true);
                  } catch {
                    setActionList([]);
                    setActionListOpen(true);
                  }
                }}>{count}</Button>
              );
            } catch { return 0; }
          } },
          // 新增：附件列（显示附件规则总数并可点击查看）
          { title: '附件', key: 'attachments', align: 'center' as const, render: (_: any, r: any) => {
            try {
              const req = r.attachment_requirements || {};
              const numOrder = Array.isArray(req.order) ? req.order.length : 0;
              let numActions = 0;
              if (req.actions && typeof req.actions === 'object') {
                Object.keys(req.actions).forEach(k => {
                  const arr = Array.isArray(req.actions[k]) ? req.actions[k] : [];
                  numActions += arr.length;
                });
              }
              const total = numOrder + numActions;
              if (!total) return <span style={{ display: 'inline-block', lineHeight: '20px', minWidth: 16 }}>0</span>;
              return (
                <Button type="link" size="small" style={{ padding: 0, height: 'auto', lineHeight: '20px' }} onClick={async () => {
                  try {
                    const d = await fetchTemplateDetail(Number(r.id));
                    const tpl = d || r || {};
                    const req2 = tpl.attachment_requirements || {};
                    const items: Array<{ name: string; variant?: string; basis?: string; order?: number; stage?: number; group?: string; required?: boolean }>=[];
                    const pushNode = (scopeLabel:string, n:any) => {
                      const types = Array.isArray(n.types) ? n.types.join('  ') : '-';
                      const counts = `最小${n.min != null ? n.min : 0} ${(n.max != null ? `最大${n.max}` : '')}`.trim();
                      items.push({
                        name: `${scopeLabel}：${n.label || '未命名'}`,
                        variant: counts,
                        basis: types,
                        order: undefined,
                        stage: undefined,
                        group: undefined,
                        required: (n.min != null && Number(n.min) !== 0),
                      });
                    };
                    if (Array.isArray(req2.order)) req2.order.forEach((n:any)=> pushNode('客户端需提交', n));
                    if (req2.actions && typeof req2.actions === 'object') {
                      Object.keys(req2.actions).forEach((k:string) => {
                        const arr = Array.isArray(req2.actions[k]) ? req2.actions[k] : [];
                        const niceKey = (!k || k==='ALL') ? '所有动作(ALL)' : (k.startsWith('op:') ? k : k);
                        arr.forEach((n:any)=> pushNode(`完成动作需提交（${niceKey}）`, n));
                      });
                    }
                    setActionListTitle(`${tpl.name || '指令模板'} - 附件要求`);
                    setActionListMode('attachments');
                    setActionList(items);
                    setActionListOpen(true);
                  } catch {
                    setActionList([]);
                    setActionListOpen(true);
                  }
                }}>{total}</Button>
              );
            } catch { return 0; }
          } },
          // 新增：执行动作序列（查看步骤序列）
          { title: '执行动作序列', key: 'exec_pack', width: 100, align: 'center' as const, render: (_: any, r: any) => {
            const id = Number(r.id);
            const val = Number.isFinite(id) ? execPackCountByTplId[id] : undefined;
            return (
              <Button type="link" size="small" style={{ padding: 0, height: 'auto', lineHeight: '20px' }} onClick={() => openViewExecSteps(r)}>
                {val == null ? '-' : String(val)}
              </Button>
            );
          } },
          { title: '操作', key: 'actions', width: 420, render: (_: any, r: any) => {
            const ready = !!genStatusByTplId[Number(r.id)]?.ready;
            const locked = !!genStatusByTplId[Number(r.id)]?.locked;
            const genBtnStyle: React.CSSProperties = {
              background: (ready ? '#f6ffed' : '#e6f4ff'),
              color: (ready ? '#389e0d' : '#0958d9'),
              borderColor: (ready ? '#b7eb8f' : '#91caff'),
              whiteSpace: 'pre-line',
              height: 'auto',
              lineHeight: '18px',
              padding: '4px 8px'
            };
            const moreItems = [
              (STRONG_EDIT || !locked) ? {
                key: 'edit',
                label: (
                  <Tooltip title="仅用于系统开发阶段，上线后请使用“一键复制并编辑”创建新指令模板。">
                    <span>编辑</span>
                  </Tooltip>
                ),
                onClick: async () => {
                const d = await fetchTemplateDetail(Number(r.id));
                const tpl = d || r;
                setEditingTpl(tpl);
                form.setFieldsValue({ name: tpl.name, short_code: tpl.short_code, level: tpl.level, customer_id: tpl.customer_id, project_id: tpl.project_id, is_active: tpl.is_active, brief_description: tpl.brief_description });
                setInventoryConsumeCfg(readInventoryConsumeCfgFromMeta(tpl?.meta));
                try {
                  const req = tpl.attachment_requirements || {};
                  const rows: any[] = [];
                  // 计算稳定 rule_key（与 Create.tsx 展示规则一致）
                  if (Array.isArray(req.order)) req.order.forEach((n:any, i:number) => rows.push({ scope: 'order', label: n.label, types: n.types || [], min: n.min, max: n.max, tips: n.tips, template_ids: normalizeTemplateIds(n), printable: !!n.printable, sort_order: n.sort_order, show_if: n.show_if?.key ? { key: n.show_if.key, op: n.show_if.op || 'eq', value_text: showIfValueToText(n.show_if.value) } : undefined, __rule_key: `order:${i}` }));
                  if (req.actions && typeof req.actions === 'object') {
                    Object.keys(req.actions).forEach(k => {
                      const arr = Array.isArray(req.actions[k]) ? req.actions[k] : [];
                      arr.forEach((n:any, i:number) => rows.push({ scope: 'actions', action_key: k, label: n.label, types: n.types || [], min: n.min, max: n.max, tips: n.tips, template_ids: normalizeTemplateIds(n), printable: !!n.printable, sort_order: n.sort_order, show_if: n.show_if?.key ? { key: n.show_if.key, op: n.show_if.op || 'eq', value_text: showIfValueToText(n.show_if.value) } : undefined, __rule_key: `actions:${k}:${i}` }));
                    });
                  }
                  rows.forEach((x:any, i:number) => { if (!Number.isFinite(Number(x?.sort_order))) x.sort_order = i + 1; });
                  rows.sort((a:any, b:any) => {
                    const sa = Number(a?.sort_order || 0);
                    const sb = Number(b?.sort_order || 0);
                    if (sa !== sb) return sa - sb;
                    return 0;
                  });
                  setAttachmentRows(rows);
                  // 加载角色目录与现有绑定
                  try { await loadAttachmentRoles(); } catch {}
                  try { await loadAttachmentBindings(Number(tpl.id)); } catch {}
                } catch { setAttachmentRows([]); }
                try {
                  const qn = tpl.questionnaire || {};
                  const qs = Array.isArray(qn.questions) ? qn.questions : [];
                  const mapped = qs.map((q:any) => ({
                    key: q.key,
                    label: q.label,
                    type: q.type,
                    required: !!q.required,
                    tips: q.tips,
                    placeholder: q.placeholder,
                    options: Array.isArray(q.options) ? q.options.map((o:any)=>({ value: String(o.value), label: o.label || String(o.value) })) : [],
                    options_source: q.options_source || q.optionsSource || undefined,
                    min: q.min, max: q.max, step: q.step, unit: q.unit,
                    max_length: q.max_length,
                    default: (q as any)?.default,
                    ...(normalizeQuestionShowIfFromPayload(q?.show_if)),
                    required_if: q?.required_if?.key ? {
                      key: q.required_if.key,
                      op: q.required_if.op || 'eq',
                      value_text: showIfValueToText(q.required_if.value),
                    } : undefined,
                  }));
                  setQRows(mapped);
                } catch { setQRows([]); }
                try {
                  const acts = Array.isArray(tpl.actions) ? tpl.actions : [];
                  const mapped = acts.map((a:any) => mapActionFromTemplate(a));
                  setTypeActions(mapped);
                } catch { setTypeActions([]); }
                try {
                  const br = tpl.baseline_requirements || {};
                  const items = Array.isArray(br.items) ? br.items : [];
                  const mapped = items.map((it:any) => ({
                    code: it.code || '',
                    name: it.name || '',
                    required: !!it.required,
                    formats: Array.isArray(it.formats) ? it.formats : [],
                    min_files: it.min_files,
                    max_files: it.max_files,
                    bind: it.bind || {},
                    schema_hint_json: it.schema_hint ? JSON.stringify(it.schema_hint, null, 2) : ''
                  }));
                  setBaselineItems(mapped);
                } catch { setBaselineItems([]); }
                setVisible(true);
                }
              } : null,
              {
                key: 'cloneAndEdit',
                label: '一键复制并编辑',
                onClick: async () => {
                  try {
                    const resp = await request.post(`/api/v1/instruction/templates/${r.id}/clone-version/`, {});
                    const newId = Number(resp?.data?.id || resp?.data?.template?.id);
                    if (!Number.isFinite(newId)) { message.error('复制失败'); return; }
                    const d = await fetchTemplateDetail(newId);
                    const tpl = d || (resp?.data?.template) || {};
                    setEditingTpl(tpl);
                    form.setFieldsValue({ name: tpl.name, short_code: tpl.short_code, level: tpl.level, customer_id: tpl.customer_id, project_id: tpl.project_id, is_active: tpl.is_active, brief_description: tpl.brief_description });
                    setInventoryConsumeCfg(readInventoryConsumeCfgFromMeta(tpl?.meta));
                    try {
                      const req = tpl.attachment_requirements || {};
                      const rows: any[] = [];
                      if (Array.isArray(req.order)) req.order.forEach((n:any) => rows.push({ scope: 'order', label: n.label, types: n.types || [], min: n.min, max: n.max, tips: n.tips, template_ids: normalizeTemplateIds(n), printable: !!n.printable, sort_order: n.sort_order, show_if: n.show_if?.key ? { key: n.show_if.key, op: n.show_if.op || 'eq', value_text: showIfValueToText(n.show_if.value) } : undefined }));
                      if (req.actions && typeof req.actions === 'object') {
                        Object.keys(req.actions).forEach(k => {
                          const arr = Array.isArray(req.actions[k]) ? req.actions[k] : [];
                          arr.forEach((n:any) => rows.push({ scope: 'actions', action_key: k, label: n.label, types: n.types || [], min: n.min, max: n.max, tips: n.tips, template_ids: normalizeTemplateIds(n), printable: !!n.printable, sort_order: n.sort_order, show_if: n.show_if?.key ? { key: n.show_if.key, op: n.show_if.op || 'eq', value_text: showIfValueToText(n.show_if.value) } : undefined }));
                        });
                      }
                      rows.forEach((x:any, i:number) => { if (!Number.isFinite(Number(x?.sort_order))) x.sort_order = i + 1; });
                      rows.sort((a:any, b:any) => {
                        const sa = Number(a?.sort_order || 0);
                        const sb = Number(b?.sort_order || 0);
                        if (sa !== sb) return sa - sb;
                        return 0;
                      });
                      setAttachmentRows(rows);
                    } catch { setAttachmentRows([]); }
                    try {
                      const qn = tpl.questionnaire || {};
                      const qs = Array.isArray(qn.questions) ? qn.questions : [];
                      const mapped = qs.map((q:any) => ({
                        key: q.key, label: q.label, type: q.type, required: !!q.required, tips: q.tips, placeholder: q.placeholder,
                        options: Array.isArray(q.options) ? q.options.map((o:any)=>({ value: String(o.value), label: o.label || String(o.value) })) : [],
                        options_source: q.options_source || q.optionsSource || undefined,
                        min: q.min, max: q.max, step: q.step, unit: q.unit, max_length: q.max_length,
                        default: (q as any)?.default,
                        ...(normalizeQuestionShowIfFromPayload(q?.show_if)),
                        required_if: q?.required_if?.key ? {
                          key: q.required_if.key,
                          op: q.required_if.op || 'eq',
                          value_text: showIfValueToText(q.required_if.value),
                        } : undefined,
                      }));
                      setQRows(mapped);
                    } catch { setQRows([]); }
                    try {
                      const acts = Array.isArray(tpl.actions) ? tpl.actions : [];
                      const mapped = acts.map((a:any) => mapActionFromTemplate(a));
                      setTypeActions(mapped);
                    } catch { setTypeActions([]); }
                    try {
                      const br = tpl.baseline_requirements || {};
                      const items = Array.isArray(br.items) ? br.items : [];
                      const mapped = items.map((it:any) => ({
                        code: it.code || '',
                        name: it.name || '',
                        required: !!it.required,
                        formats: Array.isArray(it.formats) ? it.formats : [],
                        min_files: it.min_files,
                        max_files: it.max_files,
                        bind: it.bind || {},
                        schema_hint_json: it.schema_hint ? JSON.stringify(it.schema_hint, null, 2) : ''
                      }));
                      setBaselineItems(mapped);
                    } catch { setBaselineItems([]); }
                    setVisible(true);
                    message.success('已复制为新指令模板并打开编辑');
                  } catch (e:any) {
                    message.error(e?.response?.data?.detail || '复制失败');
                  }
                }
              },
              !locked ? {
                key: 'delete',
                danger: true,
                label: '删除',
                onClick: () => openDeleteConfirm(r)
              } : null,
              r?.is_active ? {
                key: 'deactivate',
                danger: true,
                label: '禁用',
                onClick: async () => {
                  try {
                    await request.post(`/api/v1/instruction/templates/${r.id}/archive/`, {});
                    message.success('已禁用该指令模板');
                    fetchData();
                  } catch (e:any) {
                    message.error(e?.response?.data?.detail || '禁用失败');
                  }
                }
              } : {
                key: 'activate',
                label: '启用',
                onClick: async () => {
                  try {
                    await request.post(`/api/v1/instruction/templates/${r.id}/activate/`, {});
                    message.success('已启用该指令模板');
                    fetchData();
                  } catch (e:any) {
                    message.error(e?.response?.data?.detail || '启用失败');
                  }
                }
              },
            ].filter(Boolean) as any[];
            return (
              <Space>
                <Button size="small" style={{ borderColor: '#000000', color: '#000000', whiteSpace: 'pre-line', height: 'auto', lineHeight: '18px', padding: '4px 8px' }} onClick={async () => { const d = await fetchTemplateDetail(Number(r.id)); setDetailRecord(d || r); setDetailOpen(true); }}>详情</Button>
                <Button
                  size="small"
                  style={{ background: '#e6f4ff', borderColor: '#91caff', color: '#0958d9', height: 'auto', padding: '4px 6px', width: 96 }}
                  onClick={() => openExecPack(r)}
                >
                  <span style={{ display: 'inline-flex', flexDirection: 'column', lineHeight: '16px', whiteSpace: 'normal', textAlign: 'center' }}>
                    <span>配置操作</span>
                    <span>步骤序列</span>
                  </span>
                </Button>
                <Button size="small" style={genBtnStyle} onClick={async () => {
                  if (ready) { openViewOwned(r); return; }
                  setAutoGenTpl(r);
                  setAutoGenPreview(null);
                  setAutoGenOpen(true);
                  setAutoGenLoading(true);
                  try {
                    const resp = await request.post(`/api/v1/instruction/templates/${r.id}/auto-generate/`, { dry_run: true }, { params: { dry_run: 1 } });
                    const payload = resp?.data || {};
                    setAutoGenPreview({
                      created_templates: Array.isArray(payload.created_templates) ? payload.created_templates : [],
                      updated_templates: Array.isArray(payload.updated_templates) ? payload.updated_templates : [],
                      route: payload.route,
                      warnings: Array.isArray(payload.warnings) ? payload.warnings : [],
                    });
                    try { if (payload?.route && (payload.route.updated || payload.route.created)) setGenStatusByTplId(prev => ({ ...prev, [Number(r.id)]: { ready: true } })); } catch {}
                  } catch (e:any) {
                    message.error(e?.response?.data?.detail || '预览失败');
                  } finally { setAutoGenLoading(false); }
                }}>
                  {ready ? (
                    <>
                      <span style={{ display: 'inline-block' }}>已生成工单模板</span>
                      <br />
                      <span style={{ display: 'inline-block' }}>与路由</span>
                    </>
                  ) : (
                    <>
                      <span style={{ display: 'inline-block' }}>生成工单模板</span>
                      <br />
                      <span style={{ display: 'inline-block' }}>与路由</span>
                    </>
                  )}
                </Button>
                <Dropdown menu={{ items: moreItems }}>
                  <Button size="small">更多</Button>
                </Dropdown>
            </Space>
            );
          } }
        ]} />
      </Card>

      <Modal
        open={visible}
        onOk={handleOk}
        onCancel={() => { setVisible(false); setEditingTpl(null); }}
        title={editingTpl ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span>编辑指令模板 -</span>
            <span style={{ fontWeight: 700, color: '#1677ff' }}>{(editingTpl as any)?.system_code || '-'}</span>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, color: '#faad14', fontWeight: 600 }}>
              <InfoCircleOutlined />
              <span>仅用于系统开发阶段，上线后请使用“一键复制并编辑”创建新指令模板。</span>
            </span>
          </div>
        ) : '新建指令模板'}
        okText={editingTpl ? '保存' : '创建指令模板'}
        cancelText="取消"
        width={1280}
      >
        <Form form={form} layout="vertical" initialValues={{ is_active: true, level: 'system' }}>
          <Space size={16} style={{ display: 'flex' }}>
            <Form.Item name="name" label="名称" rules={[{ required: true }]}>
              <Input style={{ width: 260 }} />
            </Form.Item>
            <Form.Item name="short_code" label="短码">
              <Input style={{ width: 200 }} placeholder="如：IT_SYS_19" />
            </Form.Item>
            <Form.Item name="level" label="层级" rules={[{ required: true }]}> 
              <Select style={{ width: 160 }} disabled={!!editingTpl} options={[{ value: 'system', label: '系统级' }, { value: 'customer', label: '客户级' }, { value: 'project', label: '项目级' }]} onChange={() => {
                form.setFieldsValue({ customer_id: undefined, project_id: undefined });
                setProjectOptions([]);
                setTypeActions([]);
                setExtraScopedOpIds([]);
                loadEffectiveOps();
              }} />
            </Form.Item>
            <Form.Item name="is_active" label="启用" valuePropName="checked" initialValue={true} style={{ marginLeft: 16 }}>
              <Switch />
            </Form.Item>
            <Form.Item
              name="default_workorder_template"
              label={(
                <div style={{ display:'flex', alignItems:'center', gap: 8 }}>
                  <span>默认工单模板（1:1）</span>
                  <Button type="link" size="small" onClick={() => Modal.info({
                    title: '默认工单模板（1:1） - 说明',
                    icon: null,
                    centered: true,
                    okText: '知道了',
                    width: 760,
                    modalRender: (node) => (<div style={{ background:'#ffffff', color:'#141414', borderRadius:8 }}>{node}</div>),
                    content: (
                      <div style={{ whiteSpace: 'pre-line', color: '#141414' }}>
{`作用与优先级：
· 当“审核计划(plan_override)”和“路由(WorkOrderRoute)”都未命中时，用作兜底方案，直接生成一张工单；优先级：计划覆盖 > 路由 > 默认工单模板。

新建指令模板时为什么可选：
· 兼容“先有工单模板库，再绑定指令模板”的做法；如果团队已有通用/原子工单模板，可在新建阶段直接指向，保证开单不被配置缺失阻断；也可留空，后续再绑定或依赖路由/计划。

编辑指令模板时的建议用法：
· 若采用 1:1 专属工单模板（通过“自动生成工单模板与路由”产出或人工创建），这里可选择该专属模板作为兜底；
· 若采用共享工单模板（多个指令模板复用），这里可选择通用模板，仍建议优先通过“路由/计划”表达复杂流程。

可选的约束思路（后续优化方向）：
· 仅展示“归属于该指令模板”的专属工单模板，或默认高亮命名约定匹配项（如 <模板编码>_<操作类型>），降低误选风险。`}
                      </div>
                    )
                  })}>查看说明</Button>
                </div>
              )}
              tooltip="默认使用该工单模板生成工单；若在审核设置了计划或命中路由，则以其为准"
            >
              <Select
                style={{ width: 320 }}
                showSearch
                allowClear
                placeholder="选择默认工单模板"
                loading={wotplLoading}
                options={(wotplOptions || []).map((opt:any) => {
                  const code = String(opt?.code || '').toUpperCase();
                  const owner = String((editingTpl as any)?.system_code || '').toUpperCase();
                  const owned = owner && code.startsWith(owner + '_');
                  const base = `${opt?.name || ''} · ${opt?.code || ''}`.trim();
                  return {
                    ...opt,
                    label: (
                      <div style={{ display:'flex', justifyContent:'space-between', width:'100%' }}>
                        <span style={owned ? { fontWeight: 600 } : undefined}>{base}</span>
                        {owned ? <Tag color="green">专属</Tag> : null}
                      </div>
                    )
                  };
                })}
                onFocus={() => loadWorkOrderTemplates()}
                filterOption={(input, option:any) => {
                  const s = String(option?.searchLabel || option?.name || option?.code || '').toLowerCase();
                  return s.includes(String(input).toLowerCase());
                }}
              />
            </Form.Item>
          </Space>
        {/* 作用域提示：客户级/项目级需先配置收费系统 */}
        <Form.Item shouldUpdate noStyle>
          {() => {
            const lvl = form.getFieldValue('level');
            if (lvl === 'customer' || lvl === 'project') {
              const text = lvl === 'customer'
                ? '提示：创建客户级指令模板前，需要先在收费系统中为该客户创建对应的客户级操作类型与计费规则，否则保存将失败。'
                : '提示：创建项目级指令模板前，需要先在收费系统中为该项目创建对应的项目级操作类型与计费规则，否则保存将失败。';
              return (
                <div style={{ margin: '8px 0 0 0', padding: '6px 10px', background: '#fffbe6', border: '1px solid #ffe58f', color: '#ad6800', borderRadius: 4 }}>
                  {text}
                </div>
              );
            }
            return null;
          }}
        </Form.Item>
          <Space size={16} style={{ display: 'flex' }}>
            {/* 客户/项目在客户级/项目级时可选 */}
            <Form.Item shouldUpdate noStyle>
              {() => {
                const lvl = form.getFieldValue('level');
                if (lvl === 'customer') {
                  return (
                    <>
                      <Form.Item name="customer_id" label="客户" rules={[{ required: true }]}>
                        <Select style={{ width: 260 }} showSearch allowClear loading={customerLoading}
                                onFocus={ensureCustomers}
                                options={customerOptions}
                                optionFilterProp="label"
                                onChange={() => { form.setFieldsValue({ project_id: undefined }); setProjectOptions([]); setTypeActions([]); setExtraScopedOpIds([]); loadEffectiveOps(); }}
                        />
                      </Form.Item>
                    </>
                  );
                }
                if (lvl === 'project') {
                  return (
                    <>
                      <Form.Item name="customer_id" label="客户" rules={[{ required: true }]}>
                        <Select style={{ width: 260 }} showSearch allowClear loading={customerLoading}
                                onFocus={ensureCustomers}
                                options={customerOptions}
                                optionFilterProp="label"
                                onChange={() => { form.setFieldsValue({ project_id: undefined }); setProjectOptions([]); setTypeActions([]); }}
                        />
                      </Form.Item>
                      <Form.Item name="project_id" label="项目" rules={[{ required: true }]}>
                        <Select style={{ width: 300 }} showSearch allowClear loading={projectLoading}
                                disabled={!form.getFieldValue('customer_id')}
                                onFocus={() => searchProjects('')}
                                onSearch={(val) => searchProjects(val)}
                                filterOption={false}
                                options={projectOptions}
                                onChange={() => { setTypeActions([]); loadEffectiveOps(); }}
                        />
                      </Form.Item>
                    </>
                  );
                }
                return null;
              }}
            </Form.Item>
          </Space>
        <Form.Item name="brief_description" label="简要说明(将显示在前端客户页面)">
            <Input.TextArea rows={3} placeholder="用于解释模板的基本要求、操作顺序提示等" />
          </Form.Item>
        </Form>
        <div style={{ marginTop: 10, marginBottom: 8, fontWeight: 600, display: 'flex', alignItems: 'center' }}>
          <Space size={8} align="center">
            <span>库存终态结算配置（终审提交）</span>
            <Button
              size="small"
              type="default"
              style={{ paddingInline: 8, color: '#1677ff', borderColor: '#91caff' }}
              onClick={() => {
                Modal.info({
                  title: '库存终态结算配置 - 说明',
                  icon: null,
                  centered: true,
                  okText: '知道了',
                  width: 860,
                  modalRender: (node) => (<div style={{ background:'#ffffff', color:'#141414', borderRadius:8 }}>{node}</div>),
                  content: (
                    <div style={{ whiteSpace: 'pre-line', color: '#141414', lineHeight: 1.8 }}>
{`用途：
· 配置“首次终审提交（operation_done -> completed）”时，库存占用应如何结算。
· 若不启用自定义，则使用系统默认策略（按模板族自动选择）。

系统默认策略（未自定义或条件不命中时）：
· 换标+装箱：transfer_to_business_hold
· 一件代发：consume_reserved
· 常规出库：consume_reserved
· 分拣清点：release_reserved

字段说明：
1) 启用自定义结算策略（开关）
   · 关闭：使用系统默认策略。
   · 开启：使用本区域配置的“结算模式 + 适用条件”。

2) 结算模式（单选）
   · consume_reserved：将执行期占用转为消耗。
   · release_reserved：释放执行期占用，不做消耗。
   · transfer_to_business_hold：执行期占用转为业务留存占用（供后续业务继续消费）。

3) 常规出库IOC消耗可用占用类型（多选）
   · 作用于“常规出库阶段 IOC -> IU 消耗”链路。
   · execution：允许消费执行期占用。
   · business_hold：允许消费业务留存占用。
   · 不配置时默认：execution + business_hold。

4) 适用：运单来源（多选）
   · 当前指令的 waybill_source 命中时，自定义结算策略才生效。

5) 适用：运单上传模式（包含）（多选）
   · 当前指令 waybill_submit_mode 必须在该列表中才生效。

6) 适用：运单上传模式（排除）（多选）
   · 当前指令 waybill_submit_mode 若在该列表中，则不生效。

7) 适用：是否需打包（多选）
   · 当前指令 is_pack_required 命中时才生效。

命中规则（很重要）：
· 以上“适用条件”是 AND 关系（并且关系）；
· 只要配置了某项条件，就必须满足；
· 任一条件不满足时，自动回退系统默认策略。`}
                    </div>
                  )
                });
              }}
            >
              说明
            </Button>
            <Tag color={inventoryConsumeCfg.enabled ? 'green' : 'default'}>
              {inventoryConsumeCfg.enabled ? '已自定义' : '默认'}
            </Tag>
            {inventoryConsumeCfg.enabled ? (
              <Tag color="blue">{inventoryFinalizeModeLabel(inventoryConsumeCfg.mode)}</Tag>
            ) : null}
          </Space>
        </div>
        <div style={{ border: '1px solid #d9f7be', background: '#f6ffed', borderRadius: 6, padding: 10, marginBottom: 8 }}>
          <Space direction="vertical" size={8} style={{ width: '100%' }}>
            <div style={{ color: '#389e0d' }}>
              说明：仅在“首次终审提交（operation_done → completed）”时触发库存终态结算，后续修改终审结果不会重复执行；未自定义时默认使用 release_reserved（释放执行期占用）。
            </div>
            <Space>
              <span>启用自定义结算策略</span>
              <Switch
                checked={inventoryConsumeCfg.enabled}
                onChange={(v) => setInventoryConsumeCfg(prev => ({ ...prev, enabled: !!v }))}
              />
            </Space>
            <Select
              allowClear={false}
              disabled={!inventoryConsumeCfg.enabled}
              style={{ width: 420 }}
              placeholder="结算模式"
              options={INVENTORY_FINALIZE_MODE_OPTIONS}
              value={inventoryConsumeCfg.mode}
              onChange={(v) => {
                const modeRaw = String(v || '').trim();
                const mode = (modeRaw === 'release_reserved' || modeRaw === 'transfer_to_business_hold' || modeRaw === 'consume_reserved')
                  ? modeRaw
                  : 'consume_reserved';
                setInventoryConsumeCfg(prev => ({ ...prev, mode: mode as any }));
              }}
            />
            <Select
              mode="multiple"
              allowClear
              style={{ width: 560 }}
              placeholder="常规出库IOC消耗可用占用类型"
              options={IOC_CONSUME_RESERVATION_KIND_OPTIONS}
              value={inventoryConsumeCfg.ioc_consume_reservation_kinds}
              onChange={(vals) => {
                const list = Array.from(new Set(
                  (Array.isArray(vals) ? vals : [])
                    .map((x: any) => String(x || '').trim().toLowerCase())
                    .filter((x: string) => x === 'execution' || x === 'business_hold')
                ));
                setInventoryConsumeCfg(prev => ({ ...prev, ioc_consume_reservation_kinds: list }));
              }}
            />
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
              <Select
                mode="multiple"
                allowClear
                disabled={!inventoryConsumeCfg.enabled}
                style={{ width: 280 }}
                placeholder="适用：运单来源"
                options={WAYBILL_SOURCE_OPTIONS}
                value={inventoryConsumeCfg.waybill_source_in}
                onChange={(vals) => setInventoryConsumeCfg(prev => ({ ...prev, waybill_source_in: (Array.isArray(vals) ? vals : []).map((x: any) => String(x || '').trim()).filter(Boolean) }))}
              />
              <Select
                mode="multiple"
                allowClear
                disabled={!inventoryConsumeCfg.enabled}
                style={{ width: 280 }}
                placeholder="适用：运单上传模式（包含）"
                options={WAYBILL_MODE_OPTIONS}
                value={inventoryConsumeCfg.waybill_mode_in}
                onChange={(vals) => setInventoryConsumeCfg(prev => ({ ...prev, waybill_mode_in: (Array.isArray(vals) ? vals : []).map((x: any) => String(x || '').trim()).filter(Boolean) }))}
              />
              <Select
                mode="multiple"
                allowClear
                disabled={!inventoryConsumeCfg.enabled}
                style={{ width: 280 }}
                placeholder="适用：运单上传模式（排除）"
                options={WAYBILL_MODE_OPTIONS}
                value={inventoryConsumeCfg.waybill_mode_not_in}
                onChange={(vals) => setInventoryConsumeCfg(prev => ({ ...prev, waybill_mode_not_in: (Array.isArray(vals) ? vals : []).map((x: any) => String(x || '').trim()).filter(Boolean) }))}
              />
              <Select
                mode="multiple"
                allowClear
                disabled={!inventoryConsumeCfg.enabled}
                style={{ width: 280 }}
                placeholder="适用：是否需打包"
                options={PACK_REQUIRED_OPTIONS}
                value={inventoryConsumeCfg.is_pack_required_in}
                onChange={(vals) => setInventoryConsumeCfg(prev => ({ ...prev, is_pack_required_in: (Array.isArray(vals) ? vals : []).filter((x: any) => typeof x === 'boolean') }))}
              />
            </div>
          </Space>
        </div>
        {/* 基准数据规范（模板级） */}
        <div style={{ marginTop: 16, marginBottom: 8, fontWeight: 600, display: 'flex', alignItems: 'center' }}>
          <Space size={8} align="center">
            <span>基准数据规范(仓库操作基线文件)</span>
            <HelpHintButton
              onClick={() => {
              Modal.info({
                title: '基准数据规范 - 说明',
                icon: null,
                centered: true,
                okText: '知道了',
                width: 760,
                modalRender: (node) => (<div style={{ background:'#ffffff', color:'#141414', borderRadius:8 }}>{node}</div>),
                content: (
                  <div style={{ whiteSpace: 'pre-line', color: '#141414' }}>
{`用途：
· 描述执行环节所需的“操作基线”数据（例如：旧→新条码映射表、装箱清单、检验规则等）。
· 审核端可基于此规范生成草稿并上传文件；执行页会按阶段/分组/步骤读取“就绪”的基线。

字段说明（每一项）：
· code：唯一键（如 BARCODE_MAPPING），建议大写下划线命名。
· name：显示名称（如 新旧条码对应表）。
· required：是否必需（审核前务必补齐）。
· formats：允许文件格式（csv/xlsx/json/pdf…），可多选。
· min_files / max_files：最少/最多文件数（min>=1 表示必传）。
· bind：基线的使用位置，支持：
  - stage：阶段号（数字）
  - group_key：分组键（字符串）
  - steps：步骤键数组（如 ["RELABEL"]）
· schema_hint：结构提示（JSON），例如：
  { "columns": ["old_barcode","new_barcode"] }
  或更复杂的列规则/正则/示例等（仅作提示与可选的轻校验）。`}
                  <div style={{ height: 8 }} />
                  <div style={{ whiteSpace: 'pre-line', color: '#141414' }}>
{`绑定说明（与“动作步骤序列”的关系）：
· bind 用于把基线注入到执行页的具体步骤；当步骤满足以下任一条件即命中并注入：
  - steps：数组内包含该步骤的“步骤键”（如 'RELABEL'）
  - stage：等于该步骤所在“阶段”号
  - group_key：等于该步骤所在“分组”键
· 后端在 stage-ui 中按上述规则筛选出匹配的 code，并仅返回 state=ready 的基线到该步骤的 data.baselines；前端据此显示/使用。

建议：
· 至少填写 steps / stage / group_key 中的一项，否则不会注入任何步骤。
· 优先使用 steps 精确绑定，stage/group_key 作为补充（两者可能随模板结构调整而变更）。
· 一个 code 最好只绑定到一个使用位置；如需复用，请在 code 或 name 上区分（例如 BARCODE_MAPPING_RELABEL / BARCODE_MAPPING_QC）。

示例：
· 换标映射：code=BARCODE_MAPPING，bind: { "steps": ["RELABEL"] }
· 打印拣货清单：bind: { "stage": 1, "group_key": "PICK" }`}
                  </div>
                  </div>
                )
              });
            }}>说明</HelpHintButton>
            <Button
              type="primary"
              style={{ background: '#f6ffed', borderColor: '#b7eb8f', color: '#389e0d' }}
              onClick={() => setBaselineItems(prev => [...prev, { code: '', name: '', required: true, formats: [], bind: {}, schema_hint_json: '' }])}
            >
              新增基准项
            </Button>
          </Space>
        </div>
        <Space direction="vertical" style={{ width: '100%' }}>
          {(baselineItems || []).map((it, idx) => (
            <div key={idx} style={{ display: 'flex', flexWrap: 'wrap', gap: 8, border: '1px solid #f0f0f0', borderRadius: 6, padding: 8 }}>
              <Input style={{ width: 180 }} placeholder="code（如 BARCODE_MAPPING）" value={it.code} onChange={(e)=> setBaselineItems(prev => prev.map((x,i)=> i===idx ? ({ ...x, code: e.target.value }) : x))} />
              <Input style={{ width: 200 }} placeholder="名称（如 新旧条码对应表）" value={it.name} onChange={(e)=> setBaselineItems(prev => prev.map((x,i)=> i===idx ? ({ ...x, name: e.target.value }) : x))} />
              <Select
                mode="multiple"
                style={{ width: 220 }}
                placeholder="允许格式（可多选）"
                value={it.formats || []}
                options={[
                  'csv','xlsx','xls','json','pdf','png','jpg','jpeg','gif','zip','rar','7z','doc','docx'
                ].map(ext => ({ value: ext, label: ext.toUpperCase() }))}
                showSearch
                allowClear
                filterOption={(input, option:any) => {
                  const t = String(option?.label || option?.value || '').toLowerCase();
                  return t.includes(String(input || '').toLowerCase());
                }}
                onChange={(vals)=> setBaselineItems(prev => prev.map((x,i)=> i===idx ? ({ ...x, formats: vals as string[] }) : x))}
              />
              <Space>
                <span>必需</span>
                <Switch checked={!!it.required} onChange={(v)=> setBaselineItems(prev => prev.map((x,i)=> i===idx ? ({ ...x, required: v }) : x))} />
              </Space>
              <InputNumber style={{ width: 120 }} placeholder="最少文件数" min={0} value={it.min_files as any} onChange={(v)=> setBaselineItems(prev => prev.map((x,i)=> i===idx ? ({ ...x, min_files: Number(v ?? 0) }) : x))} />
              <InputNumber style={{ width: 120 }} placeholder="最多文件数" min={0} value={it.max_files as any} onChange={(v)=> setBaselineItems(prev => prev.map((x,i)=> i===idx ? ({ ...x, max_files: Number(v ?? 0) }) : x))} />
              <InputNumber style={{ width: 120 }} placeholder="绑定阶段(stage)" min={1} value={it.bind?.stage as any} onChange={(v)=> setBaselineItems(prev => prev.map((x,i)=> i===idx ? ({ ...x, bind: { ...(x.bind||{}), stage: Number(v || 1) } }) : x))} />
              <Input style={{ width: 160 }} placeholder="绑定分组(group_key)" value={it.bind?.group_key || ''} onChange={(e)=> setBaselineItems(prev => prev.map((x,i)=> i===idx ? ({ ...x, bind: { ...(x.bind||{}), group_key: e.target.value } }) : x))} />
              <Select mode="tags" style={{ width: 220 }} placeholder="绑定步骤（如 RELABEL）" value={it.bind?.steps || []} onChange={(vals)=> setBaselineItems(prev => prev.map((x,i)=> i===idx ? ({ ...x, bind: { ...(x.bind||{}), steps: vals as string[] } }) : x))} />
              <Space>
                <Button size="small" onClick={() => {
                  setBaselineSchemaIdx(idx);
                  setBaselineSchemaText(String(it.schema_hint_json || (it as any)?.schema_hint || '{}') || '{}');
                  setBaselineSchemaOpen(true);
                }}>编辑schema</Button>
                <Button size="small" danger onClick={()=> setBaselineItems(prev => prev.filter((_, i)=> i!==idx))}>删除</Button>
              </Space>
            </div>
          ))}
          {(baselineItems || []).length === 0 ? <div style={{ color:'#999' }}>未配置基准数据规范</div> : null}
        </Space>
        <div style={{ marginTop: 12, marginBottom: 8, fontWeight: 600, display: 'flex', alignItems: 'center' }}>
          <Space size={8} align="center">
            <span>计费动作组合（虚拟工单）</span>
            <HelpHintButton onClick={() => setShowActionPlanHelp(true)}>说明</HelpHintButton>
            <Button
              type="primary"
              style={{ background: '#f6ffed', borderColor: '#b7eb8f', color: '#389e0d' }}
              onClick={addTypeAction}
            >
              新增计费项（操作类型）
            </Button>
          </Space>
        </div>
        <div style={{ margin: '0 0 8px 0', padding: '6px 10px', background: '#fffbe6', border: '1px solid #ffe58f', color: '#ad6800', borderRadius: 4 }}>
          说明：此处的“动作”仅用于计费轨，生成“工单模板与路由”以及后续的虚拟工单（计费行）。仓库员工的实际执行动作序列在“执行动作序列”中维护，与此处无直接对应。
        </div>
        <Space direction="vertical" size={8} style={{ width: '100%' }}>
          {typeActions.map((ta, idx) => (
            <Space key={idx} style={{ display: 'flex', flexWrap: 'nowrap' }} size={8}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <Select
                  placeholder="选择操作类型（计费系统）"
                  style={{ width: 340 }}
                  value={ta.operation_type_id}
                  onChange={(v) => { setTypeActions(prev => prev.map((x, i) => i===idx ? { ...x, operation_type_id: v, variant_key: undefined } : x)); loadVariantKeys(v); }}
                  notFoundContent={<div style={{ padding: 8, color: '#888' }}>暂无操作类型</div>}
                  showSearch
                  filterOption={(input, option:any) => {
                    if (!option || option.disabled) return false;
                    const text = String(option.label || '').toLowerCase();
                    const q = String(input || '').toLowerCase();
                    return text.includes(q);
                  }}
                  options={(() => {
                    const selectedIds = new Set(
                      (typeActions || [])
                        .map((x:any, i:number) => (i === idx ? undefined : Number(x?.operation_type_id)))
                        .filter((n:any) => Number.isFinite(n))
                    );
                    const hasVariants = (id: number) => {
                      try {
                        const opts = variantOptionsByOp[id] || [];
                        if (Array.isArray(opts) && opts.length > 0) return true;
                        return (typeActions || []).some((x:any) => Number(x?.operation_type_id) === id && String(x?.variant_key || '').trim());
                      } catch { return false; }
                    };
                    const makeOpt = (a:any) => {
                      const id = Number(a?.id);
                      const used = Number.isFinite(id) && selectedIds.has(id);
                      const canRepeat = Number.isFinite(id) && hasVariants(id);
                      const disabled = used && !canRepeat;
                      return {
                        value: a.id,
                        label: disabled ? `${a.name} (${a.system_code}) · 已选择` : `${a.name} (${a.system_code})`,
                        disabled,
                      };
                    };
                    const sys = (opTypeGroups.sys || []).map(makeOpt);
                    const scoped = (opTypeGroups.scoped || []).map(makeOpt);
                    if (scoped.length > 0) {
                      return [
                        ...sys,
                        { value: '__DIVIDER__', label: '—— 专属（所选客户/项目） ——', disabled: true },
                        ...scoped,
                      ];
                    }
                    return sys;
                  })()}
                />
                {((opTypeGroups.sys || []).length + (opTypeGroups.scoped || []).length) === 0 && (
                  <Button size="small" type="link" onClick={() => {
                    // 跳到收费系统-操作类型维护页（若无该路由，请替换为实际路径）
                    window.open('/dashboard/admin/billing/system?tab=operation-types', '_blank');
                  }}>去创建</Button>
                )}
              </div>
              {(() => {
                const opts = ta.operation_type_id ? (variantOptionsByOp[ta.operation_type_id] || []) : [];
                const usedVariantKeys = (() => {
                  try {
                    if (!ta.operation_type_id) return new Set<string>();
                    return new Set(
                      (typeActions || [])
                        .map((x:any, i:number) => (i === idx ? undefined : x))
                        .filter((x:any) => Number(x?.operation_type_id) === Number(ta.operation_type_id))
                        .map((x:any) => String(x?.variant_key || '').trim())
                        .filter((k:any) => k)
                    );
                  } catch { return new Set<string>(); }
                })();
                if (opts.length > 0) {
                  return (
                    <div style={!ta.variant_key ? { padding: 2, border: '1px solid #ff4d4f', borderRadius: 6, boxShadow: '0 0 0 2px rgba(255,77,79,0.2)' } : undefined}>
                      <Select
                        allowClear
                        placeholder="变体键（可选）"
                        style={{ width: 240 }}
                        value={ta.variant_key}
                            onFocus={() => ta.operation_type_id && loadVariantKeys(ta.operation_type_id)}
                            onChange={(v) => setTypeActions(prev => prev.map((x, i) => i===idx ? { ...x, variant_key: v } : x))}
                            options={opts.map((k:string)=>({
                              value: k,
                              label: usedVariantKeys.has(String(k)) ? `${k} · 已选择` : k,
                              disabled: usedVariantKeys.has(String(k)),
                            }))}
                    />
                    </div>
                  );
                }
                return (
                  <Input placeholder="变体键（可选）" value={ta.variant_key} onChange={(e) => setTypeActions(prev => prev.map((x, i) => i===idx ? { ...x, variant_key: e.target.value } : x))} style={{ width: 240 }} />
                );
              })()}
              <InputNumber placeholder="阶段(stage)" style={{ width: 120 }} min={1} value={ta.stage} onChange={(v) => setTypeActions(prev => prev.map((x, i) => i===idx ? { ...x, stage: Number(v || 1) } : x))} />
              <Input placeholder="分组(group)" style={{ width: 160 }} value={ta.group_key} onChange={(e) => setTypeActions(prev => prev.map((x, i) => i===idx ? { ...x, group_key: e.target.value } : x))} />
              <InputNumber placeholder="动作顺序（从小到大）" style={{ width: 120 }} value={ta.sort_order} onChange={(v) => setTypeActions(prev => prev.map((x, i) => i===idx ? { ...x, sort_order: Number(v || 0) } : x))} />
              <Button
                onClick={() => {
                  setBillingCfgIdx(idx);
                  setBillingCfgOpen(true);
                }}
              >
                计费基准设置
              </Button>
              <Button danger onClick={() => removeTypeAction(idx)}>移除</Button>
            </Space>
          ))}
        </Space>
        <Modal
          title={(() => {
            const idx = Number(billingCfgIdx);
            const row = (Number.isFinite(idx) && idx >= 0) ? (typeActions[idx] || {}) : {};
            const opId = Number((row as any)?.operation_type_id);
            const op = (opTypes || []).find((x: any) => Number(x?.id) === opId) || null;
            const opName = String((op as any)?.name || (opIdToName.get(opId) || '').replace(/\s*\(.+\)\s*$/, '') || '').trim();
            const opCode = String((op as any)?.system_code || (op as any)?.code || '').trim();
            const variantKey = String((row as any)?.variant_key || '').trim();
            const label = opName || opCode ? `${opName || '-'}${opCode ? ` (${opCode})` : ''}${variantKey ? ` · 变体: ${variantKey}` : ''}` : '-';
            return (
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, maxWidth: 'calc(100% - 56px)' }}>
                <span style={{ flex: '0 0 auto' }}>计费基准设置（当前虚拟工单）</span>
                <span style={{ color: '#1677ff', fontSize: 14, fontWeight: 600, flex: '1 1 auto', minWidth: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  当前：{label}
                </span>
              </div>
            );
          })()}
          open={billingCfgOpen}
          onCancel={() => { setBillingCfgOpen(false); setBillingCfgIdx(-1); }}
          onOk={() => { setBillingCfgOpen(false); setBillingCfgIdx(-1); }}
          width={860}
        >
          {(() => {
            const idx = Number(billingCfgIdx);
            const row = (Number.isFinite(idx) && idx >= 0) ? (typeActions[idx] || {}) : {};
            return (
              <Space direction="vertical" size={12} style={{ width: '100%' }}>
                <div>
                  <div style={{ marginBottom: 6, fontWeight: 600 }}>默认证据源优先级</div>
                  <Select
                    mode="multiple"
                    allowClear
                    style={{ width: '100%' }}
                    value={Array.isArray((row as any)?.source_priority) ? (row as any).source_priority : []}
                    options={SOURCE_PRIORITY_OPTIONS}
                    placeholder="不填则沿用当前旧逻辑（兼容模式）"
                    onChange={(vals) => {
                      setTypeActions((prev) => prev.map((x, i) => i === idx ? ({ ...x, source_priority: sanitizePriorityList(vals) }) : x));
                    }}
                  />
                </div>

                <div>
                  <div style={{ marginBottom: 6, fontWeight: 600 }}>计费数量语义（quantity_semantic，可选）</div>
                  <Input
                    placeholder="如：container_count / buy_waybill_charge / order_count"
                    value={String((row as any)?.quantity_semantic || '')}
                    onChange={(e) => {
                      const v = String(e.target.value || '').trim();
                      setTypeActions((prev) => prev.map((x, i) => i === idx ? ({ ...x, quantity_semantic: (v || undefined) as any }) : x));
                    }}
                  />
                </div>

                <div>
                  <div style={{ marginBottom: 6, fontWeight: 600 }}>按口径覆盖证据源优先级（可选）</div>
                  <Space direction="vertical" size={8} style={{ width: '100%' }}>
                    {METRIC_KEYS_FOR_PROFILE.map((mk) => (
                      <div key={mk} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <div style={{ width: 110, color: '#595959', fontFamily: 'monospace' }}>{mk}</div>
                        <Select
                          mode="multiple"
                          allowClear
                          style={{ flex: 1 }}
                          value={Array.isArray((row as any)?.metric_sources?.[mk]) ? (row as any).metric_sources[mk] : []}
                          options={SOURCE_PRIORITY_OPTIONS}
                          placeholder="为空则继承默认证据源优先级"
                          onChange={(vals) => {
                            setTypeActions((prev) => prev.map((x, i) => {
                              if (i !== idx) return x;
                              const next = { ...((x as any)?.metric_sources || {}) } as Record<string, string[]>;
                              const s = sanitizePriorityList(vals);
                              if (s.length) next[mk] = s;
                              else delete next[mk];
                              return { ...x, metric_sources: next };
                            }));
                          }}
                        />
                      </div>
                    ))}
                  </Space>
                </div>

                <div>
                  <div style={{ marginBottom: 6, fontWeight: 600 }}>不满足条件时（适用条件）</div>
                  <Space direction="vertical" size={8} style={{ width: '100%' }}>
                    <div>
                      <div style={{ marginBottom: 4, color: '#595959' }}>waybill_mode_in（仅这些运单提交模式生效）</div>
                      <Select
                        mode="multiple"
                        allowClear
                        style={{ width: '100%' }}
                        value={Array.isArray((row as any)?.applicability_waybill_mode_in) ? (row as any).applicability_waybill_mode_in : []}
                        options={WAYBILL_MODE_OPTIONS}
                        placeholder="不填表示不过滤"
                        onChange={(vals) => {
                          const list = (Array.isArray(vals) ? vals : []).map((x: any) => String(x || '').trim()).filter(Boolean);
                          setTypeActions((prev) => prev.map((x, i) => i === idx ? ({ ...x, applicability_waybill_mode_in: list }) : x));
                        }}
                      />
                    </div>
                    <div>
                      <div style={{ marginBottom: 4, color: '#595959' }}>waybill_mode_not_in（排除这些运单提交模式）</div>
                      <Select
                        mode="multiple"
                        allowClear
                        style={{ width: '100%' }}
                        value={Array.isArray((row as any)?.applicability_waybill_mode_not_in) ? (row as any).applicability_waybill_mode_not_in : []}
                        options={WAYBILL_MODE_OPTIONS}
                        placeholder="不填表示不排除"
                        onChange={(vals) => {
                          const list = (Array.isArray(vals) ? vals : []).map((x: any) => String(x || '').trim()).filter(Boolean);
                          setTypeActions((prev) => prev.map((x, i) => i === idx ? ({ ...x, applicability_waybill_mode_not_in: list }) : x));
                        }}
                      />
                    </div>
                    <div>
                      <div style={{ marginBottom: 4, color: '#595959' }}>waybill_source_in（仅这些运单来源生效）</div>
                      <Select
                        mode="multiple"
                        allowClear
                        style={{ width: '100%' }}
                        value={Array.isArray((row as any)?.applicability_waybill_source_in) ? (row as any).applicability_waybill_source_in : []}
                        options={WAYBILL_SOURCE_OPTIONS}
                        placeholder="不填表示不过滤"
                        onChange={(vals) => {
                          const list = (Array.isArray(vals) ? vals : []).map((x: any) => String(x || '').trim()).filter(Boolean);
                          setTypeActions((prev) => prev.map((x, i) => i === idx ? ({ ...x, applicability_waybill_source_in: list }) : x));
                        }}
                      />
                    </div>
                    <div>
                      <div style={{ marginBottom: 4, color: '#595959' }}>is_pack_required_in（仅这些打包需求生效）</div>
                      <Select
                        mode="multiple"
                        allowClear
                        style={{ width: '100%' }}
                        value={Array.isArray((row as any)?.applicability_is_pack_required_in) ? (row as any).applicability_is_pack_required_in : []}
                        options={PACK_REQUIRED_OPTIONS}
                        placeholder="不填表示不过滤"
                        onChange={(vals) => {
                          const list = (Array.isArray(vals) ? vals : []).filter((x: any) => typeof x === 'boolean');
                          setTypeActions((prev) => prev.map((x, i) => i === idx ? ({ ...x, applicability_is_pack_required_in: list }) : x));
                        }}
                      />
                    </div>
                    <div>
                      <div style={{ marginBottom: 4, color: '#595959' }}>estimated_packing_method_in（仅这些预估打包方式生效）</div>
                      <Select
                        mode="multiple"
                        allowClear
                        style={{ width: '100%' }}
                        value={Array.isArray((row as any)?.applicability_estimated_packing_method_in) ? (row as any).applicability_estimated_packing_method_in : []}
                        options={ESTIMATED_PACKING_METHOD_OPTIONS}
                        placeholder="不填表示不过滤"
                        onChange={(vals) => {
                          const list = (Array.isArray(vals) ? vals : []).map((x: any) => String(x || '').trim()).filter(Boolean);
                          setTypeActions((prev) => prev.map((x, i) => i === idx ? ({ ...x, applicability_estimated_packing_method_in: list }) : x));
                        }}
                      />
                    </div>
                    <div>
                      <div style={{ marginBottom: 4, color: '#595959' }}>target_source_in（仅这些操作对象来源生效）</div>
                      <Select
                        mode="multiple"
                        allowClear
                        style={{ width: '100%' }}
                        value={Array.isArray((row as any)?.applicability_target_source_in) ? (row as any).applicability_target_source_in : []}
                        options={TARGET_SOURCE_OPTIONS}
                        placeholder="不填表示不过滤"
                        onChange={(vals) => {
                          const list = sanitizeTargetSourceList(vals);
                          setTypeActions((prev) => prev.map((x, i) => i === idx ? ({ ...x, applicability_target_source_in: list }) : x));
                        }}
                      />
                    </div>
                    <div>
                      <div style={{ marginBottom: 4, color: '#595959' }}>target_source_not_in（排除这些操作对象来源）</div>
                      <Select
                        mode="multiple"
                        allowClear
                        style={{ width: '100%' }}
                        value={Array.isArray((row as any)?.applicability_target_source_not_in) ? (row as any).applicability_target_source_not_in : []}
                        options={TARGET_SOURCE_OPTIONS}
                        placeholder="不填表示不排除"
                        onChange={(vals) => {
                          const list = sanitizeTargetSourceList(vals);
                          setTypeActions((prev) => prev.map((x, i) => i === idx ? ({ ...x, applicability_target_source_not_in: list }) : x));
                        }}
                      />
                    </div>
                  </Space>
                </div>

                <div>
                  <div style={{ marginBottom: 6, fontWeight: 600 }}>缺少计费证据时处理策略</div>
                  <Select
                    allowClear
                    style={{ width: 320 }}
                    placeholder="默认 warn"
                    value={(row as any)?.on_missing}
                    options={[
                      { value: 'warn', label: 'warn（提示）' },
                      { value: 'skip', label: 'skip（跳过该工单自动建议）' },
                      { value: 'block', label: 'block（标记阻断问题）' },
                    ]}
                    onChange={(v) => {
                      setTypeActions((prev) => prev.map((x, i) => i === idx ? ({ ...x, on_missing: (v || undefined) as any }) : x));
                    }}
                  />
                </div>
              </Space>
            );
          })()}
        </Modal>
        <Modal
          title="计费动作组合（虚拟工单）- 填写提示"
          open={showActionPlanHelp}
          onCancel={() => setShowActionPlanHelp(false)}
          footer={null}
          width={760}
        >
          <Space direction="vertical" size={10} style={{ width: '100%' }}>
            <div style={{ whiteSpace: 'pre-line', color: '#141414', background: '#f6ffed', border: '1px solid #b7eb8f', borderRadius: 8, padding: 12 }}>
{`【用途说明】
此处配置的是“计费轨”的动作组合（OperationType + 变体键），用于自动生成“工单模板与路由”，并在受理后生成“虚拟工单（计费行）”以匹配计费规则。
仓库员工实际执行的步骤与顺序，请在“执行动作序列”中维护，与此处无直接对应。`}
            </div>

            <div style={{ whiteSpace: 'pre-line', color: '#141414', background: '#fffbe6', border: '1px solid #ffe58f', borderRadius: 8, padding: 12 }}>
{`【字段说明】（位于“变体键”右侧的三个输入）
1) 阶段(stage)【必填，数字】：用于划分串行阶段。阶段之间严格串行：stage=2 依赖 stage=1 的全部完成。
2) 分组(group)【必填，文本】：同阶段内的并/串行分组键。
   - 同阶段、不同 group → 并行（无相互依赖）
   - 同阶段、相同 group → 串行（见“动作顺序”）
3) 动作顺序(sort_order)【数字】：同组内的顺序，从小到大串行；仅在同组时生效。`}
            </div>

            <div style={{ whiteSpace: 'pre-line', color: '#141414', background: '#f0f5ff', border: '1px solid #adc6ff', borderRadius: 8, padding: 12 }}>
{`【并/串行判定】（仅按 stage/group 决定）
- 不同阶段：串行。后阶段“每个组的第一步”依赖上一阶段所有步骤。
- 同阶段、不同组：并行。互相无 after 依赖。
- 同阶段、相同组：按 sort_order 串行，后一步 after 前一步。

示例：下架 → （换标 || 装箱） → 出库
- 下架：stage=1, group=PICK, sort=1
- 换标：stage=2, group=RELABEL, sort=1
- 装箱：stage=2, group=BOX,     sort=1
- 出库：stage=3, group=SHIP,    sort=1
生成计划：
- RELABEL.after = [PICK]
- BOX.after = [PICK]
- SHIP.after = [RELABEL, BOX]

提示：审核端仍可在“计划编辑器”临时增删 after 并保存为 plan_override，以覆盖自动生成的并/串行。`}
            </div>

            <div style={{ whiteSpace: 'pre-line', color: '#141414', background: '#fff7e6', border: '1px solid #ffd591', borderRadius: 8, padding: 12 }}>
{`【变体键规则】（同一操作类型含多个变体键时怎么配）
- 原则：同一 OperationType（如“装箱”）需要收取多种口径（如基础费/容积费）时，每个变体键单独新增一条动作记录（如 base、volume）。
- 推荐填写：
  - operation_type：装箱（相同）
  - variant_key：base、volume（不同）
  - stage：相同（如 2）
  - group：不同（如 BOX_BASE、BOX_VOLUME）→ 同阶段不同组并行
  - sort_order：各自填写（仅同组内生效）
- 自动生成效果：
  - 会为不同变体生成不同工单模板与计划节点，模板编码形如 <模板>_<OP>_<VARIANT>，模板名称追加 (VARIANT)，彼此独立执行与计费。
  - 计费时分别命中各自收费规则（如 box_count 与 volume_sum），最终在指令层汇总。`}
            </div>

            <div style={{ whiteSpace: 'pre-line', color: '#141414', background: '#f9f0ff', border: '1px solid #d3adf7', borderRadius: 8, padding: 12 }}>
{`【Profile 计费基准设置】（点击每行“计费基准设置”）
- 作用：为“当前虚拟工单”单独指定自动聚合建议值的证据来源优先级，不影响其他工单。
- 兼容原则：若不填写 profile 字段，系统沿用旧逻辑（不会影响已稳定的换标+装箱模板）。

1) 默认证据源优先级（source_priority）
- 可选：operation（操作产物）/ instruction（指令事实）
- 示例：["operation","instruction"] 表示先取操作产物，缺失时再回退指令事实。

2) 按口径覆盖（metric_sources）
- 可按 quantity / volume_m3 / volume_cc / weight_kg / weight_g 分别设置优先级。
- 未配置某个口径时，会继承“默认证据源优先级”。

3) 计费数量语义（quantity_semantic）
- 用于 quantity 口径的语义细分（如 container_count、buy_waybill_charge 等）。
- 仅在该工单规则需要 quantity 语义时填写；不填则按默认规则解析。

4) 适用条件（applicability.waybill_mode_in）
- 用于限制该工单仅在特定运单模式生效（例如仅仓库购单模式）。
- 不满足条件时，该工单会被标记为 not_applicable 并跳过自动建议。

5) 缺失证据处理（on_missing）
- warn：给提示，允许继续。
- skip：跳过该工单的自动建议值。
- block：记为阻断问题，需要人工处理后再终审更稳妥。`}
            </div>
          </Space>
        </Modal>
        {/* 问题配置（问卷） */}
        <div style={{ marginTop: 16, marginBottom: 8, fontWeight: 600, display: 'flex', alignItems: 'center' }}>
          <Space size={8} align="center">
            <span>问题配置（问卷）</span>
            <Button
              type="primary"
              style={{ background: '#f5f5f5', borderColor: '#d9d9d9', color: '#595959' }}
              onClick={() => {
                Modal.info({
                  title: '问题配置 - 说明',
                  icon: null,
                  centered: true,
                  width: 860,
                  okText: '知道了',
                  modalRender: (node) => (<div style={{ background: '#ffffff', color: '#141414', borderRadius: 8 }}>{node}</div>),
                  content: (
                    <div style={{ whiteSpace: 'pre-line', color: '#141414' }}>
{`用途：
· 配置客户在创建指令时需要回答的问题。
· 可选设置“显示条件(show_if)”与“条件必填(required_if)”。

字段说明与示例：
· key：唯一键，建议英文下划线命名。例：need_label
· label：问题标题（展示给客户）。例：是否需要换标？
· type：题型。boolean/select/multiselect/number/text/textarea
· required：是否必填。例：必填（客户必须回答）
· required_if：条件必填（可选），满足条件时才必填。示例：
  - required_if.key=waybill_source
  - required_if.op=eq
  - required_if.value=仓库提供
· options：选项，仅用于单选/多选。例：A,B,C（回车新增）
· default：默认值，可选。例：boolean=是；select=标准；multiselect=[A,B]；number=5
· min/max/step/unit：数字题约束。例：min=1,max=100,step=1,unit=件
· max_length：文本最大长度。例：50
· tips：提示说明，显示在标题旁。例：仅支持 A4 PDF
· placeholder：输入占位。例：请输入说明
· show_if（显示条件）：依赖前置问题显示。示例：
  - 基础模式（单组）：
    · show_if.key=waybill_submit_mode
    · show_if.op=eq
    · show_if.value=immediate
  - 组合模式（推荐）：
    · show_if.any_conditions（任一满足，OR）
    · show_if.must_conditions（全部满足，AND）
    · 最终规则：must_conditions 全部满足 且 any_conditions 任一满足
  - 组合示例： (运单来源=仓库提供 或 运单上传时机=先申请包装数据) 且 打包方式=托盘
    · show_if.must_conditions = [{ key: "repack_method", op: "in", value: ["装箱后打托盘","直接打托盘"] }]
    · show_if.any_conditions = [
        { key: "waybill_source", op: "eq", value: "仓库提供" },
        { key: "waybill_submit_mode", op: "eq", value: "先申请包装数据" }
      ]
  - 条件值支持文本或 JSON。例：true、["A","B"]`}
                    </div>
                  )
                });
              }}
            >
              说明
            </Button>
            <Button
              type="primary"
              style={{ background: '#f6ffed', borderColor: '#b7eb8f', color: '#389e0d' }}
              onClick={() => setQRows(prev => [...prev, { key: '', label: '', type: 'boolean', required: false }])}
            >
              新增问题
            </Button>
          </Space>
        </div>
        <Space direction="vertical" style={{ width: '100%' }}>
          {(qRows || []).map((q, idx) => (
            <div key={idx} style={{ border: '1px solid #e5e7eb', borderRadius: 6, padding: 12, background: idx % 2 === 0 ? '#fff7e6' : '#f6ffed' }}>
            <Space style={{ display: 'flex', flexWrap: 'wrap' }} size={8}>
              <span style={{ width: 48, textAlign: 'right', color: '#fa541c', fontWeight: 700 }}>{`#${idx + 1}`}</span>
              <Input style={{ width: 160 }} placeholder="键(key)" value={q.key} onChange={(e)=> setQRows(prev=> prev.map((x,i)=> i===idx? { ...x, key: e.target.value }: x))} />
              <Input style={{ width: 220 }} placeholder="问题标题(label)" value={q.label} onChange={(e)=> setQRows(prev=> prev.map((x,i)=> i===idx? { ...x, label: e.target.value }: x))} />
              <Select style={{ width: 160 }} value={q.type} options={[
                { value: 'boolean', label: '是/否' },
                { value: 'select', label: '单选' },
                { value: 'multiselect', label: '多选' },
                { value: 'number', label: '数字' },
                { value: 'text', label: '文本' },
                { value: 'textarea', label: '多行文本' },
              ]} onChange={(v)=> setQRows(prev=> prev.map((x,i)=> i===idx? { ...x, type: v as any, default: undefined }: x))} />
              <Select style={{ width: 120 }} value={q.required? '1':'0'} options={[{value:'1', label:'必填'},{value:'0', label:'可选'}]} onChange={(v)=> setQRows(prev=> prev.map((x,i)=> i===idx? { ...x, required: v==='1' }: x))} />
              {/* 默认值(default)：可选，保存到 questionnaire.questions[*].default（创建指令页可读取并注入 answers） */}
              {q.type === 'boolean' && (
                <Select
                  allowClear
                  style={{ width: 160 }}
                  placeholder="默认值(default)"
                  value={q.default === true ? '1' : (q.default === false ? '0' : undefined)}
                  options={[
                    { value: '1', label: '默认：是' },
                    { value: '0', label: '默认：否' },
                  ]}
                  onChange={(v)=> setQRows(prev=> prev.map((x,i)=> i===idx ? ({ ...x, default: (v === '1' ? true : (v === '0' ? false : undefined)) }) : x))}
                />
              )}
              {q.type === 'number' && (
                <InputNumber
                  style={{ width: 160 }}
                  placeholder="默认值(default)"
                  value={q.default as any}
                  onChange={(v)=> setQRows(prev=> prev.map((x,i)=> i===idx ? ({ ...x, default: (v==null ? undefined : Number(v)) }) : x))}
                />
              )}
              {q.type === 'select' && (
                <Select
                  allowClear
                  style={{ width: 200 }}
                  placeholder="默认值(default)"
                  value={(q.default != null && String(q.default).trim() ? String(q.default).trim() : undefined) as any}
                  options={(q.options || []).map((o:any)=> ({ value: String(o.value), label: o.label || String(o.value) }))}
                  onChange={(v)=> setQRows(prev=> prev.map((x,i)=> i===idx ? ({ ...x, default: (v ? String(v) : undefined) }) : x))}
                />
              )}
              {q.type === 'multiselect' && (
                <Select
                  mode="multiple"
                  allowClear
                  style={{ width: 260 }}
                  placeholder="默认值(default)"
                  value={(Array.isArray(q.default) ? q.default.map((x:any)=> String(x)) : []) as any}
                  options={(q.options || []).map((o:any)=> ({ value: String(o.value), label: o.label || String(o.value) }))}
                  onChange={(vals)=> setQRows(prev=> prev.map((x,i)=> i===idx ? ({ ...x, default: Array.isArray(vals) ? vals.map(v=> String(v)) : [] }) : x))}
                />
              )}
              {(q.type === 'select' || q.type === 'multiselect') && (
                <>
                  <Select
                    style={{ width: 160 }}
                    value={q.options_source?.type === 'model' ? 'model' : 'custom'}
                    options={[
                      { value: 'custom', label: '自定义选项' },
                      { value: 'model', label: '数据库模型' },
                    ]}
                    onChange={(v)=> setQRows(prev=> prev.map((x,i)=> {
                      if (i !== idx) return x;
                      if (v === 'model') {
                        return {
                          ...x,
                          options_source: { type: 'model', model: 'logistics_provider', value_field: 'code', label_field: 'code' },
                          options: [],
                        };
                      }
                      const next = { ...x };
                      delete (next as any).options_source;
                      return next;
                    }))}
                  />
                  {q.options_source?.type === 'model' && (
                    <>
                      <Select
                        style={{ width: 180 }}
                        value={q.options_source?.model || 'logistics_provider'}
                        options={[{ value: 'logistics_provider', label: '物流服务商' }]}
                        onChange={(v)=> setQRows(prev=> prev.map((x,i)=> i===idx ? ({
                          ...x,
                          options_source: {
                            ...(x.options_source || {}),
                            type: 'model',
                            model: v as any,
                          }
                        }) : x))}
                      />
                      <Select
                        style={{ width: 140 }}
                        value={q.options_source?.value_field || 'code'}
                        options={[
                          { value: 'code', label: 'value: code' },
                          { value: 'id', label: 'value: id' },
                        ]}
                        onChange={(v)=> setQRows(prev=> prev.map((x,i)=> i===idx ? ({
                          ...x,
                          options_source: {
                            ...(x.options_source || {}),
                            type: 'model',
                            value_field: v as any,
                          }
                        }) : x))}
                      />
                      <Select
                        style={{ width: 140 }}
                        value={q.options_source?.label_field || 'code'}
                        options={[
                          { value: 'code', label: 'label: code' },
                          { value: 'name', label: 'label: name' },
                        ]}
                        onChange={(v)=> setQRows(prev=> prev.map((x,i)=> i===idx ? ({
                          ...x,
                          options_source: {
                            ...(x.options_source || {}),
                            type: 'model',
                            label_field: v as any,
                          }
                        }) : x))}
                      />
                    </>
                  )}
                </>
              )}
              {(q.type === 'text' || q.type === 'textarea') && (
                <Input
                  allowClear
                  style={{ width: 220 }}
                  placeholder="默认值(default)"
                  value={(q.default ?? '') as any}
                  onChange={(e)=> setQRows(prev=> prev.map((x,i)=> i===idx ? ({ ...x, default: e.target.value }) : x))}
                />
              )}
              {(q.type==='select' || q.type==='multiselect') && (
                (q.options_source?.type !== 'model') ? (
                  <Select
                    mode="tags"
                    style={{ width: 300 }}
                    placeholder="选项（输入回车新增）"
                    value={(q.options || []).map(o=>o.value)}
                    onChange={(vals)=> setQRows(prev=> prev.map((x,i)=> i===idx? { ...x, options: (vals as string[]).map(v=> ({ value: v, label: v })) }: x))}
                  />
                ) : null
              )}
              {q.type==='number' && (
                <>
                  <InputNumber style={{ width: 120 }} placeholder="最小值(min)" value={q.min} onChange={(v)=> setQRows(prev=> prev.map((x,i)=> i===idx? { ...x, min: Number(v??0) }: x))} />
                  <InputNumber style={{ width: 120 }} placeholder="最大值(max)" value={q.max} onChange={(v)=> setQRows(prev=> prev.map((x,i)=> i===idx? { ...x, max: Number(v??0) }: x))} />
                  <InputNumber style={{ width: 120 }} placeholder="步长(step)" value={q.step} onChange={(v)=> setQRows(prev=> prev.map((x,i)=> i===idx? { ...x, step: Number(v??0) }: x))} />
                  <Input style={{ width: 120 }} placeholder="单位(unit)" value={q.unit} onChange={(e)=> setQRows(prev=> prev.map((x,i)=> i===idx? { ...x, unit: e.target.value }: x))} />
                </>
              )}
              {(q.type==='text' || q.type==='textarea') && (
                <InputNumber style={{ width: 140 }} placeholder="最大长度" value={q.max_length} onChange={(v)=> setQRows(prev=> prev.map((x,i)=> i===idx? { ...x, max_length: Number(v??0) }: x))} />
              )}
              <Input style={{ width: 260 }} placeholder="提示(tips)" value={q.tips} onChange={(e)=> setQRows(prev=> prev.map((x,i)=> i===idx? { ...x, tips: e.target.value }: x))} />
              <Input style={{ width: 260 }} placeholder="占位文本(placeholder)" value={q.placeholder} onChange={(e)=> setQRows(prev=> prev.map((x,i)=> i===idx? { ...x, placeholder: e.target.value }: x))} />
              <span style={{ color: '#8c8c8c' }}>显示条件（必选 + 任一）</span>
              <span style={{ color: '#595959' }}>任一条件(OR)</span>
              <Button
                size="small"
                onClick={() => setQRows(prev => prev.map((x, i) => {
                  if (i !== idx) return x;
                  const arr = Array.isArray((x as any)?.show_if_any_conditions) ? [...(x as any).show_if_any_conditions] : [];
                  arr.push({ key: '', op: 'eq', value_text: '' });
                  return { ...x, show_if_any_conditions: arr };
                }))}
              >
                新增任一条件
              </Button>
              {(Array.isArray(q.show_if_any_conditions) ? q.show_if_any_conditions : []).map((cond: any, cIdx: number) => (
                <React.Fragment key={`show-if-any-${idx}-${cIdx}`}>
                  <Input
                    style={{ width: 160 }}
                    placeholder={`任一条件${cIdx + 1}: 依赖问题key`}
                    value={cond?.key || ''}
                    onChange={(e)=> setQRows(prev=> prev.map((x,i)=> {
                      if (i !== idx) return x;
                      const arr = Array.isArray((x as any)?.show_if_any_conditions) ? [...(x as any).show_if_any_conditions] : [];
                      arr[cIdx] = { ...(arr[cIdx] || {}), key: e.target.value };
                      return { ...x, show_if_any_conditions: arr };
                    }))}
                  />
                  <Select
                    style={{ width: 140 }}
                    placeholder="条件"
                    value={cond?.op || 'eq'}
                    options={[
                      { value: 'eq', label: '等于' },
                      { value: 'neq', label: '不等于' },
                      { value: 'in', label: '在集合中' },
                      { value: 'contains', label: '包含' },
                      { value: 'gt', label: '大于' },
                      { value: 'gte', label: '大于等于' },
                      { value: 'lt', label: '小于' },
                      { value: 'lte', label: '小于等于' },
                      { value: 'is_true', label: '为真' },
                      { value: 'is_false', label: '为假' },
                      { value: 'is_empty', label: '为空' },
                      { value: 'not_empty', label: '非空' },
                    ]}
                    onChange={(v)=> setQRows(prev=> prev.map((x,i)=> {
                      if (i !== idx) return x;
                      const arr = Array.isArray((x as any)?.show_if_any_conditions) ? [...(x as any).show_if_any_conditions] : [];
                      arr[cIdx] = { ...(arr[cIdx] || {}), op: (v as any) };
                      return { ...x, show_if_any_conditions: arr };
                    }))}
                  />
                  <Input
                    style={{ width: 220 }}
                    placeholder="条件值(JSON/文本)"
                    disabled={['is_true','is_false','is_empty','not_empty'].includes(String(cond?.op || ''))}
                    value={cond?.value_text || ''}
                    onChange={(e)=> setQRows(prev=> prev.map((x,i)=> {
                      if (i !== idx) return x;
                      const arr = Array.isArray((x as any)?.show_if_any_conditions) ? [...(x as any).show_if_any_conditions] : [];
                      arr[cIdx] = { ...(arr[cIdx] || {}), value_text: e.target.value };
                      return { ...x, show_if_any_conditions: arr };
                    }))}
                  />
                  <Button
                    size="small"
                    danger
                    onClick={() => setQRows(prev => prev.map((x, i) => {
                      if (i !== idx) return x;
                      const arr = Array.isArray((x as any)?.show_if_any_conditions) ? [...(x as any).show_if_any_conditions] : [];
                      return { ...x, show_if_any_conditions: arr.filter((_: any, j: number) => j !== cIdx) };
                    }))}
                  >
                    删除条件
                  </Button>
                </React.Fragment>
              ))}
              <span style={{ color: '#595959' }}>必选条件(AND)</span>
              <Button
                size="small"
                onClick={() => setQRows(prev => prev.map((x, i) => {
                  if (i !== idx) return x;
                  const arr = Array.isArray((x as any)?.show_if_must_conditions) ? [...(x as any).show_if_must_conditions] : [];
                  arr.push({ key: '', op: 'eq', value_text: '' });
                  return { ...x, show_if_must_conditions: arr };
                }))}
              >
                新增必选条件
              </Button>
              {(Array.isArray(q.show_if_must_conditions) ? q.show_if_must_conditions : []).map((cond: any, cIdx: number) => (
                <React.Fragment key={`show-if-must-${idx}-${cIdx}`}>
                  <Input
                    style={{ width: 160 }}
                    placeholder={`必选条件${cIdx + 1}: 依赖问题key`}
                    value={cond?.key || ''}
                    onChange={(e)=> setQRows(prev=> prev.map((x,i)=> {
                      if (i !== idx) return x;
                      const arr = Array.isArray((x as any)?.show_if_must_conditions) ? [...(x as any).show_if_must_conditions] : [];
                      arr[cIdx] = { ...(arr[cIdx] || {}), key: e.target.value };
                      return { ...x, show_if_must_conditions: arr };
                    }))}
                  />
                  <Select
                    style={{ width: 140 }}
                    placeholder="条件"
                    value={cond?.op || 'eq'}
                    options={[
                      { value: 'eq', label: '等于' },
                      { value: 'neq', label: '不等于' },
                      { value: 'in', label: '在集合中' },
                      { value: 'contains', label: '包含' },
                      { value: 'gt', label: '大于' },
                      { value: 'gte', label: '大于等于' },
                      { value: 'lt', label: '小于' },
                      { value: 'lte', label: '小于等于' },
                      { value: 'is_true', label: '为真' },
                      { value: 'is_false', label: '为假' },
                      { value: 'is_empty', label: '为空' },
                      { value: 'not_empty', label: '非空' },
                    ]}
                    onChange={(v)=> setQRows(prev=> prev.map((x,i)=> {
                      if (i !== idx) return x;
                      const arr = Array.isArray((x as any)?.show_if_must_conditions) ? [...(x as any).show_if_must_conditions] : [];
                      arr[cIdx] = { ...(arr[cIdx] || {}), op: (v as any) };
                      return { ...x, show_if_must_conditions: arr };
                    }))}
                  />
                  <Input
                    style={{ width: 220 }}
                    placeholder="条件值(JSON/文本)"
                    disabled={['is_true','is_false','is_empty','not_empty'].includes(String(cond?.op || ''))}
                    value={cond?.value_text || ''}
                    onChange={(e)=> setQRows(prev=> prev.map((x,i)=> {
                      if (i !== idx) return x;
                      const arr = Array.isArray((x as any)?.show_if_must_conditions) ? [...(x as any).show_if_must_conditions] : [];
                      arr[cIdx] = { ...(arr[cIdx] || {}), value_text: e.target.value };
                      return { ...x, show_if_must_conditions: arr };
                    }))}
                  />
                  <Button
                    size="small"
                    danger
                    onClick={() => setQRows(prev => prev.map((x, i) => {
                      if (i !== idx) return x;
                      const arr = Array.isArray((x as any)?.show_if_must_conditions) ? [...(x as any).show_if_must_conditions] : [];
                      return { ...x, show_if_must_conditions: arr.filter((_: any, j: number) => j !== cIdx) };
                    }))}
                  >
                    删除条件
                  </Button>
                </React.Fragment>
              ))}
              <span style={{ color: '#8c8c8c' }}>必填条件</span>
              <Input
                style={{ width: 160 }}
                placeholder="依赖问题key"
                value={q.required_if?.key || ''}
                onChange={(e)=> setQRows(prev=> prev.map((x,i)=> i===idx? { ...x, required_if: { ...(x.required_if || {}), key: e.target.value } }: x))}
              />
              <Select
                style={{ width: 140 }}
                placeholder="条件"
                value={q.required_if?.op || undefined}
                allowClear
                options={[
                  { value: 'eq', label: '等于' },
                  { value: 'neq', label: '不等于' },
                  { value: 'in', label: '在集合中' },
                  { value: 'contains', label: '包含' },
                  { value: 'gt', label: '大于' },
                  { value: 'gte', label: '大于等于' },
                  { value: 'lt', label: '小于' },
                  { value: 'lte', label: '小于等于' },
                  { value: 'is_true', label: '为真' },
                  { value: 'is_false', label: '为假' },
                  { value: 'is_empty', label: '为空' },
                  { value: 'not_empty', label: '非空' },
                ]}
                onChange={(v)=> setQRows(prev=> prev.map((x,i)=> i===idx? { ...x, required_if: { ...(x.required_if || {}), op: (v as any) } }: x))}
              />
              <Input
                style={{ width: 220 }}
                placeholder="条件值(JSON/文本)"
                disabled={['is_true','is_false','is_empty','not_empty'].includes(String(q.required_if?.op || ''))}
                value={q.required_if?.value_text || ''}
                onChange={(e)=> setQRows(prev=> prev.map((x,i)=> i===idx? { ...x, required_if: { ...(x.required_if || {}), value_text: e.target.value } }: x))}
              />
              <Button
                size="small"
                disabled={idx === 0}
                onClick={() => moveQuestionRow(idx, -1)}
              >
                上移
              </Button>
              <Button
                size="small"
                disabled={idx === (qRows?.length || 0) - 1}
                onClick={() => moveQuestionRow(idx, 1)}
              >
                下移
              </Button>
              <Button danger onClick={()=> setQRows(prev => prev.filter((_,i)=> i!==idx))}>移除</Button>
            </Space>
            </div>
          ))}
        </Space>
        <div style={{ marginTop: 16, marginBottom: 8, fontWeight: 600, display: 'flex', alignItems: 'center' }}>
          <Space size={8} align="center">
            <span>附件需求（可视化设置）</span>
            <HelpHintButton
              onClick={() => Modal.info({
              title: '说明',
              icon: null,
              centered: true,
              okText: '知道了',
              modalRender: (node) => (<div style={{ background: '#ffffff', color: '#141414', borderRadius: 8 }}>{node}</div>),
              content: (
                <div style={{ whiteSpace: 'pre-line', color: '#141414' }}>
{`- 附件显示名称：该附件在前端展示时的名称（仅用于显示/审核提示，不影响文件名）。
 - 文件类型：允许上传的扩展名（可自定义录入，如 xlsx/csv/pdf/png/jpg）。
 - 最少(min)：最小上传数量。Min ≥ 1 表示必传；Min = 0 表示可选。
 - 最多(max)：最大上传数量限制（如仅允许 1 个 PDF 或最多 10 张图片）。
 - 提交阶段：客户端需提交（指令创建阶段，客户上传整单级资料）；完成动作需提交（执行阶段，仓库在完成对应动作时上传，支持 ALL 或具体 op:<id>）。
 - 打印需求：开启后，客户上传此类附件时必须回答四项：是否打印、打印件数、纸张尺寸、放置位置。同一类型下的每个附件可单独配置不同的打印设置，用于执行时提醒与打印计费。`}
                </div>
              )
            })}>说明</HelpHintButton>
            <Button
              type="primary"
              style={{ background: '#f6ffed', borderColor: '#b7eb8f', color: '#389e0d' }}
              onClick={() => {
              Modal.confirm({
                title: <div style={{ color: '#141414' }}>新增附件规则</div>,
                icon: null,
                centered: true,
                okText: '添加“客户端需提交”',
                cancelText: '添加“完成动作需提交”',
                onOk: () => setAttachmentRows(prev => [...prev, { scope: 'order', label: '', types: [], min: 0, printable: false }]),
                onCancel: () => setAttachmentRows(prev => [...prev, { scope: 'actions', action_key: 'ALL', label: '', types: [], min: 0, printable: false }])
              });
            }}
            >
              新增附件规则
            </Button>
          </Space>
        </div>
        <Space direction="vertical" style={{ width: '100%' }}>
          {(attachmentRows || []).filter(Boolean).map((row, idx) => (
            <div
              key={idx}
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: 8,
                padding: 10,
                borderRadius: 6,
                border: `1px solid ${idx % 2 === 0 ? '#91d5ff' : '#ffd591'}`,
                background: idx % 2 === 0 ? '#e6f7ff' : '#fff7e6',
              }}
            >
              {/* 行1：非模板字段 */}
              <Space style={{ display: 'flex', flexWrap: 'wrap' }} size={8}>
                <Select value={(row as any)?.scope || 'order'} style={{ width: 160 }} onChange={(v) => setAttachmentRows(prev => prev.map((x, i) => i===idx && x ? { ...x, scope: v as any } : x))}
                        options={[{ value: 'order', label: '客户端需提交' }, { value: 'actions', label: '完成动作需提交' }]} />
                {(row as any)?.scope === 'actions' && (() => {
                  // 选项：ALL + 当前已添加的动作(去重)
                  const optsBase = [{ value: 'ALL', label: '所有动作(ALL)' }];
                  const seen = new Set<string>();
                  const actionOpts = (typeActions || [])
                    .map(a => {
                      const id = Number(a.operation_type_id);
                      if (!id || Number.isNaN(id)) return null;
                      const val = `op:${id}`;
                      if (seen.has(val)) return null; seen.add(val);
                      return { value: val, label: opIdToName.get(id) || val };
                    })
                    .filter(Boolean) as { value: string; label: string }[];
                  const options = [...optsBase, ...actionOpts];
                  const value = (row as any)?.action_key && (row as any).action_key.trim() ? (row as any).action_key : 'ALL';
                  return (
                    <Select style={{ width: 220 }} value={value} options={options}
                            onChange={(v) => setAttachmentRows(prev => prev.map((x, i) => i===idx && x ? { ...x, action_key: v } : x))} />
                  );
                })()}
                <Input placeholder="附件显示名称" style={{ width: 200 }} value={(row as any)?.label}
                       onChange={(e) => setAttachmentRows(prev => prev.map((x, i) => i===idx && x ? { ...x, label: e.target.value } : x))} />
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                  <span style={{ fontSize: 12 }}>打印需求</span>
                  <Switch checked={!!(row as any)?.printable} onChange={(v) => setAttachmentRows(prev => prev.map((x, i) => i===idx && x ? { ...x, printable: v } : x))} size="small" />
                </span>
                <Select
                  mode="tags"
                  style={{ width: 240 }}
                  placeholder="文件类型(回车新增)"
                  value={(row as any)?.types || []}
                  options={[
                    'pdf','xlsx','csv','png','jpg','jpeg','gif','doc','docx','zip','rar','7z','mp4','avi'
                  ].map(ext => ({ value: ext, label: ext }))}
                  onChange={(vals) => setAttachmentRows(prev => prev.map((x, i) => i===idx && x ? { ...x, types: vals as string[] } : x))}
                />
                <InputNumber placeholder="最少(min)" style={{ width: 140 }} min={0} value={(row as any)?.min}
                            onChange={(v) => setAttachmentRows(prev => prev.map((x, i) => i===idx && x ? { ...x, min: Math.max(0, Number(v ?? 0)) } : x))} />
                <InputNumber placeholder="最多(max)" style={{ width: 140 }} min={0} value={(row as any)?.max}
                            onChange={(v) => setAttachmentRows(prev => prev.map((x, i) => i===idx && x ? { ...x, max: Math.max(0, Number(v ?? 0)) } : x))} />
                <Input placeholder="提示(tips)" style={{ width: 280 }} value={(row as any)?.tips}
                       onChange={(e) => setAttachmentRows(prev => prev.map((x, i) => i===idx && x ? { ...x, tips: e.target.value } : x))} />
              <Input
                placeholder="显示条件-问题key"
                style={{ width: 180 }}
                value={(row as any)?.show_if?.key || ''}
                onChange={(e) => setAttachmentRows(prev => prev.map((x, i) => i===idx && x ? { ...x, show_if: { ...(x as any)?.show_if, key: e.target.value } } : x))}
              />
              <Select
                style={{ width: 140 }}
                value={(row as any)?.show_if?.op || 'eq'}
                options={[
                  { value: 'eq', label: '等于' },
                  { value: 'neq', label: '不等于' },
                  { value: 'in', label: '在集合中' },
                  { value: 'contains', label: '包含' },
                  { value: 'gt', label: '大于' },
                  { value: 'gte', label: '大于等于' },
                  { value: 'lt', label: '小于' },
                  { value: 'lte', label: '小于等于' },
                  { value: 'is_true', label: '为真' },
                  { value: 'is_false', label: '为假' },
                  { value: 'is_empty', label: '为空' },
                  { value: 'not_empty', label: '非空' },
                ]}
                onChange={(v) => setAttachmentRows(prev => prev.map((x, i) => i===idx && x ? { ...x, show_if: { ...(x as any)?.show_if, op: v as any } } : x))}
              />
              <Input
                placeholder="显示条件-值"
                style={{ width: 180 }}
                value={(row as any)?.show_if?.value_text || ''}
                onChange={(e) => setAttachmentRows(prev => prev.map((x, i) => i===idx && x ? { ...x, show_if: { ...(x as any)?.show_if, value_text: e.target.value } } : x))}
              />
              </Space>
              {/* 行2：模板相关 + 行移除 */}
              <Space style={{ display: 'flex', flexWrap: 'wrap' }} size={8}>
                <Select
                  mode="multiple"
                  allowClear
                  style={{ width: 360 }}
                  placeholder="附件模板（可多选）"
                  loading={attachTplLoading}
                  onFocus={loadAttachmentTemplates}
                  value={Array.isArray((row as any)?.template_ids)
                    ? (row as any).template_ids
                    : ((row as any)?.template_id != null ? [Number((row as any).template_id)] : [])}
                  options={attachTplOptions
                    .filter(o => o.scope === (((row as any)?.scope === 'actions') ? 'actions' : 'order'))
                    .map(o => ({ value: o.value, label: o.label }))}
                  onChange={(vals) => setAttachmentRows(prev => prev.map((x, i) => i===idx && x ? { ...x, template_ids: vals as number[], template_id: (Array.isArray(vals) && vals.length === 1) ? Number(vals[0]) : undefined } : x))}
                />
                {(() => {
                  const ids = Array.isArray((row as any)?.template_ids)
                    ? (row as any).template_ids
                    : ((row as any)?.template_id != null ? [Number((row as any).template_id)] : []);
                  if (!ids || ids.length === 0) return null;
                  return (
                  <Space size={4} wrap>
                    {ids.map((tid:number, tIdx:number) => {
                        const opt = attachTplOptions.find(o => Number(o.value) === Number(tid));
                        const label = opt?.file_name || opt?.label || `${(row as any)?.label || '附件模板'}-${tIdx + 1}`;
                        const btnText = opt?.file_name ? `下载附件模板-${opt.file_name}` : (opt?.label ? `下载附件模板-${opt.label}` : '下载附件模板');
                      return (
                        <Button key={`tpl-${tid}`} type="link" onClick={() => downloadTemplate(Number(tid), label)}>{btnText}</Button>
                      );
                    })}
                  </Space>
                  );
                })()}
                <Button onClick={() => openUploadTemplateForRow(idx)}>上传/替换模板</Button>
                {(() => {
                  const ids = Array.isArray((row as any)?.template_ids)
                    ? (row as any).template_ids
                    : ((row as any)?.template_id != null ? [Number((row as any).template_id)] : []);
                  return ids.length > 0 ? (
                  <Button danger onClick={() => {
                    setAttachmentRows(prev => prev.map((x, i) => i===idx ? { ...x, template_ids: [], template_id: undefined } : x));
                  }}>移除模板</Button>
                  ) : null;
                })()}
                <Button
                  size="small"
                  disabled={idx === 0}
                  onClick={() => moveAttachmentRow(idx, -1)}
                >
                  上移
                </Button>
                <Button
                  size="small"
                  disabled={idx === (attachmentRows?.length || 0) - 1}
                  onClick={() => moveAttachmentRow(idx, 1)}
                >
                  下移
                </Button>
                <Button danger onClick={() => setAttachmentRows(prev => prev.filter((_, i) => i!==idx))}>移除规则</Button>
                {/* 行内：附件角色绑定（方式A） */}
                {(() => {
                  const ruleKey = (row as any)?.__rule_key as string | undefined;
                  const binding = ruleKey ? bindingsByRule[ruleKey] : null;
                  const disabledBind = !ruleKey || !editingTpl?.id;
                  return (
                    <Space size={8} style={{ marginLeft: 8 }}>
                      <Select
                        style={{ width: 260 }}
                        placeholder={disabledBind ? '保存模板后可绑定角色' : '选择附件角色'}
                        value={binding?.role_code || undefined}
                        onFocus={loadAttachmentRoles}
                        loading={rolesLoading || bindingBusy}
                        disabled={disabledBind}
                        options={(roleOptions || []).map(r => ({ value: r.code, label: `${r.name || r.code} · ${r.code}` }))}
                        onChange={(code) => bindOrPatch(ruleKey!, { role_code: String(code) })}
                      />
                      <Button size="small" disabled={disabledBind} onClick={() => { if (ruleKey) { setNewRoleForRule(ruleKey); setNewRoleOpen(true); } }}>
                        新建角色并绑定
                      </Button>
                      <span style={{ color: '#8c8c8c' }}>阻断</span>
                      <Switch size="small" disabled={disabledBind || !binding} checked={!!binding?.block_on_fail} onChange={(ck)=> bindOrPatch(ruleKey!, { block_on_fail: ck })} />
                      <span style={{ color: '#8c8c8c' }}>启用</span>
                      <Switch size="small" disabled={disabledBind || !binding} checked={binding ? (binding.enabled !== false) : true} onChange={(ck)=> bindOrPatch(ruleKey!, { enabled: ck })} />
                      {binding?.role_code ? <Tag color="blue">角色: {binding.role_code}</Tag> : null}
                    </Space>
                  );
                })()}
              </Space>
            </div>
          ))}
        </Space>
        {/* 上传模板弹窗（轻提示校验） */}
        <Modal
          open={uploadTplOpen}
          title="为此规则添加附件模板"
          onCancel={() => setUploadTplOpen(false)}
          onOk={doUploadTemplate}
        >
          {uploadTplRowIdx != null && (attachmentRows || [])[uploadTplRowIdx] ? (
            <div style={{ marginBottom: 8, color: '#666' }}>
              规则：{(attachmentRows[uploadTplRowIdx] as any)?.scope === 'order' ? '客户端需提交' : `完成动作需提交（${(attachmentRows[uploadTplRowIdx] as any)?.action_key || 'ALL'}）`}
            </div>
          ) : null}
          <Upload.Dragger
            multiple={false}
            beforeUpload={() => false}
            fileList={uploadTplFiles}
            onChange={({ fileList }) => {
              setUploadTplFiles(fileList);
              try {
                const f = fileList[0]?.originFileObj as File | undefined;
                if (!f || uploadTplRowIdx == null) { setUploadTplTips(''); return; }
                const row = (attachmentRows || [])[uploadTplRowIdx] as any;
                const ext = (f.name.split('.').pop() || '').toLowerCase();
                const types = Array.isArray(row?.types) ? row.types.map((t:any)=>String(t).toLowerCase()) : [];
                const warns: string[] = [];
                if (types.length > 0 && !types.includes(ext)) {
                  warns.push(`文件扩展名 .${ext} 不在规则允许范围内：${types.join(', ')}`);
                }
                if (ext === 'csv') {
                  const reader = new FileReader();
                  reader.onload = () => {
                    try {
                      const text = String(reader.result || '');
                      const firstLine = text.split(/\r?\n/)[0] || '';
                      const cols = firstLine.split(',').map(s=>s.trim()).filter(Boolean);
                      if (cols.length < 2) {
                        warns.push('CSV 首行列数过少，可能不是有效模板（轻提示）');
                      }
                      setUploadTplTips(warns.join('\n'));
                    } catch { setUploadTplTips(warns.join('\n')); }
                  };
                  reader.readAsText(f);
                } else {
                  setUploadTplTips(warns.join('\n'));
                }
              } catch { setUploadTplTips(''); }
            }}
          >
            <p>拖拽或点击上传（CSV/XLSX/PDF…）</p>
          </Upload.Dragger>
          {uploadTplTips ? <div style={{ whiteSpace: 'pre-line', color: '#faad14', marginTop: 8 }}>{uploadTplTips}</div> : null}
        </Modal>
        {/* 新建附件角色并绑定到当前规则 */}
        <Modal
          open={newRoleOpen}
          title="新建附件角色并绑定到当前规则"
          onCancel={() => { setNewRoleOpen(false); }}
          onOk={async () => {
            try {
              const code = String(newRoleForm.code || '').trim();
              const name = String(newRoleForm.name || '').trim();
              if (!code || !name) { message.warning('请填写角色编码与名称'); return; }
              let default_schema = {};
              let default_validators: any = [];
              try { default_schema = JSON.parse(newRoleForm.default_schema_text || '{}'); } catch { message.warning('默认schema JSON无效'); return; }
              try { default_validators = JSON.parse(newRoleForm.default_validators_text || '[]'); } catch { message.warning('默认校验器 JSON无效'); return; }
              const body = { code, name, default_schema, default_validators, is_active: !!newRoleForm.is_active };
              const resp = await request.post('/api/v1/instruction/attachment-roles/', body);
              if (resp?.data?.code) {
                message.success('角色已创建');
                try { await loadAttachmentRoles(); } catch {}
                // 立即绑定到当前规则行
                if (newRoleForRule) {
                  await bindOrPatch(newRoleForRule, { role_code: code });
                }
              }
              setNewRoleOpen(false);
            } catch (e:any) {
              message.error(e?.response?.data?.detail || '创建角色失败');
            }
          }}
        >
          <Space direction="vertical" style={{ width: '100%' }} size={8}>
            <div style={{ padding: '6px 8px', background: '#f5f5f5', border: '1px solid #d9d9d9', borderRadius: 6, color: '#595959' }}>
              用于为当前附件规则创建“附件角色”，并立即绑定到该规则。角色用于后续的结构提示与校验，例如条码映射表、装箱清单等。
            </div>
            <Input placeholder="角色编码（例如：BARCODE_MAPPING）" value={newRoleForm.code} onChange={(e)=> setNewRoleForm(prev => ({ ...prev, code: e.target.value }))} />
            <Input placeholder="角色名称（例如：新旧条码对应表）" value={newRoleForm.name} onChange={(e)=> setNewRoleForm(prev => ({ ...prev, name: e.target.value }))} />
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ color: '#8c8c8c' }}>启用：</span>
              <Switch checked={newRoleForm.is_active} onChange={(ck)=> setNewRoleForm(prev => ({ ...prev, is_active: ck }))} />
            </div>
            <div>
              <div style={{ fontWeight: 600, marginBottom: 4 }}>默认 schema（JSON）</div>
              <Input.TextArea rows={6} value={newRoleForm.default_schema_text} onChange={(e)=> setNewRoleForm(prev => ({ ...prev, default_schema_text: e.target.value }))} />
            </div>
            <div>
              <div style={{ fontWeight: 600, marginBottom: 4 }}>默认校验器（JSON）</div>
              <Input.TextArea rows={4} value={newRoleForm.default_validators_text} onChange={(e)=> setNewRoleForm(prev => ({ ...prev, default_validators_text: e.target.value }))} />
            </div>
          </Space>
        </Modal>
      </Modal>
      {/* 基准数据 schema 编辑器（行内） */}
      <Modal
        open={baselineSchemaOpen}
        onCancel={() => { setBaselineSchemaOpen(false); setBaselineSchemaIdx(null); }}
        onOk={() => {
          if (baselineSchemaIdx == null) { setBaselineSchemaOpen(false); return; }
          setBaselineItems(prev => prev.map((x,i)=> i===baselineSchemaIdx ? ({ ...x, schema_hint_json: baselineSchemaText }) : x));
          setBaselineSchemaOpen(false);
          setBaselineSchemaIdx(null);
        }}
        width={800}
        title="编辑基准数据 schema(JSON)"
      >
        <Input.TextArea value={baselineSchemaText} onChange={(e)=> setBaselineSchemaText(e.target.value)} autoSize={{ minRows: 10, maxRows: 20 }} placeholder="{}" />
        <div style={{ marginTop: 8, color: '#8c8c8c' }}>请填写有效 JSON；保存时会做解析校验。</div>
      </Modal>
      {/* 查看执行动作序列步骤列表 */}
      <Modal
        open={viewExecOpen}
        onCancel={() => { setViewExecOpen(false); setViewExecTpl(null); setViewExecSteps([]); }}
        title={viewExecTpl ? (<div style={{ display:'flex', alignItems:'center', gap:8 }}><span>执行动作序列 -</span><span style={{ fontWeight: 700, color: '#1677ff' }}>{viewExecTpl?.name || viewExecTpl?.system_code || ''}</span></div>) : '执行动作序列'}
        footer={null}
        width={1100}
      >
        {viewExecLoading ? <div style={{ color:'#999' }}>加载中...</div> : (
          <Table
            size="small"
            rowKey={(r:any, i?:number)=> String(r?.id ?? i)}
            pagination={false}
            dataSource={viewExecSteps}
            columns={[
              { title: '标题', dataIndex: 'title', width: 240, render: (v:any, row:any)=> v || row.step_type || '-' },
              { title: '步骤键', dataIndex: 'step_type', width: 180 },
              { title: '必做', dataIndex: 'required', width: 80, render: (v:any)=> v ? <Tag color="red">必做</Tag> : <span style={{ color:'#999' }}>可选</span> },
              { title: '阶段', dataIndex: 'stage', width: 90, render: (v:any)=> v ?? '-' },
              { title: '分组', dataIndex: 'group_key', width: 140, render: (v:any)=> v || <span style={{ color:'#999' }}>-</span> },
              { title: '组内顺序', dataIndex: 'sort_order', width: 90, render: (v:any)=> v ?? 0 },
              { title: '指标键', dataIndex: 'metrics_keys', width: 260, render: (v:any)=> {
                const arr = Array.isArray(v) ? v : [];
                return arr.length ? <span title={arr.join(', ')}>{arr.join(' / ')}</span> : <span style={{ color:'#999' }}>-</span>;
              } },
            ] as any}
          />
        )}
      </Modal>
      {/* 执行动作序列配置弹窗 */}
      <Modal
        open={execPackOpen}
        onCancel={() => { setExecPackOpen(false); setExecPackTpl(null); setExecTaskTpl(null); setExecSteps([]); }}
        title={execPackTpl ? (<div style={{ display:'flex', alignItems:'center', gap:8 }}><span>配置操作步骤序列 -</span><span style={{ fontWeight: 700, color: '#1677ff' }}>{execPackTpl?.name || execPackTpl?.system_code || ''}</span></div>) : '配置操作步骤序列'}
        width={1280}
        footer={[
          <Button key="close" onClick={() => { setExecPackOpen(false); setExecPackTpl(null); setExecTaskTpl(null); setExecSteps([]); }}>关闭</Button>,
          <Button key="save" type="primary" loading={execPackLoading} onClick={saveExecPack}>保存</Button>
        ]}
      >
        <div style={{ marginBottom: 12 }}>
          <Space>
            <span>步骤序列名称：</span>
            <Input style={{ width: 320 }} value={execTplName} onChange={(e)=> setExecTplName(e.target.value)} />
          </Space>
        </div>
        <div style={{ marginBottom: 8, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <span style={{ fontWeight: 600 }}>步骤列表</span>
          <Space>
            <Typography.Link style={{ border: '1px solid #faad14', background: '#fff7e6', padding: '2px 8px', borderRadius: 4 }} onClick={() => setShowExecStepHelp(true)}>
              填写提示
            </Typography.Link>
            <Button onClick={async () => {
              setStepLibOpen(true);
              setStepLibLoading(true);
              try {
                const resp = await request.get('/api/v1/instruction/steplibrary/', { params: { page_size: 1000 } });
                const list = Array.isArray(resp?.data?.results) ? resp.data.results : (Array.isArray(resp?.data) ? resp.data : []);
                setStepLibRows(list);
              } catch {
                message.error('加载步骤库失败');
              } finally {
                setStepLibLoading(false);
              }
            }}>从仓库操作步骤库导入</Button>
            <Button type="primary" onClick={addExecStep}>新增步骤</Button>
          </Space>
        </div>
        <Table
          size="small"
          loading={execPackLoading}
          rowKey={(r:any, i?:number) => String(r?.id ?? `new-${i}`)}
          dataSource={execSteps}
          pagination={false}
          scroll={{ x: 1400 }}
          columns={[
            { title: '来源操作步骤库', dataIndex: 'library_step', width: 220, render: (_:any, row:any, idx:number) => (
              <Select
                showSearch
                allowClear
                placeholder="从操作步骤库选择（可选）"
                style={{ width: 230 }}
                value={row.library_step}
                onFocus={async ()=> {
                  if (stepLibRows.length === 0) {
                    try {
                      setStepLibLoading(true);
                      const resp = await request.get('/api/v1/instruction/steplibrary/', { params: { page_size: 1000 } });
                      const list = Array.isArray(resp?.data?.results) ? resp.data.results : (Array.isArray(resp?.data) ? resp.data : []);
                      setStepLibRows(list);
                    } catch {} finally { setStepLibLoading(false); }
                  }
                }}
                onChange={(val:any) => setExecSteps(prev => prev.map((x,i)=> {
                  if (i !== idx) return x;
                  const next:any = { ...x, library_step: val || undefined };
                  const lib = (stepLibRows || []).find((r:any) => Number(r.id) === Number(val));
                  if (lib) {
                    if (!x.step_type || String(x.step_type).trim() === '') {
                      next.step_type = lib.step_key || '';
                    }
                    if (!x.title || String(x.title).trim() === '') {
                      next.title = lib.name || '';
                    }
                    const isEmptyObjJson = (s:any) => {
                      const t = String(s ?? '').trim();
                      return !t || t === '{}' || t === 'null';
                    };
                    const isEmptyArrJson = (s:any) => {
                      const t = String(s ?? '').trim();
                      return !t || t === '[]' || t === 'null';
                    };
                    const safeStringify = (v:any, isArray?: boolean) => {
                      try {
                        const fallback = isArray ? [] : {};
                        return JSON.stringify((v == null ? fallback : v), null, 2);
                      } catch {
                        return isArray ? '[]' : '{}';
                      }
                    };
                    if (isEmptyObjJson(x.required_metrics_json)) {
                      next.required_metrics_json = safeStringify(lib.default_required_metrics, false);
                    }
                    if (isEmptyObjJson(x.ui_schema_json)) {
                      next.ui_schema_json = safeStringify(lib.default_ui_schema, false);
                    }
                    if (isEmptyArrJson(x.action_plan_json)) {
                      next.action_plan_json = safeStringify(lib.default_action_plan, true);
                    }
                  }
                  return next;
                }))}
                options={stepLibRows.map((r:any)=> ({ value: r.id, label: `${r.step_key} · ${r.name}` }))}
              />
            )},
              { title: '页面渲染方式', dataIndex: 'render_mode', width: 150, render: (_:any, row:any) => {
                const key = String(row?.step_type || '').toUpperCase();
                const known = ['PICK', 'SUBMIT_AND_WRAPUP', 'WRAP_UP', 'RELABEL', 'BOX/BAG_PACK'];
                if (!known.includes(key)) return <Tag>JSON</Tag>;
                const display = (key === 'BOX/BAG_PACK') ? 'BOX/BAG_PACK' : key;
                return <Tag color="green">组件:{display}</Tag>;
              }},
            { title: '步骤键', dataIndex: 'step_type', width: 250, render: (_:any, row:any, idx:number) => (
              <Input value={row.step_type} onChange={(e)=> setExecSteps(prev => prev.map((x,i)=> i===idx ? ({ ...x, step_type: e.target.value }) : x))} />
            )},
            { title: '标题', dataIndex: 'title', width: 150, render: (_:any, row:any, idx:number) => (
              <Input value={row.title} onChange={(e)=> setExecSteps(prev => prev.map((x,i)=> i===idx ? ({ ...x, title: e.target.value }) : x))} />
            )},
            { title: '必做', dataIndex: 'required', width: 20, render: (v:any, row:any, idx:number) => (
              <Switch checked={!!row.required} onChange={(checked)=> setExecSteps(prev => prev.map((x,i)=> i===idx ? ({ ...x, required: checked }) : x))} />
            )},
            { title: '阶段', dataIndex: 'stage', width: 20, render: (_:any, row:any, idx:number) => (
              <InputNumber min={1} value={row.stage} onChange={(val)=> setExecSteps(prev => prev.map((x,i)=> i===idx ? ({ ...x, stage: Number(val || 1) }) : x))} />
            )},
            { title: '分组', dataIndex: 'group_key', width: 280, render: (_:any, row:any, idx:number) => (
              <Input value={row.group_key} onChange={(e)=> setExecSteps(prev => prev.map((x,i)=> i===idx ? ({ ...x, group_key: e.target.value }) : x))} />
            )},
            { title: '顺序', dataIndex: 'sort_order', width: 20, render: (_:any, row:any, idx:number) => (
              <InputNumber value={row.sort_order} onChange={(val)=> setExecSteps(prev => prev.map((x,i)=> i===idx ? ({ ...x, sort_order: Number(val || 0) }) : x))} />
            )},
            { title: '编辑JSON', key: 'json_edit', width: 200, render: (_:any, row:any, idx:number) => (
              <Space size="small">
                <Button size="small" onClick={()=> openJsonEditor('required_metrics_json', idx)}>指标</Button>
                <Button size="small" onClick={()=> openJsonEditor('ui_schema_json', idx)}>UI</Button>
                <Button size="small" onClick={()=> openJsonEditor('action_plan_json', idx)}>动作计划</Button>
              </Space>
            )},
            { title: '操作', key: 'ops', width: 100, render: (_:any, __:any, idx:number) => (
              <Button danger onClick={() => removeExecStep(idx)}>删除</Button>
            )},
          ] as any}
        />
      </Modal>
      {/* JSON 字段编辑器 */}
      <Modal
        open={jsonEditorOpen}
        onCancel={()=> setJsonEditorOpen(false)}
        onOk={saveJsonEditor}
        width={900}
        title={jsonEditorField === 'required_metrics_json' ? '编辑指标(JSON)'
          : jsonEditorField === 'ui_schema_json' ? '编辑UI配置(JSON)'
          : jsonEditorField === 'action_plan_json' ? '编辑动作计划(JSON)' : '编辑JSON'}
      >
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 8 }}>
          <Button size="small" onClick={async () => {
            try {
              if (navigator?.clipboard?.writeText) {
                await navigator.clipboard.writeText(jsonEditorText || '');
              } else {
                const ta = document.createElement('textarea');
                ta.value = jsonEditorText || '';
                document.body.appendChild(ta);
                ta.select();
                document.execCommand('copy');
                document.body.removeChild(ta);
              }
              message.success('已复制到剪贴板');
            } catch {
              message.error('复制失败');
            }
          }}>复制</Button>
        </div>
        <Input.TextArea
          value={jsonEditorText}
          onChange={(e)=> setJsonEditorText(e.target.value)}
          autoSize={{ minRows: 12, maxRows: 24 }}
          placeholder={jsonEditorField === 'action_plan_json' ? '[]' : '{}'}
        />
        <div style={{ marginTop: 8, color: '#8c8c8c' }}>
          保存前请确认 JSON 语法有效；动作计划支持模板变量：{`{{metrics.xxx}}`}、{`{{instruction_id}}`}、{`{{user_id}}`}。
        </div>
      </Modal>
      {/* 从步骤库导入弹窗 */}
      <Modal
        open={stepLibOpen}
        title="从仓库操作步骤库导入"
        onCancel={()=> { setStepLibOpen(false); setStepLibSelectedKeys([]); }}
        onOk={() => {
          const byId = new Map(stepLibRows.map((r:any)=> [Number(r.id), r]));
          const picked = stepLibSelectedKeys.map((k)=> byId.get(Number(k))).filter(Boolean) as any[];
          if (picked.length === 0) { message.warning('请选择要导入的标准步骤'); return; }
          setExecSteps(prev => {
            const maxOrder = prev.reduce((m, x) => Math.max(m, Number(x.sort_order || 0)), 0);
            let order = maxOrder + 1;
            const added = picked.map((p:any) => ({
              id: undefined,
              task_template: execTaskTpl?.id,
              library_step: p.id,
              step_type: p.step_key || '',
              title: p.name || '',
              required: true,
              stage: 1,
              group_key: '',
              sort_order: order++,
              required_metrics_json: JSON.stringify(p.default_required_metrics || {}, null, 2),
              ui_schema_json: JSON.stringify(p.default_ui_schema || {}, null, 2),
              action_plan_json: JSON.stringify(p.default_action_plan || [], null, 2),
            }));
            return [...prev, ...added];
          });
          setStepLibOpen(false);
          setStepLibSelectedKeys([]);
        }}
        width={900}
      >
        <Table
          loading={stepLibLoading}
          rowKey="id"
          dataSource={stepLibRows}
          rowSelection={{
            selectedRowKeys: stepLibSelectedKeys,
            onChange: (keys)=> setStepLibSelectedKeys(keys),
          }}
          pagination={{ pageSize: 10, showSizeChanger: true }}
          columns={[
            { title: '步骤键', dataIndex: 'step_key', width: 180 },
            { title: '名称', dataIndex: 'name', width: 220 },
            { title: '版本', dataIndex: 'version', width: 100 },
            { title: '标签', dataIndex: 'tags', render: (v:any) => Array.isArray(v) ? v.join('、') : (v || '-') },
            { title: '启用', dataIndex: 'is_active', width: 100, render: (v:any)=> v ? <Tag color="green">启用</Tag> : <Tag>停用</Tag> },
            { title: '更新时间', dataIndex: 'updated_at', width: 180 },
          ]}
        />
      </Modal>
      {/* 执行步骤 - 填写提示 */}
      <Modal
        open={showExecStepHelp}
        onCancel={() => setShowExecStepHelp(false)}
        footer={null}
        title="执行步骤 - 填写提示"
        width={900}
      >
        <div style={{ whiteSpace: 'pre-line', color: '#141414' }}>
{`字段含义
【页面渲染方式（提示）】
- 组件优先、JSON 兜底：若步骤键命中前端“渲染组件注册表”（如 PICK），页面将使用专用组件渲染；否则按 UI(JSON) 渲染。
- 列表“页面渲染方式”列已提示“组件:<键>”或“JSON”。

1) step_type【必填，英文/编码，“步骤键”】：
   - 列表中的“步骤键”，用于系统内部识别、渲染方式选择与（如需）计费口径映射，建议与 OperationType 语义保持一致（如：RELABEL/BOX_PACK/PICK/WEIGH）。
   - 员工页面主要展示“标题”，step_type 更偏向技术标识。请勿随意修改已投产模板的步骤键，否则可能导致已绑定的渲染组件失效（将回退为 JSON）。

2) 标题（title）【必填，中文】：
   - 员工看到的步骤名称，例如“打印仓位表”“拣选”“换标”“装箱”“记录体积重量”。

3) 必做（required）：
   - 勾选后表示该步骤为必做，未完成前不可视为本组/阶段完成。

4) 阶段（stage，数字，建议从 1 起）：
   - 用于划分串行阶段；阶段之间严格串行：stage=2 要在 stage=1 全部组初步完成之后才能开始。
   - 常见用法：1=准备类；2=加工类；3=出库/收尾类。

5) 分组（group_key，文本）：
   - 同一阶段内的并/串行分组键。
   - 同阶段，不同 group → 并行；同阶段，相同 group → 按“顺序（sort_order）”串行。

6) 顺序（sort_order，数字）：
   - 仅在同一阶段且同一 group 内生效，从小到大依次串行。

7) 指标(JSON)（required_metrics）：
   - 该步骤需要现场采集的指标定义（JSON 对象），用于渲染前端输入与驱动计费。
   - 键名必须与计费规则的口径对齐（示例：label_count/box_count/volume_cc/weight_g/work_time_min/photos/remarks）。
   - 建议结构（示例）：
{
  "label_count": { "type": "number", "label": "换标数量", "required": true, "min": 0 },
  "box_count":   { "type": "number", "label": "箱数",     "required": true, "min": 0 },
  "volume_cc":   { "type": "number", "label": "体积(cc)", "required": false },
  "weight_g":    { "type": "number", "label": "重量(g)",  "required": false },
  "work_time_min": { "type": "number", "label": "作业时长(分钟)", "required": false },
  "photos":      { "type": "array",  "item": "image", "label": "现场照片", "required": false, "max": 10 },
  "remarks":     { "type": "string", "label": "备注" }
}
   - 常用 type：number/string/array；array.item 可选 image/file/string；可带 min/max/step/unit/required/placeholder 等。
   - JSON 必须是有效对象，不能包含注释。

8) UI(JSON)（ui_schema_json，对应 ui_schema）：
   - 定义该步骤子页面要显示的控件与布局（扫描框、数字输入、表格、上传等）以及与指标的绑定，即用户输入如何写入 metrics。
   - 示例：
{
  "components": [
    { "type": "ScanInput", "label": "容器", "bind": "bin_code", "placeholder": "扫描容器条码" },
    { "type": "ScanInput", "label": "库存单元", "bind": "iu_code" },
    { "type": "Number", "label": "数量", "bind": "qty", "min": 0 }
  ]
}
   - 说明：bind 对应 required_metrics 中的键名；提交时会把控件的值写入 collected_metrics。

9) 动作计划(JSON)（action_plan_json，对应 action_plan）：
   - 定义提交后后端要执行的动作（白名单动作 + 参数映射）；参数可引用模板变量：
     - {{metrics.xxx}}（本步骤采集的指标）
     - {{instruction_id}} / {{user_id}} / {{step_id}}
   - 示例：
[
  { "action": "inventory.move", "args": { "from_container": "{{metrics.bin_code}}", "inventory_unit": "{{metrics.iu_code}}", "quantity": "{{metrics.qty}}", "reason": "PICK" } }
]
   - 当前实现会以审计日志（instruction.step_actions_executed）记录动作与解析参数；后续可接入真实库存/装箱服务。

10) 来源库步骤（library_step，可选）：
   - 可选择“仓库操作步骤库”的标准步骤作为来源，自动带出默认的 required_metrics、ui_schema、action_plan。
   - 生效规则：模板内“覆写优先”，未覆写则回退到库默认。
   - 支持在弹窗中通过“从仓库操作步骤库导入”批量插入多个标准步骤作为初始行。

关于 step_type 与 OperationType 的关系
- 对“参与计费”的步骤：step_type 尽量与对应 OperationType 使用同一语义编码（如：RELABEL、BOX_PACK、PALLETIZE、OUTBOUND_SHIP），便于规则映射、统计与审计。
- 对“不会计费”的准备/收尾步骤：不需要也不应强行对应 OperationType，可使用独立的非计费编码（如：PRINT_PICK_LIST、PICK、QC、PHOTO、EXCEPTION、HANDOVER）。
- 维护一份团队共用的“step_type 编码字典”（含是否计费/说明），避免拼写分歧与歧义。

命名建议（两类并行）
- 计费相关：RELABEL、BOX_PACK、PALLETIZE、OUTBOUND_SHIP…
- 非计费准备/收尾：PRINT_PICK_LIST、PICK、QC、PHOTO、EXCEPTION、HANDOVER…

实践要点
- 非计费步骤：required_metrics 为空 {} 或仅包含 remarks/photos 等运营性字段，不要使用与计费规则口径同名的键。
- 计费步骤：required_metrics 的键名需与计费规则口径严格一致（如 label_count、box_count、volume_cc、weight_g、work_time_min）。

并/串行判定规则（仅由 stage 与 group 决定）
- 不同 stage：严格串行，后阶段在前阶段完成后开始；
- 同一 stage，不同 group：并行；
- 同一 stage、同一 group：按 sort_order 从小到大串行。

示例：换标 + 装箱
- 阶段1：打印仓位表（PICK_LIST）、拣选（PICK）【并行或串行视业务】
- 阶段2：换标（RELABEL，required_metrics 包含 label_count）/ 装箱（BOX_PACK，required_metrics 包含 box_count/volume_cc/weight_g）
- 提交后：指标将回填到对应虚拟工单 execution_metrics，并驱动“换标费/装箱基础费/装箱容积费”等规则计算。

实用建议
- 标题面向员工清晰可读；step_type 面向系统稳定一致；
- 指标键名务必与计费规则保持一致，减少后端映射；
- 若暂未对接表单渲染，也可先用空 JSON 跑通流程，再逐步补齐。`}
        </div>
      </Modal>

      {/* 自动生成：预览与执行弹窗 */}
      <Modal
        title={autoGenTpl ? `自动生成（${autoGenTpl?.name || autoGenTpl?.system_code || ''}）` : '自动生成'}
        open={autoGenOpen}
        onCancel={() => { setAutoGenOpen(false); setAutoGenPreview(null); setAutoGenTpl(null); }}
        footer={[
          <Button key="cancel" onClick={() => { setAutoGenOpen(false); setAutoGenPreview(null); setAutoGenTpl(null); }}>关闭</Button>,
          <Button key="ok" type="primary" loading={autoGenLoading} disabled={!autoGenTpl || !autoGenTpl?.is_active} onClick={async () => {
            if (!autoGenTpl) return;
            setAutoGenLoading(true);
            try {
              await request.post(`/api/v1/instruction/templates/${autoGenTpl.id}/auto-generate/`, {});
              message.success('已生成/更新工单模板与路由');
            try {
              // 生成成功后刷新该模板的 ready/locked 状态
              const resp2 = await request.post('/api/v1/instruction/templates/ready-status/', { ids: [Number(autoGenTpl.id)] });
              const arr2 = Array.isArray(resp2?.data) ? resp2.data : [];
              const row = arr2[0];
              if (row && Number.isFinite(Number(row.id))) {
                setGenStatusByTplId(prev => ({ ...prev, [Number(row.id)]: { ready: !!row.ready, locked: !!row.locked } }));
              } else {
                setGenStatusByTplId(prev => ({ ...prev, [Number(autoGenTpl.id)]: { ...(prev[Number(autoGenTpl.id)]||{}), ready: true } }));
              }
            } catch {}
              setAutoGenOpen(false);
              setAutoGenPreview(null);
              setAutoGenTpl(null);
            } catch (e:any) {
              message.error(e?.response?.data?.detail || '生成失败');
            } finally { setAutoGenLoading(false); }
          }}>执行生成</Button>
        ]}
        width={960}
      >
        <div style={{ maxHeight: 420, overflow: 'auto' }}>
          {autoGenLoading && <div style={{ color: '#999' }}>加载中...</div>}
          {!autoGenLoading && autoGenPreview && (
            <div>
              {Array.isArray(autoGenPreview.warnings) && autoGenPreview.warnings.length > 0 && (
                <div style={{ marginBottom: 8, padding: '6px 8px', background: '#fffbe6', border: '1px solid #ffe58f', borderRadius: 4 }}>
                  {autoGenPreview.warnings.map((w, i) => (<div key={i} style={{ color: '#ad6800' }}>· {w}</div>))}
                </div>
              )}
              <div style={{ marginBottom: 12 }}>
                <div style={{ fontWeight: 600, marginBottom: 6 }}>将创建的工单模板（{autoGenPreview.created_templates.length}）</div>
                {autoGenPreview.created_templates.length > 0 ? (
                  <Table
                    size="small"
                    rowKey={(r:any, i?:number)=> r?.code || i}
                    pagination={false}
                    dataSource={autoGenPreview.created_templates}
                    columns={[
                      { title: '名称', dataIndex: 'name', width: 220, render: (v:string, r:any) => <span>{v}</span> },
                      { title: '编码', dataIndex: 'code', width: 260, render: (v:string) => <span style={{ color:'#999' }}>{v}</span> },
                      { title: '操作类型', dataIndex: 'operation_type', width: 160, render: (v:string) => <Tag color="blue">{v}</Tag> },
                    ] as any}
                  />
                ) : (<div style={{ color:'#999' }}>无</div>)}
              </div>
              <div style={{ marginBottom: 12 }}>
                <div style={{ fontWeight: 600, marginBottom: 6 }}>将更新的工单模板（{autoGenPreview.updated_templates.length}）</div>
                {autoGenPreview.updated_templates.length > 0 ? (
                  <Table
                    size="small"
                    rowKey={(r:any, i?:number)=> r?.code || i}
                    pagination={false}
                    dataSource={autoGenPreview.updated_templates}
                    columns={[
                      { title: '名称', dataIndex: 'name', width: 220, render: (v:string) => <span>{v}</span> },
                      { title: '编码', dataIndex: 'code', width: 260, render: (v:string) => <span style={{ color:'#999' }}>{v}</span> },
                      { title: '操作类型', dataIndex: 'operation_type', width: 160, render: (v:string) => <Tag color="geekblue">{v}</Tag> },
                    ] as any}
                  />
                ) : (<div style={{ color:'#999' }}>无</div>)}
              </div>
              <div>
                <div style={{ fontWeight: 600 }}>路由</div>
                <div style={{ margin: '6px 0 8px 0', padding: '6px 8px', background: '#e6f4ff', border: '1px solid #91caff', borderRadius: 4, color: '#0958d9' }}>
                  路由/计划说明：按指令模板编码命中配置，产出将生成的工单模板清单及其顺序与依赖（after 表示需在前序完成后执行），可带条件（项目/仓库）与优先级；若审核端设置了计划覆盖，则以计划覆盖为准。同一模板编码仅保留一次，依赖通过 after 表达。
                </div>
                <div style={{ paddingLeft: 6 }}>
                  {autoGenPreview.route ? (
                    <div style={{ color:'#333' }}>
                      <div>相关指令模板：{autoGenPreview.route.instruction_template_code || '-'}</div>
                      <div>优先级：{String(autoGenPreview.route.priority ?? 100)}</div>
                      <div>是否更新：{autoGenPreview.route.updated ? '是' : (autoGenPreview.route.created ? '新建' : '未知')}</div>
                      <div>计划：</div>
                      {(() => {
                        // 代码→名称映射，优先使用新建/更新列表名称
                        const map: Record<string, string> = {};
                        try {
                          (autoGenPreview.created_templates || []).forEach((t:any)=> { if (t?.code) map[String(t.code)] = t.name || String(t.code); });
                          (autoGenPreview.updated_templates || []).forEach((t:any)=> { if (t?.code) map[String(t.code)] = t.name || String(t.code); });
                        } catch {}
                        return Array.isArray(autoGenPreview.route.plan) && autoGenPreview.route.plan.length > 0 ? (
                        <div style={{ display:'flex', flexDirection:'column', gap: 6 }}>
                          {autoGenPreview.route.plan.map((p:any, i:number) => {
                            const afterCodes = Array.isArray(p.after) ? p.after : (p.after ? [p.after] : []);
                            const afterNames = afterCodes.map((c:string)=> map[c] || c);
                            const targetName = map[p.template_code] || p.template_code;
                            return (
                              <div key={i} style={{ display:'flex', alignItems:'center', gap:8, flexWrap:'wrap' }}>
                                {afterNames && afterNames.length > 0 ? (
                                  <span style={{ color:'#999' }}>{afterNames.join('、')}</span>
                                ) : (
                                  <span style={{ color:'#999' }}>起点</span>
                                )}
                                <span style={{ color:'#999' }}>→</span>
                                <Tag color="processing">{targetName}</Tag>
                              </div>
                            );
                          })}
                        </div>
                        ) : (<div style={{ color:'#999' }}>-</div>);
                      })()}
                    </div>
                  ) : (
                    <div style={{ color:'#999' }}>无</div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </Modal>

      {/* 查看已生成的专属工单模板与路由 */}
      <Modal
        title={ownedTpl ? `专属工单模板与路由 - ${ownedTpl?.name || ownedTpl?.system_code || ''}` : '专属工单模板与路由'}
        open={ownedOpen}
        onCancel={() => { setOwnedOpen(false); setOwnedTpl(null); setOwnedData(null); }}
        footer={[
          <Button
            key="regen"
            type="primary"
            loading={ownedLoading}
            disabled={!ownedTpl?.id || !ownedTpl?.is_active}
            onClick={async () => {
              if (!ownedTpl?.id) return;
              setOwnedLoading(true);
              try {
                await request.post(`/api/v1/instruction/templates/${ownedTpl.id}/auto-generate/`, {});
                message.success('已重新生成工单模板与路由');
                const resp2 = await request.post('/api/v1/instruction/templates/ready-status/', { ids: [Number(ownedTpl.id)] });
                const arr2 = Array.isArray(resp2?.data) ? resp2.data : [];
                const row = arr2[0];
                if (row && Number.isFinite(Number(row.id))) {
                  setGenStatusByTplId(prev => ({ ...prev, [Number(row.id)]: { ready: !!row.ready, locked: !!row.locked } }));
                } else {
                  setGenStatusByTplId(prev => ({ ...prev, [Number(ownedTpl.id)]: { ...(prev[Number(ownedTpl.id)] || {}), ready: true } }));
                }
                const resp = await request.get(`/api/v1/instruction/templates/${ownedTpl.id}/owned-workorders/`);
                const payload = resp?.data || {};
                const templates = Array.isArray(payload.templates) ? payload.templates : [];
                const route = payload.route || undefined;
                setOwnedData({ templates, route });
              } catch (e:any) {
                message.error(e?.response?.data?.detail || '重新生成失败');
              } finally {
                setOwnedLoading(false);
              }
            }}
          >
            重新生成工单模板与路由
          </Button>,
          <Button key="close" onClick={() => { setOwnedOpen(false); setOwnedTpl(null); setOwnedData(null); }}>关闭</Button>
        ]}
        width={1000}
      >
        {ownedLoading && <div style={{ color:'#999' }}>加载中...</div>}
        {!ownedLoading && ownedData && (
          <div>
            <div style={{ marginBottom: 8, fontWeight: 600 }}>工单模板</div>
            <Table
              size="small"
              rowKey={(r:any, i?:number)=> String(r?.id || r?.value || i)}
              dataSource={ownedData.templates}
              pagination={false}
              columns={[
                { title: '模板编码', dataIndex: 'code', width: 200 },
                { title: '名称', dataIndex: 'name', width: 220 },
                { title: '操作类型', dataIndex: 'operation_type', width: 160 },
              ] as any}
            />
            <div style={{ height: 12 }} />
            <div style={{ marginBottom: 8, fontWeight: 600 }}>路由</div>
            <div style={{ margin: '6px 0 8px 0', padding: '6px 8px', background: '#e6f4ff', border: '1px solid #91caff', borderRadius: 4, color: '#0958d9' }}>
              路由/计划说明：按指令模板编码命中配置，产出将生成的工单模板清单及其顺序与依赖（after 表示需在前序完成后执行），可带条件（项目/仓库）与优先级；若审核端设置了计划覆盖，则以计划覆盖为准。同一模板编码仅保留一次，依赖通过 after 表达。
            </div>
            {ownedData.route ? (
              <div>
                <div>指令模板：{ownedData.route.instruction_template_code || '-'}</div>
                <div>优先级：{String(ownedData.route.priority ?? 100)}</div>
                <div>计划：</div>
                {(() => {
                  try {
                    const map: Record<string, string> = {};
                    (ownedData.templates || []).forEach((t:any) => {
                      if (t?.code) map[String(t.code)] = t.name || String(t.code);
                    });
                    const plan = Array.isArray(ownedData.route.plan) ? ownedData.route.plan : [];
                    if (plan.length === 0) return <div style={{ color:'#999' }}>-</div>;
                    return (
                      <div style={{ display:'flex', flexDirection:'column', gap: 6 }}>
                        {plan.map((p:any, i:number) => {
                          const afterCodes = Array.isArray(p.after) ? p.after : (p.after ? [p.after] : []);
                          const afterNames = afterCodes.map((c:string)=> map[c] || c);
                          const targetName = map[p.template_code] || p.template_code;
                          return (
                            <div key={i} style={{ display:'flex', alignItems:'center', gap:8, flexWrap:'wrap' }}>
                              {afterNames && afterNames.length > 0 ? (
                                <span style={{ color:'#999' }}>{afterNames.join('、')}</span>
                              ) : (
                                <span style={{ color:'#999' }}>起点</span>
                              )}
                              <span style={{ color:'#999' }}>→</span>
                              <Tag color="processing">{targetName}</Tag>
                            </div>
                          );
                        })}
                      </div>
                    );
                  } catch {
                    return <div style={{ color:'#999' }}>-</div>;
                  }
                })()}
              </div>
            ) : <div style={{ color:'#999' }}>未配置路由</div>}
          </div>
        )}
      </Modal>
      {/* 动作/问题/附件详情弹窗 */}
      <Modal open={actionListOpen} onCancel={() => { setActionListOpen(false); setActionList([]); }} title={actionListTitle} footer={null} width={1400}>
        {actionList && actionList.length > 0 ? (() => {
          let data = [...actionList];
          let columns: any[] = [];
          if (actionListMode === 'actions') {
            data = data.sort((a:any,b:any)=>{
              const sa = Number.isFinite(Number(a.stage)) ? Number(a.stage) : Number.POSITIVE_INFINITY;
              const sb = Number.isFinite(Number(b.stage)) ? Number(b.stage) : Number.POSITIVE_INFINITY;
              if (sa !== sb) return sa - sb;
              const ga = (a.group || '~') as string;
              const gb = (b.group || '~') as string;
              if (ga !== gb) return ga.localeCompare(gb);
              const oa = Number(a.order || 0);
              const ob = Number(b.order || 0);
              return oa - ob;
            });
            columns = [
              { title: '名称', dataIndex: 'name', width: 260 },
              { title: '阶段', dataIndex: 'stage', width: 100, render: (v:any)=> v!=null? String(v): '-' },
              { title: '阶段内并行分组', dataIndex: 'group', width: 180, render: (v:any)=> v ? <Tag>{v}</Tag> : <span style={{ color:'#999' }}>-</span> },
              { title: '组内顺序', dataIndex: 'order', width: 120, render: (v:any)=> v!=null? String(v): '-' },
              { title: '变体', dataIndex: 'variant', width: 140, render: (v:any)=> v? <Tag color="purple">{v}</Tag> : <span style={{ color:'#999' }}>-</span> },
            ];
          } else if (actionListMode === 'questions') {
            // 问题详情：标题/是否必填/类型/默认值/选项/提示/占位
            columns = [
              // 说明：缩小“问题标题/选项”列宽，确保所有列宽总和不超过弹窗宽度（避免横向滚动）
              { title: '问题标题', dataIndex: 'name', width: 180, ellipsis: true },
              { title: '是否必填', dataIndex: 'variant', width: 90, render: (v:any)=> v ? <Tag color="red">必填</Tag> : <span style={{ color:'#999' }}>可选</span> },
              { title: '类型', dataIndex: 'basis', width: 90, render: (v:any)=> v || <span style={{ color:'#999' }}>-</span> },
              { title: '默认值', dataIndex: 'defaultText', width: 160, ellipsis: true, render: (v:any)=> (v !== undefined && v !== null && String(v) !== '' ? <span title={String(v)}>{String(v)}</span> : <span style={{ color:'#999' }}>-</span>) },
              { title: '选项', dataIndex: 'options', width: 280, ellipsis: true, render: (v:any)=> v ? <span title={v}>{v}</span> : <span style={{ color:'#999' }}>-</span> },
              { title: '提示', dataIndex: 'tips', width: 220, ellipsis: true, render: (v:any)=> {
                const s = String(v ?? '').trim();
                if (!s) return <span style={{ color:'#999' }}>-</span>;
                return (
                  <Tooltip title={s}>
                    <span>{s}</span>
                  </Tooltip>
                );
              }},
              { title: '占位', dataIndex: 'placeholder', width: 220, ellipsis: true, render: (v:any)=> {
                const s = String(v ?? '').trim();
                if (!s) return <span style={{ color:'#999' }}>-</span>;
                return (
                  <Tooltip title={s}>
                    <span>{s}</span>
                  </Tooltip>
                );
              }},
            ];
          } else {
            // 附件详情：作用域/名称、类型、数量
            columns = [
              { title: '作用域/附件名称', dataIndex: 'name', width: 360 },
              { title: '是否必填', dataIndex: 'required', width: 120, render: (v:any)=> v ? <Tag color="red">必填</Tag> : <span style={{ color:'#999' }}>可选</span> },
              { title: '类型', dataIndex: 'basis', width: 240, render: (v:any)=> v || <span style={{ color:'#999' }}>-</span> },
              { title: '数量', dataIndex: 'variant', width: 180, render: (v:any)=> v || <span style={{ color:'#999' }}>-</span> },
            ];
          }
          return (
            <Table
              size="small"
              rowKey={(r:any, i?:number)=> String(i)}
              pagination={false}
              dataSource={data}
              columns={columns as any}
            />
          );
        })() : (<div>-</div>)}
      </Modal>

      <Modal
        open={detailOpen}
        onCancel={() => setDetailOpen(false)}
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span>指令模板详情 -</span>
            <span style={{ fontWeight: 700, color: '#1677ff' }}>{detailRecord?.name || ''}</span>
          </div>
        }
        footer={null}
        width={1000}
      >
        {detailRecord ? (
          <div>
            <Divider orientation="left" style={{ marginTop: 0, marginBottom: 12 }}>基本信息</Divider>
            <div style={{ background: '#fafafa', border: '1px solid #f0f0f0', borderRadius: 6, padding: 12, marginBottom: 12 }}>
            <div style={{ marginBottom: 8 }}>
                <Space size={24}>
                <span>系统编码：{detailRecord.system_code}</span>
                <span>层级：{detailRecord.level}</span>
                <span>启用：{detailRecord.is_active ? '是' : '否'}</span>
              </Space>
            </div>
              <div style={{ marginBottom: 8 }}>
                客户：{(() => {
                  try {
                    const cid = Number(detailRecord.customer_id);
                    const mapped = !Number.isNaN(cid) ? customerLabelById[cid] : undefined;
                    const fallback = detailRecord.customer_name || detailRecord.customer || detailRecord.customer_username || detailRecord.customer_code;
                    return mapped || fallback || '-';
                  } catch { return detailRecord.customer_name || detailRecord.customer_id || '-'; }
                })()}
              </div>
              <div style={{ marginBottom: 8 }}>
                项目：{(() => {
                  try {
                    const pid = Number(detailRecord.project_id);
                    const mapped = !Number.isNaN(pid) ? projectLabelById[pid] : undefined;
                    if (mapped) return mapped;
                    const code = detailRecord.project_code || detailRecord.project_system_code || detailRecord.project || '';
                    const name = detailRecord.project_name || '';
                    const id = detailRecord.project_id;
                    return code || name ? `${code || id || ''}${(code||id)&&name?' - ':''}${name}` : (id || '-');
                  } catch { return detailRecord.project_id || '-'; }
                })()}
              </div>
              <div>简要说明：{detailRecord.brief_description || '-'}</div>
            </div>
            <Divider orientation="left" style={{ margin: '12px 0' }}>库存终态结算配置（终审提交）</Divider>
            <div style={{ background: '#f6ffed', border: '1px solid #b7eb8f', borderRadius: 6, padding: 12, marginBottom: 12 }}>
              {(() => {
                try {
                  const meta = (detailRecord?.meta && typeof detailRecord.meta === 'object') ? detailRecord.meta : {};
                  const inv = (meta?.inventory && typeof meta.inventory === 'object') ? meta.inventory : {};
                  const cfgFinalize = (inv?.final_review_finalize && typeof inv.final_review_finalize === 'object') ? inv.final_review_finalize : {};
                  const cfgLegacy = (inv?.final_review_consume && typeof inv.final_review_consume === 'object') ? inv.final_review_consume : {};
                  const hasFinalize = !!(cfgFinalize && Object.keys(cfgFinalize).length > 0);
                  const cfg = hasFinalize ? cfgFinalize : cfgLegacy;
                  const modeRaw = String(cfg?.mode || '').trim();
                  const mode = (modeRaw === 'release_reserved' || modeRaw === 'transfer_to_business_hold' || modeRaw === 'consume_reserved')
                    ? modeRaw
                    : 'consume_reserved';
                  const enabled = hasFinalize ? true : !!cfg?.enabled;
                  const iocCfg = (
                    inv?.regular_outbound_ioc_consume
                    && typeof inv.regular_outbound_ioc_consume === 'object'
                  ) ? inv.regular_outbound_ioc_consume : {};
                  const iocKinds = Array.from(new Set(
                    (Array.isArray(iocCfg?.reservation_kinds) ? iocCfg.reservation_kinds : [])
                      .map((x: any) => String(x || '').trim().toLowerCase())
                      .filter((x: string) => x === 'execution' || x === 'business_hold')
                  ));
                  const ap = (cfg?.applicability && typeof cfg.applicability === 'object') ? cfg.applicability : {};
                  const modesIn = Array.isArray(ap?.waybill_mode_in) ? ap.waybill_mode_in.map((x:any)=>String(x||'').trim()).filter(Boolean) : [];
                  const modesNotIn = Array.isArray(ap?.waybill_mode_not_in) ? ap.waybill_mode_not_in.map((x:any)=>String(x||'').trim()).filter(Boolean) : [];
                  const sources = Array.isArray(ap?.waybill_source_in) ? ap.waybill_source_in.map((x:any)=>String(x||'').trim()).filter(Boolean) : [];
                  const packs = Array.isArray(ap?.is_pack_required_in) ? ap.is_pack_required_in.filter((x:any)=> typeof x === 'boolean') : [];
                  const modeLabel = (v: string) => (WAYBILL_MODE_OPTIONS.find((o:any)=> String(o.value) === v)?.label || v);
                  const sourceLabel = (v: string) => (WAYBILL_SOURCE_OPTIONS.find((o:any)=> String(o.value) === v)?.label || v);
                  const packLabel = (v: boolean) => (PACK_REQUIRED_OPTIONS.find((o:any)=> o.value === v)?.label || String(v));
                  const conditionItems: string[] = [];
                  if (sources.length) conditionItems.push(`运单来源包含：${sources.map(sourceLabel).join(' / ')}`);
                  if (modesIn.length) conditionItems.push(`运单上传模式包含：${modesIn.map(modeLabel).join(' / ')}`);
                  if (modesNotIn.length) conditionItems.push(`运单上传模式排除：${modesNotIn.map(modeLabel).join(' / ')}`);
                  if (packs.length) conditionItems.push(`是否需打包：${packs.map(packLabel).join(' / ')}`);
                  return (
                    <div>
                      <div style={{ marginBottom: 8 }}>
                        <Space size={16}>
                          <span>状态：{enabled ? <Tag color="green">已自定义</Tag> : <Tag>默认</Tag>}</span>
                          <span>结算模式：<Tag color="blue">{inventoryFinalizeModeLabel(enabled ? mode : 'release_reserved')}</Tag></span>
                          <span>常规出库IOC消耗：{
                            iocKinds.length > 0
                              ? <Tag color="purple">{iocKinds.join(' + ')}</Tag>
                              : <Tag>默认(execution + business_hold)</Tag>
                          }</span>
                          <span>触发时机：首次终审提交（operation_done → completed）</span>
                        </Space>
                      </div>
                      {conditionItems.length > 0 ? (
                        <ol style={{ paddingLeft: 18, margin: 0 }}>
                          {conditionItems.map((line, idx) => (
                            <li key={idx}>{line}</li>
                          ))}
                        </ol>
                      ) : (
                        <span style={{ color: '#999' }}>{enabled ? '未设置附加条件（仅按该自定义规则生效）' : '使用系统默认规则（release_reserved）'}</span>
                      )}
                    </div>
                  );
                } catch {
                  return <span>-</span>;
                }
              })()}
            </div>
            <Divider orientation="left" style={{ margin: '12px 0' }}>动作(计费轨操作类型)</Divider>
            <div style={{ background: '#f6ffed', border: '1px solid #b7eb8f', borderRadius: 6, padding: 12, marginBottom: 12 }}>
              {(() => {
                const rows = ((detailRecord.actions || []) as any[]).map((a:any) => {
                    const opId = Number(a.operation_type_id || a.operation_type);
                  const name = (!Number.isNaN(opId) && opIdToName.get(opId)) || (typeof a.operation_type_name === 'string' && a.operation_type_name) || `ID=${opId || '-'}`;
                  return {
                    name,
                    stage: (a.stage != null ? Number(a.stage) : undefined),
                    group: a.group_key || undefined,
                    order: Number(a.sort_order || 0),
                    variant: a.variant_key,
                    basis: a.basis_override,
                    opId: (!Number.isNaN(opId) ? opId : undefined),
                    _id: Number(a.id || 0),
                  };
                });
                const sorted = rows.sort((a:any,b:any)=>{
                  const sa = Number.isFinite(Number(a.stage)) ? Number(a.stage) : Number.POSITIVE_INFINITY;
                  const sb = Number.isFinite(Number(b.stage)) ? Number(b.stage) : Number.POSITIVE_INFINITY;
                  if (sa !== sb) return sa - sb;
                  const ga = (a.group || '~') as string;
                  const gb = (b.group || '~') as string;
                  if (ga !== gb) return ga.localeCompare(gb);
                  const oa = Number(a.order || 0);
                  const ob = Number(b.order || 0);
                  if (oa !== ob) return oa - ob;
                  return Number(a._id || 0) - Number(b._id || 0);
                });
                    return (
                  <Table
                    size="small"
                    rowKey={(r:any,i?:number)=> String(r._id || i)}
                    pagination={false}
                    dataSource={sorted}
                    columns={[
                      { title: '名称', dataIndex: 'name', width: 260 },
                      { title: '阶段', dataIndex: 'stage', width: 100, render: (v:any)=> v!=null? String(v): '-' },
                      { title: '阶段内并行分组', dataIndex: 'group', width: 180, render: (v:any)=> v ? <Tag>{v}</Tag> : <span style={{ color:'#999' }}>-</span> },
                      { title: '组内顺序', dataIndex: 'order', width: 120, render: (v:any)=> v!=null? String(v): '-' },
                      { title: '变体', dataIndex: 'variant', width: 140, render: (v:any)=> v? <Tag color="purple">{v}</Tag> : <span style={{ color:'#999' }}>-</span> },
                      // 口径列移除（不展示）
                    ] as any}
                  />
                );
              })()}
            </div>
            {/* 新增：问题集显示 */}
            <Divider orientation="left" style={{ margin: '12px 0' }}>问题集</Divider>
            <div style={{ background: '#fff7e6', border: '1px solid #ffe58f', borderRadius: 6, padding: 12, marginBottom: 12 }}>
              {(() => {
                try {
                  const qn = detailRecord.questionnaire || {};
                  const qs = Array.isArray(qn.questions) ? qn.questions : [];
                  if (!qs || qs.length === 0) return <span>-</span>;
                  const fmtDefault = (q:any): string | undefined => {
                    try {
                      const t = String(q?.type || '');
                      const def = q?.default;
                      if (def === undefined || def === null) return undefined;
                      if (t === 'boolean') return (def === true ? '是' : def === false ? '否' : undefined);
                      if (t === 'number') {
                        const n = Number(def);
                        return Number.isFinite(n) ? String(n) : undefined;
                      }
                      if (t === 'select') {
                        const s = String(def ?? '').trim();
                        return s || undefined;
                      }
                      if (t === 'multiselect') {
                        const arr = Array.isArray(def) ? def : [];
                        const vals = arr.map((x:any)=> String(x ?? '').trim()).filter(Boolean);
                        return vals.length ? vals.join(' / ') : undefined;
                      }
                      if (t === 'text' || t === 'textarea') {
                        const s = String(def ?? '');
                        return (s !== '' ? s : '(空)');
                      }
                    } catch {}
                    return undefined;
                  };
                  return (
                    <ol style={{ paddingLeft: 18, margin: 0 }}>
                      {qs.map((q:any, idx:number) => {
                        const isChoice = (q?.type === 'select' || q?.type === 'multiselect');
                        const options = isChoice && Array.isArray(q?.options) ? (q.options as any[]).map((o:any)=> (o?.label || o?.value || '')).filter(Boolean) : [];
                        const defText = fmtDefault(q);
                        return (
                        <li key={idx}>
                          <span>{q.label || q.key || `问题${idx+1}`}</span>
                          {q.required ? <span> · 必填</span> : null}
                          {q.required_if?.key ? <span> · 条件必填</span> : null}
                          {q.type ? <span> · 类型={q.type}</span> : null}
                          {defText !== undefined ? <span> · 默认值={defText}</span> : null}
                          {q.tips ? <span> · 提示={q.tips}</span> : null}
                            {q.placeholder ? <span> · 占位={q.placeholder}</span> : null}
                          {q.type === 'number' && (q.min!=null || q.max!=null) ? <span>{` · 范围=[${q.min!=null?q.min:'-'}, ${q.max!=null?q.max:'-'}]`}</span> : null}
                          {q.unit ? <span> · 单位={q.unit}</span> : null}
                            {isChoice ? (
                              options.length > 0
                                ? <span> · 选项={options.join(' / ')}</span>
                                : <span> · 选项=-</span>
                          ) : null}
                        </li>
                        );
                      })}
                    </ol>
                  );
                } catch { return <span>-</span>; }
              })()}
            </div>
            {/* 新增：基准数据规范显示 */}
            <Divider orientation="left" style={{ margin: '12px 0' }}>基准数据规范(仓库操作基线文件)</Divider>
            <div style={{ background: '#f0f5ff', border: '1px solid #adc6ff', borderRadius: 6, padding: 12, marginBottom: 12 }}>
              {(() => {
                try {
                  const br = detailRecord.baseline_requirements || {};
                  const items = Array.isArray(br.items) ? br.items : [];
                  if (!items || items.length === 0) return <span>-</span>;
                  return (
                    <ol style={{ paddingLeft: 18, margin: 0 }}>
                      {items.map((it:any, idx:number) => {
                        const formats = Array.isArray(it.formats) ? it.formats.join(', ') : undefined;
                        const bind = it.bind || {};
                        const stage = (bind && bind.stage != null) ? `stage=${bind.stage}` : '';
                        const group = (bind && bind.group_key) ? `group=${bind.group_key}` : '';
                        const steps = (bind && Array.isArray(bind.steps) && bind.steps.length > 0) ? `steps=${bind.steps.join('/')}` : '';
                        const bindText = [stage, group, steps].filter(Boolean).join(' · ');
                        const schemaKeys = (() => {
                          try {
                            const s = it.schema_hint;
                            if (s && typeof s === 'object') {
                              const keys = Object.keys(s);
                              return keys.length ? keys.join(', ') : undefined;
                            }
                            return undefined;
                          } catch { return undefined; }
                        })();
                        return (
                          <li key={idx}>
                            <span>{it.name || it.code || `基准项${idx+1}`}</span>
                            {it.code ? <span> · code={it.code}</span> : null}
                            {it.required ? <span> · 必需</span> : <span> · 可选</span>}
                            {formats ? <span> · 格式={formats}</span> : null}
                            {it.min_files != null ? <span> · 最少={it.min_files}</span> : null}
                            {it.max_files != null ? <span> · 最多={it.max_files}</span> : null}
                            {bindText ? <span> · 绑定={bindText}</span> : null}
                            {schemaKeys ? <span> · schema={schemaKeys}</span> : null}
                          </li>
                        );
                      })}
                    </ol>
                  );
                } catch { return <span>-</span>; }
              })()}
            </div>
            <Divider orientation="left" style={{ margin: '12px 0' }}>附件要求</Divider>
            <div style={{ background: '#e6f4ff', border: '1px solid #91caff', borderRadius: 6, padding: 12 }}>
              {(() => {
                const req = detailRecord.attachment_requirements || {};
                const hasOrder = Array.isArray(req.order) && req.order.length > 0;
                const hasActions = req.actions && typeof req.actions === 'object' && Object.keys(req.actions).length > 0;
                if (!hasOrder && !hasActions) return <span>-</span>;
                return (
                  <div style={{ marginTop: 6 }}>
                    {hasOrder && (
                      <div style={{ marginBottom: 8 }}>
                        <div style={{ fontWeight: 600, marginBottom: 4 }}>客户端需提交</div>
                        <ol style={{ paddingLeft: 18, margin: 0 }}>
                          {req.order.map((n:any, idx:number) => (
                            <li key={idx}>
                              <span>{n.label || '未命名'}</span>
                              {Array.isArray(n.types) && n.types.length > 0 ? <span> · 类型={n.types.join(', ')}</span> : null}
                              {n.min !== undefined ? <span> · 最少={n.min}</span> : null}
                              {n.max !== undefined ? <span> · 最多={n.max}</span> : null}
                              {n.tips ? <span> · 提示={n.tips}</span> : null}
                            </li>
                          ))}
                        </ol>
                      </div>
                    )}
                    {hasActions && (
                      <div>
                        <div style={{ fontWeight: 600, marginBottom: 4 }}>完成动作需提交</div>
                        <ol style={{ paddingLeft: 18, margin: 0 }}>
                          {Object.keys(req.actions).map((key:string, i:number) => {
                            const niceKey = (() => {
                              if (!key || key === 'ALL') return '所有动作(ALL)';
                              if (key.startsWith('op:')) {
                                const id = Number(key.slice(3));
                                const text = (!Number.isNaN(id) && opIdToName.get(id)) || `op:${id}`;
                                return text;
                              }
                              return key;
                            })();
                            const arr = Array.isArray(req.actions[key]) ? req.actions[key] : [];
                            return (
                              <li key={i}>
                                <div style={{ marginBottom: 2 }}>{niceKey}</div>
                                <ol style={{ paddingLeft: 18, margin: 0 }}>
                                  {arr.map((n:any, j:number) => (
                                    <li key={j}>
                                      <span>{n.label || '未命名'}</span>
                                      {Array.isArray(n.types) && n.types.length > 0 ? <span> · 类型={n.types.join(', ')}</span> : null}
                                      {n.min !== undefined ? <span> · 最少={n.min}</span> : null}
                                      {n.max !== undefined ? <span> · 最多={n.max}</span> : null}
                                      {n.tips ? <span> · 提示={n.tips}</span> : null}
                                    </li>
                                  ))}
                                </ol>
                              </li>
                            );
                          })}
                        </ol>
                      </div>
                    )}
                  </div>
                );
              })()}
            </div>
          </div>
        ) : null}
      </Modal>
    </div>
  );
};

export default InstructionTypesPage;


